import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';
import { debounceTime, filter, timeout } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../core/auth.service';
import { DbService } from '../core/db.service';
import { DiagnosticsService } from '../core/diagnostics.service';
import { FlashMessageService } from '../support/flash-message.service';
import { environment } from '../../../environments/environment';

declare var MediaRecorder: any;

export interface VideoRecordingOptions {
    mimeType?: string;
    videoBitsPerSecond?: number;
    audioBitsPerSecond?: number;
}

interface SubscriberRecording {
    subscriberId: string;
    streamId: string;
    participantName: string;
    mediaRecorder: any;
    startTime: Date;
    isRecording: boolean;
    isVideoEnabled: boolean;
    lastToggleTime: Date;
    bufferChunks: Blob[];
    sasUrl?: string;
    blobPath?: string; // Base blob URL (no query string) this segment's blocks are bound to
    sasPending?: boolean; // Instant-start: capture began before the SAS arrived; uploads are held
    pendingUploadInfo?: Promise<AzureUploadInfo>; // In-flight SAS prefetch (finalize awaits it briefly)
    blockIds: string[];
    isPaused: boolean;
    uploadQueue: Blob[]; // Queue for chunks waiting to be uploaded
    isUploading: boolean; // Flag to prevent concurrent uploads
    uploadBackpressure?: boolean; // Upload queue backpressure (advisory; never pauses the MediaRecorder)
    pendingVideoToggle?: boolean; // Video toggle deferred during an upload cycle (latest state wins)
    rotationRequested?: boolean; // Graceful segment rotation requested (e.g. SAS token near expiry)
    forceRotation?: boolean; // Segment can no longer accept uploads (auth failure) - rotate ASAP
    finalizeQuiesceRequested?: boolean; // Terminal finalize wants the live drain loop to stop taking chunks
    emergencyStopTriggered?: boolean; // Upload queue hard-cap emergency stop already fired
    lastCommittedBlockCount?: number; // Number of blocks covered by the last successful commit
    lastCommitTime?: Date; // Time of the last successful (intermediate) commit
    lastEmergencyCommitTime?: number; // Throttles non-terminal (visibilitychange) emergency flushes
    emergencyCheckpointRequested?: boolean; // visibilitychange flush deferred into the upload critical section
    commitInFlightBlockCount?: number; // Blocks covered by a checkpoint commit currently in flight
    committedPrefixCount?: number; // Terminal prefix commit length - finalize must never commit past this
    nextCommitAttemptTime?: number; // Backoff after a failed checkpoint commit (prevents per-chunk retry stalls)
    originalMediaStream?: MediaStream; // Store original stream reference for health checks
    debugInfo: {
        totalChunks: number;
        totalSize: number;
        lastChunkTime?: Date;
        uploadErrors?: number;
        sasTokenExpiry?: Date;
        lastSuccessfulUpload?: Date;
        sasTokenRetries?: number;
        consecutiveFailures?: number;
        lastErrorTime?: Date;
        circuitBreakerOpen?: boolean;
        circuitBreakerResetTime?: Date;
        consecutiveEmptyChunks?: number;
        mediaRecorderRecoveryAttempts?: number;
        intermediateCommits?: number;
    };
}

interface AzureUploadInfo {
    containerName: string;
    blobName: string;
    sasUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class RecordingService implements OnDestroy {
    private recordings = new Map<string, SubscriberRecording>();
    private recordingStatusSubject = new BehaviorSubject<Map<string, boolean>>(new Map());
    private streamReferences = new Map<string, any>(); // Store stream references for lookups
    private opentokService: any;
    private roomSessionService: any;
    private sessionListeners = new Map<string, () => void>();
    private videoToggleDebounceTime = 300; // Reduced from 500ms to 300ms for faster response
    private maxRecordingDuration = 1800; // 30 minutes max before creating new file
    private healthMonitorInterval: any; // Health monitoring interval

    // Front-end recording limits
    private readonly maxFrontendRecordings = 1; // Only 1 subscriber recording allowed
    private backendRecordingActive = false; // Session-scoped flag

    // Segment & commit management (crash-safety + SAS rotation).
    // NOTE: the backend issues a brand-new blob with every SAS token request, so a segment
    // is permanently bound to the blob it started with - mid-segment token refresh is unsafe.
    private readonly intermediateCommitBlockInterval = 5; // Commit staged blocks every ~5 blocks (~5s)
    private readonly intermediateCommitMaxIntervalMs = 10000; // ...or at the latest every 10 seconds
    private readonly intermediateCommitDrainThreshold = 10; // Skip checkpoints while the queue is this deep...
    private readonly intermediateCommitMaxStarvationMs = 30000; // ...unless no checkpoint happened for this long
    private readonly maxBlocksPerSegment = 45000; // Rotate before Azure's 50k committed-block limit
    private readonly sasRotationThresholdMs = 60 * 60 * 1000; // Rotate when token has <60min left
    private readonly maxUploadQueueLength = 900; // Hard cap (~15min of chunks) before emergency stop
    private readonly outageWindowMs = 2100000; // 35min sliding window for undeliverable-footage drops (>= 2x worst-case queue refill)
    private localVideoSubscriptionState = new Map<string, boolean>(); // Owner's local subscribeToVideo state
    private lastStartAttempt = new Map<string, number>(); // Throttles automatic start attempts
    private lastRotationTime = new Map<string, number>(); // Throttles segment rotations
    private restartHistory = new Map<string, number[]>(); // Sliding window of automatic restarts
    private queueCapHistory = new Map<string, number[]>(); // Sliding window of upload-queue cap events
    // streamId -> archiveId memo for same-blob SAS reuse. INERT under instant-start: reuse is
    // disabled (see getSasToken) because a dead attempt's SAS can resolve concurrently with a
    // live adoption and re-poison the memo, so every SAS request now mints a fresh unique blob.
    // The map is retained (writes are no-ops; deletes/clear are harmless) to keep the blast
    // radius of this change minimal; it can be removed in a dedicated cleanup.
    private pendingSasIssue = new Map<string, string>();
    private readonly userRecordingNoticesEnabled = true; // Single switch to silence recording flash messages
    private notifiedUserIssues = new Set<string>(); // Once-per-session-per-kind dedupe for user notices
    private everRecordedStreams = new Set<string>(); // Streams that produced at least one real chunk this session
    private rotatingStreams = new Set<string>(); // Guards against concurrent rotations
    private startInFlight = new Set<string>(); // Guards against concurrent start attempts
    private stoppingStreams = new Set<string>(); // Guards against concurrent stop/finalize of one stream
    private listenerSetupTimer: any = null; // Pending 1s listener-attach timer (cancelled on re-init/teardown)

    public recordingStatus$ = this.recordingStatusSubject.asObservable();

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private dbService: DbService,
        private diagnosticsService: DiagnosticsService,
        private flashMessageService: FlashMessageService
    ) {
        // Listen for page unload to cleanup recordings
        fromEvent(window, 'beforeunload').subscribe(() => {
            // Best-effort commit of staged blocks so the blob survives the unload
            this.attemptEmergencyCommits(true);
            this.cleanupAllRecordings();
        });

        // pagehide is the reliable teardown signal on mobile browsers where beforeunload
        // often never fires (backgrounded/killed tabs). Do NOT cleanup here: pagehide also
        // fires when the page enters the back/forward cache and recording must survive that.
        fromEvent(window, 'pagehide').subscribe(() => {
            this.attemptEmergencyCommits(true);
        });

        // Flush the staged block list whenever the tab is hidden (tab switch, minimize,
        // mobile app switch) - on mobile the OS may kill the tab without any further event.
        fromEvent(document, 'visibilitychange').subscribe(() => {
            if (document.visibilityState === 'hidden') {
                this.attemptEmergencyCommits(false);
            }
        });

        // Start health monitoring
        this.startHealthMonitoring();
    }

    /**
     * Debug logging utility - only logs in development environment
     * In production, this method does nothing to improve performance
     */
    private debugLog(message: string, ...args: any[]): void {
        if (!environment.production) {
            console.log(message, ...args);
        }
    }

    /**
     * Debug warn logging utility - only logs in development environment
     */
    private debugWarn(message: string, ...args: any[]): void {
        if (!environment.production) {
            console.warn(message, ...args);
        }
    }

    /**
     * Debug error logging utility - always logs errors regardless of environment
     */
    private debugError(message: string, ...args: any[]): void {
        console.error(message, ...args);
    }

    /**
     * Initialize recording service for publisher
     */
    initForPublisher(roomSessionService: any, opentokService: any): void {
        const sessionId = roomSessionService?.currentSessionId;
        const roomId = roomSessionService?.currentRoomId;

        this.debugLog(`🚀 [RECORDING SERVICE INIT] Initializing recording service for publisher - RoomID: ${roomId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Initializing recording service for publisher - RoomID: ${roomId}, SessionID: ${sessionId}`, 'info', {
            roomId: roomId,
            sessionId: sessionId,
            component: 'RecordingService',
            method: 'initForPublisher'
        });

        // Track recording service initialization
        this.diagnosticsService.trackEvent('session_init', `Recording service initialized for new session`, {
            roomId: roomId,
            sessionId: sessionId,
            component: 'RecordingService'
        });

        // Reset backend recording flag for new session
        this.backendRecordingActive = false;

        this.roomSessionService = roomSessionService;
        this.opentokService = opentokService;

        this.debugLog(`📝 [SERVICE REFS] Service references set - RoomID: ${roomId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Service references set - RoomID: ${roomId}, SessionID: ${sessionId}`, 'info', {
            roomId: roomId,
            sessionId: sessionId
        });

        // Initialize for new session
        this.debugLog(`🔄 [SESSION INIT] Initializing for new session - SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Initializing for new session - SessionID: ${sessionId}`, 'info', {
            sessionId: sessionId
        });
        this.initForSession();

        // Restart the 30s health monitor for this room - the previous room's teardown
        // (PublisherComponent.ngOnDestroy -> recordingService.ngOnDestroy) stopped it on
        // this singleton, and the constructor only runs once per tab. Idempotent.
        this.startHealthMonitoring();

        // Wait a bit for session to be fully ready, then setup listeners
        this.debugLog(`⏳ [LISTENER DELAY] Waiting 1s before setting up listeners (reduced from 1.5s) - SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Waiting 1s before setting up listeners (reduced from 1.5s) - SessionID: ${sessionId}`, 'info', {
            sessionId: sessionId
        });
        // Track the pending attach timer: a second initForPublisher within this 1s window
        // (fast room switch / reconnect) must cancel the first, otherwise both fire and the
        // second attach OVERWRITES the first's cleanup entries in sessionListeners - the
        // first handler set is orphaned on the singleton and every later streamCreated/
        // streamDestroyed is handled twice for the rest of the tab's life.
        if (this.listenerSetupTimer) {
            clearTimeout(this.listenerSetupTimer);
        }
        this.listenerSetupTimer = setTimeout(() => {
            this.listenerSetupTimer = null;
            this.debugLog(`🎧 [LISTENER SETUP] Setting up session listeners - SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Setting up session listeners - SessionID: ${sessionId}`, 'info', {
                sessionId: sessionId
            });
            this.setupSessionListeners();
        }, 1000); // Reduced from 1500ms to 1000ms
    }

    /**
     * Initialize recording state for a new session
     */
    private initForSession(): void {
        const sessionId = this.roomSessionService?.currentSessionId;

        this.debugLog(`🔄 [SESSION RESET] Initializing session state - SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Initializing session state - SessionID: ${sessionId}`, 'info', {
            sessionId: sessionId
        });

        // Reset backend recording flag for new session
        this.backendRecordingActive = false;
        this.debugLog(`🚫 [BACKEND FLAG] Backend recording flag reset - SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Backend recording flag reset - SessionID: ${sessionId}`, 'info', {
            sessionId: sessionId
        });

        // Clear any existing recordings from previous session
        this.debugLog(`🧹 [CLEANUP] Cleaning up recordings from previous session - SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Cleaning up recordings from previous session - SessionID: ${sessionId}`, 'info', {
            sessionId: sessionId
        });
        this.cleanupAllRecordings();
    }

    /**
     * Setup OpenTok session event listeners
     */
    private setupSessionListeners(): void {
        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;

        this.debugLog(`🎧 [SESSION LISTENERS] Setting up session event listeners - RoomID: ${roomId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Setting up session event listeners - RoomID: ${roomId}, SessionID: ${sessionId}`, 'info', {
            roomId: roomId,
            sessionId: sessionId
        });

        if (!this.opentokService) {
            console.error(`❌ [SESSION LISTENERS] No OpenTok service available - SessionID: ${sessionId}`);
            this.diagnosticsService.logError(`No OpenTok service available - SessionID: ${sessionId}`, null, {
                sessionId: sessionId
            });
            return;
        }

        // Listen for new streams (subscribers joining)
        const streamCreatedHandler = (event: any) => {
            // Extract stream from event (OpenTok wraps stream in event object)
            const stream = event.stream || event;
            this.debugLog(`📺 [STREAM CREATED] New stream detected - StreamID: ${stream?.streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`New stream detected - StreamID: ${stream?.streamId}, SessionID: ${sessionId}`, 'info', {
                streamId: stream?.streamId,
                sessionId: sessionId
            });

            this.handleStreamCreated(stream);
        };

        // Listen for streams destroyed (subscribers leaving)
        const streamDestroyedHandler = (event: any) => {
            // Extract stream from event (OpenTok wraps stream in event object)
            const stream = event.stream || event;
            this.debugLog(`📺 [STREAM DESTROYED] Stream destroyed - StreamID: ${stream?.streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Stream destroyed - StreamID: ${stream?.streamId}, SessionID: ${sessionId}`, 'info', {
                streamId: stream?.streamId,
                sessionId: sessionId
            });

            this.handleStreamDestroyed(stream);
        };

        // Try multiple event listener approaches for compatibility
        let listenersAttached = false;

        // Method 1: Try opentokService events first
        if (this.opentokService.on && typeof this.opentokService.on === 'function') {
            try {
                this.opentokService.on('streamCreated', streamCreatedHandler);
                this.opentokService.on('streamDestroyed', streamDestroyedHandler);
                listenersAttached = true;
            } catch (error) {
                // Silent error handling
            }
        }

        // Method 2: Try session events directly if first method failed
        if (!listenersAttached && this.opentokService.currentSession) {
            try {
                this.opentokService.currentSession.on('streamCreated', streamCreatedHandler);
                this.opentokService.currentSession.on('streamDestroyed', streamDestroyedHandler);
                listenersAttached = true;
            } catch (error) {
                // Silent error handling
            }
        }

        if (!listenersAttached) {
            console.error(`❌ [LISTENERS FAILED] Unable to attach any event listeners - SessionID: ${sessionId}`);
            this.diagnosticsService.logError(`Unable to attach any event listeners - SessionID: ${sessionId}`, null, {
                sessionId: sessionId
            });
        } else {
            this.debugLog(`✅ [LISTENERS SUCCESS] Event listeners successfully attached - SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Event listeners successfully attached - SessionID: ${sessionId}`, 'info', {
                sessionId: sessionId
            });
        }

        // Listen for session disconnect events
        if (this.opentokService.currentSession) {
            try {
                this.opentokService.currentSession.on('sessionDisconnected', (event: any) => {
                    this.debugLog(`🔌 [SESSION DISCONNECTED] Session disconnected - SessionID: ${sessionId}`);
                    this.diagnosticsService.logMessage(`Session disconnected - SessionID: ${sessionId}`, 'info', {
                        sessionId: sessionId
                    });
                    // Reset backend recording flag for next session
                    this.backendRecordingActive = false;
                });
            } catch (error) {
                // Silent error handling
            }
        }

        // Store handlers for cleanup
        this.sessionListeners.set('streamCreated', () => {
            try {
                if (this.opentokService.off) {
                    this.opentokService.off('streamCreated', streamCreatedHandler);
                }
                if (this.opentokService.currentSession && this.opentokService.currentSession.off) {
                    this.opentokService.currentSession.off('streamCreated', streamCreatedHandler);
                }
            } catch (error) {
                // Silent error handling
            }
        });

        this.sessionListeners.set('streamDestroyed', () => {
            try {
                if (this.opentokService.off) {
                    this.opentokService.off('streamDestroyed', streamDestroyedHandler);
                }
                if (this.opentokService.currentSession && this.opentokService.currentSession.off) {
                    this.opentokService.currentSession.off('streamDestroyed', streamDestroyedHandler);
                }
            } catch (error) {
                // Silent error handling
            }
        });
    }

    /**
     * Handle new stream created (potential subscriber)
     */
    private handleStreamCreated(stream: any): void {
        const sessionId = this.roomSessionService?.currentSessionId;

        // The stream object is passed directly from OpentokService, not wrapped in an event
        if (!stream || !stream.streamId) {
            this.debugLog(`⚠️ [STREAM CREATED] Invalid stream object - SessionID: ${sessionId}`, stream);
            this.diagnosticsService.logMessage(`Invalid stream object - SessionID: ${sessionId}`, 'warning', {
                sessionId: sessionId,
                streamData: JSON.stringify(stream)
            });
            return;
        }

        const streamId = stream.streamId;
        const participantName = this.getParticipantName(stream);
        const videoType = stream.videoType;
        const hasVideo = stream.hasVideo;

        this.debugLog(`📺 [STREAM CREATED] Stream created - StreamID: ${streamId}, Participant: ${participantName}, VideoType: ${videoType}, HasVideo: ${hasVideo}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Stream created - StreamID: ${streamId}, Participant: ${participantName}`, 'info', {
            streamId: streamId,
            participantName: participantName,
            videoType: videoType,
            hasVideo: hasVideo,
            sessionId: sessionId
        });

        // Store stream reference for later lookup
        this.streamReferences.set(streamId, stream);

        // Only record camera streams that are not our own publisher
        if (stream.videoType !== 'camera') {
            return;
        }

        if (this.isOwnPublisherStream(stream)) {
            return;
        }

        // Check if backend recording is already active (limit exceeded)
        if (this.backendRecordingActive) {
            this.debugLog(`🚫 [STREAM SKIP] Backend recording active - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Backend recording active, skipping stream - StreamID: ${streamId}`, 'info', {
                streamId: streamId,
                participantName: participantName,
                sessionId: sessionId
            });
            return;
        }

        // Check current active recordings count
        const currentActiveCount = this.getActiveRecordingsCount();
        this.debugLog(`📊 [STREAM CHECK] Active recordings: ${currentActiveCount}/${this.maxFrontendRecordings} - StreamID: ${streamId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Active recordings: ${currentActiveCount}/${this.maxFrontendRecordings}`, 'info', {
            streamId: streamId,
            currentActiveCount: currentActiveCount,
            maxFrontendRecordings: this.maxFrontendRecordings,
            sessionId: sessionId
        });

        // If we're at the limit, transition to backend recording. Two triggers, not one:
        // - activeCount: the classic case - a 2nd stream appears while the 1st is live.
        // - census (same filter as the release recompute in handleStreamDestroyed): activeCount
        //   alone misses the post-release drain window, where the survivor's finalize is still
        //   draining (isRecording=false) when the next guest joins - activeCount reads 0 with two
        //   remote cameras present, and both would end up frontend-recorded over the cap. This
        //   stream is already in streamReferences (set above), so the census includes it. It also
        //   closes the two-guests-join-back-to-back race (2nd create during 1st's acquisition).
        const remoteCameraCensus = Array.from(this.streamReferences.values())
            .filter(s => s && s.videoType === 'camera' && !this.isOwnPublisherStream(s)).length;
        if (currentActiveCount >= this.maxFrontendRecordings || remoteCameraCensus > this.maxFrontendRecordings) {
            this.debugLog(`🔄 [TRANSITION] Transitioning to backend recording - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Transitioning to backend recording`, 'info', {
                streamId: streamId,
                participantName: participantName,
                sessionId: sessionId
            });
            this.transitionToBackendRecording();
            return;
        }

        this.debugLog(`⏳ [STREAM DELAY] Yielding 150ms for the subscriber component to issue subscribe() - StreamID: ${streamId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Yielding 150ms before recording start (acquisition ladder handles readiness) - StreamID: ${streamId}`, 'info', {
            streamId: streamId,
            sessionId: sessionId
        });
        // STARTUP-GAP: this used to be a blind 1.2s wait ("Increased from 800ms to 1.2s") that
        // cost ~1s of unrecorded footage on every start. The acquisition ladder inside
        // startRecordingForSubscriber (registry/DOM/event strategies with exponential backoff,
        // ~8s adaptive window) already handles a not-yet-ready subscriber, so the fixed wait is
        // redundant. Keep a minimal yield so the subscriber component's own streamCreated
        // handler runs first and actually calls session.subscribe().
        setTimeout(() => {
            this.startRecordingForSubscriber(stream);
        }, 150);
    }

    /**
     * Handle stream destroyed (subscriber leaving)
     */
    private handleStreamDestroyed(stream: any): void {
        const sessionId = this.roomSessionService?.currentSessionId;

        // The stream object is passed directly from OpentokService, not wrapped in an event
        if (!stream || !stream.streamId) {
            this.debugLog(`⚠️ [STREAM DESTROYED] Invalid stream object - SessionID: ${sessionId}`, stream);
            this.diagnosticsService.logMessage(`Invalid stream object on destroy - SessionID: ${sessionId}`, 'warning', {
                sessionId: sessionId,
                streamData: JSON.stringify(stream)
            });
            return;
        }

        const streamId = stream.streamId;
        const participantName = this.getParticipantName(stream);

        this.debugLog(`🔚 [STREAM DESTROYED] Stream destroyed - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Stream destroyed - StreamID: ${streamId}`, 'info', {
            streamId: streamId,
            participantName: participantName,
            sessionId: sessionId
        });

        const recording = this.recordings.get(streamId);
        const recordingDuration = recording ? (Date.now() - recording.startTime.getTime()) / 1000 : 0;

        if (recording) {
            this.debugLog(`📊 [RECORDING ENDED] Recording duration: ${recordingDuration}s, Chunks: ${recording.debugInfo.totalChunks} - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Recording ended - Duration: ${recordingDuration}s`, 'info', {
                streamId: streamId,
                duration: recordingDuration,
                totalChunks: recording.debugInfo.totalChunks,
                sessionId: sessionId
            });
        }

        // Clean up video toggle listener
        const videoToggleCleanup = this.sessionListeners.get(`videoToggle_${streamId}`);
        if (videoToggleCleanup) {
            videoToggleCleanup();
            this.sessionListeners.delete(`videoToggle_${streamId}`);
        }

        // Remove stream reference
        this.streamReferences.delete(streamId);
        this.localVideoSubscriptionState.delete(streamId);
        this.lastStartAttempt.delete(streamId);
        this.lastRotationTime.delete(streamId);
        this.restartHistory.delete(streamId);
        this.queueCapHistory.delete(streamId);
        this.everRecordedStreams.delete(streamId);
        this.pendingSasIssue.delete(streamId);

        // Stop recording if active
        this.stopRecordingForSubscriber(streamId);

        // DESIRED-STATE re-evaluation of backendRecordingActive (it was edge-triggered on the way
        // UP only). transitionToBackendRecording sets it when a 2nd remote camera stream appears
        // and finalizes every frontend recording; the ONLY resets are initForPublisher /
        // initForSession / sessionDisconnected. So once a session briefly reached 3+ participants,
        // handleStreamCreated's `if (this.backendRecordingActive) return` refused every later
        // stream for the REST of the OpenTok session - and since an expert who stays in the room
        // keeps ONE session across rotating guests, that is the rest of their shift.
        // Invisible today only because these accounts run OpenTok archiveMode "always"; the moment
        // they move to "manual", kickUser stops the server archive once the room is back to <=2
        // and NOTHING records the remaining calls. Recompute from the live stream set instead.
        // streamReferences already had this stream removed above, so the count is post-departure.
        if (this.backendRecordingActive) {
            const remoteCameraStreams = Array.from(this.streamReferences.values())
                .filter(s => s && s.videoType === 'camera' && !this.isOwnPublisherStream(s));
            if (remoteCameraStreams.length <= this.maxFrontendRecordings) {
                this.backendRecordingActive = false;
                this.debugLog(`✅ [BACKEND RELEASE] Remote camera streams back to ${remoteCameraStreams.length}/${this.maxFrontendRecordings} - frontend recording re-armed - SessionID: ${sessionId}`);
                this.diagnosticsService.logMessage(`Backend recording released, frontend recording re-armed`, 'info', {
                    remoteCameraStreams: remoteCameraStreams.length,
                    maxFrontendRecordings: this.maxFrontendRecordings,
                    sessionId: sessionId
                });
                // A backend release is a deliberate topology change, not a crash loop - start the
                // survivors from a clean slate. Without this, recoverMissedRecordings charges each
                // re-arm to the 3-per-5-min restartHistory failure budget (a flapping 3rd
                // participant then silently blocks the survivor's recording for minutes), and a
                // lastStartAttempt stamped by the aborted start during the transition trips the
                // 25s throttle. Mirrors the per-stream clears done for the DESTROYED stream above.
                for (const s of remoteCameraStreams) {
                    this.restartHistory.delete(s.streamId);
                    this.lastStartAttempt.delete(s.streamId);
                }
                // Re-arm now. NOTE: recoverMissedRecordings skips a survivor whose entry is still
                // in this.recordings - i.e. while the finalize kicked off by
                // transitionToBackendRecording is still draining (the entry is deleted in that
                // finalize's finally). That guard is what prevents a duplicate concurrent
                // recording, so do NOT bypass it; instead retry once shortly after, which covers
                // the normal drain (~1-3s). Pathological drains fall back to the 30s health check.
                this.recoverMissedRecordings();
                setTimeout(() => {
                    if (!this.backendRecordingActive) {
                        this.recoverMissedRecordings();
                    }
                }, 5000);
            }
        }
    }

    /**
     * Check if this is our own publisher stream
     */
    private isOwnPublisherStream(stream: any): boolean {
        // Add null checks for safety
        if (!stream || !stream.connection || !this.opentokService.currentSession || !this.opentokService.currentSession.connection) {
            return false;
        }

        // Check if this stream belongs to our own session
        return stream.connection.connectionId === this.opentokService.currentSession.connection.connectionId;
    }

    /**
     * Start recording for a subscriber
     */
    private async startRecordingForSubscriber(stream: any): Promise<void> {
        const streamId = stream.streamId;
        const participantName = this.getParticipantName(stream);
        const participantUserId = this.getParticipantUserId(stream);
        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;

        // Bookkeeping for health-check recovery throttling
        this.lastStartAttempt.set(streamId, Date.now());

        // Guard against concurrent start attempts for the same stream (the recordings entry
        // is only created after the async SAS fetch, so the Map check alone is not race-safe)
        if (this.startInFlight.has(streamId)) {
            this.debugLog(`⚠️ [START IN FLIGHT] Start already in progress - StreamID: ${streamId}, SessionID: ${sessionId}`);
            return;
        }

        // Owner has locally unsubscribed from this video - recording would capture no usable frames
        if (this.localVideoSubscriptionState.get(streamId) === false) {
            this.debugLog(`🚫 [LOCAL UNSUB] Owner locally unsubscribed from video - skipping recording start - StreamID: ${streamId}, SessionID: ${sessionId}`);
            return;
        }

        this.debugLog(`🎬 [RECORDING SERVICE] Starting recording for subscriber - StreamID: ${streamId}, Participant: ${participantName}, RoomID: ${roomId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Starting recording for subscriber`, 'info', {
            streamId: streamId,
            participantName: participantName,
            participantUserId: participantUserId,
            roomId: roomId,
            sessionId: sessionId
        });

        // Track this important event as non-fatal
        this.diagnosticsService.trackEvent('auto_recording_start', `Auto recording started`, {
            streamId: streamId,
            participantName: participantName,
            participantUserId: participantUserId,
            roomId: roomId,
            sessionId: sessionId
        });

        // Skip if already recording
        if (this.recordings.has(streamId)) {
            this.debugLog(`⚠️ [RECORDING EXISTS] Already recording for StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Already recording for stream`, 'warning', {
                streamId: streamId,
                sessionId: sessionId
            });

            return;
        }

        // Safety check: Ensure we don't exceed the limit during edge cases
        if (this.backendRecordingActive) {
            this.debugLog(`🚫 [BACKEND ACTIVE] Backend recording active, skipping frontend - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Backend recording active, skipping frontend`, 'info', {
                streamId: streamId,
                sessionId: sessionId
            });
            return;
        }

        const currentActiveCount = this.getActiveRecordingsCount();
        if (currentActiveCount >= this.maxFrontendRecordings) {
            this.debugLog(`📊 [LIMIT REACHED] Max recordings reached (${currentActiveCount}/${this.maxFrontendRecordings}), transitioning to backend - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Max recordings reached, transitioning to backend`, 'info', {
                streamId: streamId,
                currentActiveCount: currentActiveCount,
                maxFrontendRecordings: this.maxFrontendRecordings,
                sessionId: sessionId
            });

            // Track recording limit reached event
            this.diagnosticsService.trackEvent('recording_limit_reached', `Frontend recording limit reached, transitioning to backend`, {
                streamId: streamId,
                currentActiveCount: currentActiveCount,
                maxFrontendRecordings: this.maxFrontendRecordings,
                sessionId: sessionId,
                roomId: this.roomSessionService?.currentRoomId,
                participantName: this.getParticipantName(stream)
            });

            this.transitionToBackendRecording();
            return;
        }

        // Always setup video toggle listener first, regardless of initial video state
        this.debugLog(`📺 [VIDEO LISTENER] Setting up video toggle listener - StreamID: ${streamId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Setting up video toggle listener`, 'info', {
            streamId: streamId,
            sessionId: sessionId
        });
        this.setupVideoToggleListener(stream);

        // Skip if video is not available initially, but listener is now set up
        if (!stream.hasVideo) {
            this.debugLog(`⚠️ [NO VIDEO] Stream has no video initially - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Stream has no video initially`, 'warning', {
                streamId: streamId,
                sessionId: sessionId
            });
            // But if this was called from startNewRecordingWithVideoValidation, force continue anyway
            const isForceStart = this.sessionListeners.has(`forceStart_${streamId}`);
            if (!isForceStart) {
                this.debugLog(`❌ [SKIP NO VIDEO] Skipping recording due to no video - StreamID: ${streamId}, SessionID: ${sessionId}`);
                this.diagnosticsService.logMessage(`Skipping recording due to no video`, 'info', {
                    streamId: streamId,
                    sessionId: sessionId
                });

                // Track auto recording skip due to no video
                this.diagnosticsService.trackEvent('recording_skip', `Auto recording skipped - no video available`, {
                    streamId: streamId,
                    sessionId: sessionId,
                    reason: 'no_video'
                });

                return;
            } else {
                this.debugLog(`⚡ [FORCE START] Force starting despite no video - StreamID: ${streamId}, SessionID: ${sessionId}`);
                this.diagnosticsService.logMessage(`Force starting recording despite no video`, 'info', {
                    streamId: streamId,
                    sessionId: sessionId
                });
                // Remove the force flag
                this.sessionListeners.delete(`forceStart_${streamId}`);
                // Continue to start recording setup
            }
        }

        this.startInFlight.add(streamId);
        try {
            this.debugLog(`🔄 [RECORDING SETUP] Starting recording setup process - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Starting recording setup process`, 'info', {
                streamId: streamId,
                sessionId: sessionId
            });
            const currentActiveCount = this.getActiveRecordingsCount();

            // STARTUP-GAP: the SAS fetch is a network round trip (Firebase function; can be
            // seconds on a cold start) that used to run AFTER stream acquisition, serially
            // delaying MediaRecorder.start() - i.e. unrecorded footage. Kick it off NOW so it
            // resolves while the MediaStream is being acquired. The no-op catch prevents an
            // unhandled rejection if we bail out before the await; the await below still
            // receives the original rejection and routes it through the existing catch.
            const uploadInfoPromise = this.getAzureUploadInfo(streamId);
            uploadInfoPromise.catch(() => { /* handled at the await site */ });

            // Single acquisition: validateStreamReadiness used to run the SAME multi-retry
            // acquisition ladder and discard the result before acquiring again - doubling
            // startup latency. Acquire once and validate the returned stream directly.
            this.debugLog(`📹 [MEDIA STREAM] Acquiring + validating subscriber media stream - StreamID: ${streamId}, SessionID: ${sessionId}`);
            const mediaStream = await this.acquireValidatedMediaStream(streamId);
            if (!mediaStream) {
                console.error(`❌ [STREAM NOT READY] Stream not ready for recording - StreamID: ${streamId}, SessionID: ${sessionId}`);
                this.diagnosticsService.logError(`Stream not ready for recording`, null, {
                    streamId: streamId,
                    sessionId: sessionId,
                    reason: 'stream_not_ready'
                });

                // Notify backend of recording start failure
                this.notifyRecordingFailure(
                    sessionId || 'unknown',
                    'RECORDING_START_FAILED',
                    {
                        streamId: streamId,
                        reason: 'stream_not_ready',
                        participantName: participantName,
                        roomId: roomId
                    }
                );

                return;
            }

            // Remove audio tracks - video only recording
            const videoOnlyStream = this.createVideoOnlyStream(mediaStream, streamId);
            const videoTracks = videoOnlyStream.getVideoTracks();
            this.debugLog(`📊 [STREAM INFO] Video tracks: ${videoTracks.length} - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Video tracks info`, 'info', {
                streamId: streamId,
                videoTracksCount: videoTracks.length,
                sessionId: sessionId
            });

            // INSTANT-START: do NOT await the SAS round trip here. The backend call is a
            // Firebase function that can take seconds on a cold start - every second spent
            // waiting is unrecorded footage the expert can already SEE on screen. Start the
            // MediaRecorder immediately in a sasPending state: chunks buffer in uploadQueue
            // (bounded - the SAS retry ladder settles within ~96s worst case ≈ 96 one-second
            // chunks, far under the queue cap) and the adoption continuation below wires the
            // SAS in and drains the queue the moment it arrives.
            const mediaRecorder = this.createMediaRecorder(videoOnlyStream, streamId);

            // Create recording entry (SAS not adopted yet - uploads are held by sasPending)
            const recording: SubscriberRecording = {
                subscriberId: streamId,
                streamId: streamId,
                participantName: participantName,
                mediaRecorder: mediaRecorder,
                startTime: new Date(),
                isRecording: true,
                isVideoEnabled: true,
                lastToggleTime: new Date(),
                bufferChunks: [],
                sasUrl: '',
                blockIds: [],
                isPaused: false,
                uploadQueue: [],
                isUploading: false,
                blobPath: '', // Bound when the SAS is adopted - this segment then owns exactly that blob
                sasPending: true,
                pendingUploadInfo: uploadInfoPromise,
                lastCommittedBlockCount: 0,
                originalMediaStream: videoOnlyStream, // Store original stream reference for health checks
                debugInfo: {
                    totalChunks: 0,
                    totalSize: 0,
                    uploadErrors: 0,
                    // Placeholder until the real expiry is parsed from the adopted SAS URL. Far
                    // enough in the future that no rotation/refresh logic can trigger while
                    // pending (evaluateSegmentRotation additionally skips sasPending entries).
                    sasTokenExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000),
                    sasTokenRetries: 0,
                    consecutiveFailures: 0,
                    circuitBreakerOpen: false,
                    consecutiveEmptyChunks: 0,
                    mediaRecorderRecoveryAttempts: 0,
                    intermediateCommits: 0
                }
            };

            this.debugLog(`📝 [RECORDING CREATED] Recording entry created (SAS pending) - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Recording entry created (SAS pending)`, 'info', {
                streamId: streamId,
                participantName: participantName,
                sessionId: sessionId
            });
            this.recordings.set(streamId, recording);
            // NOTE: the pendingSasIssue memo is NOT consumed here - adoption owns that
            // (see adoptUploadInfo). Until then a failed start may re-issue the same row.

            // Final safety check before starting MediaRecorder
            if (this.backendRecordingActive) {
                this.debugLog(`⚠️ [BACKEND CHECK] Backend became active, canceling recording - StreamID: ${streamId}, SessionID: ${sessionId}`);
                this.diagnosticsService.logMessage(`Backend became active, canceling recording`, 'warning', {
                    streamId: streamId,
                    sessionId: sessionId
                });
                this.recordings.delete(streamId);
                return;
            }

            // Start recording with enhanced monitoring and forced chunk collection
            const startTime = new Date();
            this.debugLog(`▶️ [RECORDING START] Starting MediaRecorder with WebM timeslice for better compatibility - StreamID: ${streamId}, StartTime: ${startTime.toISOString()}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Starting MediaRecorder with WebM timeslice for better compatibility`, 'info', {
                streamId: streamId,
                startTime: startTime.toISOString(),
                sessionId: sessionId
            });

            // Start MediaRecorder WITH timeslice for WebM format compatibility
            // This ensures proper WebM container structure
            mediaRecorder.start(1000); // 1 second timeslice for WebM compatibility

            // SAS adoption continuation: wires the prefetched SAS into the live recording and
            // drains the buffered chunks. On failure the recording is torn down exactly like a
            // failed start today (health-check recovery then retries with a fresh SAS attempt).
            uploadInfoPromise
                .then(uploadInfo => this.adoptUploadInfo(streamId, recording, uploadInfo))
                .catch(error => this.handleSasStartFailure(streamId, recording, error));

            // Force chunk collection every 2 seconds using requestData()
            const chunkTimer = setInterval(() => {
                const currentRecording = this.recordings.get(streamId);
                if (!currentRecording || !currentRecording.isRecording) {
                    clearInterval(chunkTimer);
                    return;
                }

                if (mediaRecorder.state === 'recording') {
                    const elapsedTime = (Date.now() - currentRecording.startTime.getTime()) / 1000;

                    // PROACTIVE SWAP FOLLOW: OpenTok replaces the subscriber's srcObject track a
                    // few seconds into a session (renegotiation). Our recorder is bound to the
                    // ORIGINAL track, which then keeps readyState==='live' but emits only empty
                    // chunks (zombie). Waiting for 5 empty chunks to react costs ~5s of gap.
                    // Instead, every tick compare the recorded track id against the subscriber's
                    // CURRENT srcObject track id: the instant a DIFFERENT live track appears
                    // ('swapped'), finalize this segment and restart on the new track. Only
                    // 'swapped' is acted on here (a new live track definitively exists) - 'dead'/
                    // transient states are left to the empty-chunk path below, which waits for
                    // more evidence, so a momentary registry/DOM read can't cause a false restart.
                    // Skip while the swap machinery already owns this stream or SAS is pending
                    // (no committed footage to preserve yet; the acquisition just happened).
                    if (!currentRecording.sasPending && !this.rotatingStreams.has(streamId)
                            && (currentRecording.debugInfo.totalChunks || 0) > 0
                            && this.verifyStreamHealthAuthoritative(streamId) === 'swapped') {
                        this.debugLog(`🔄 [PROACTIVE SWAP] Subscriber track swapped - starting the new segment NOW, finalizing the old one in the background at ${elapsedTime.toFixed(1)}s - StreamID: ${streamId}, SessionID: ${sessionId}`);
                        clearInterval(chunkTimer);
                        // Parallel restart: begin the replacement segment immediately and drain +
                        // commit the old one in the background, so the mid-session gap shrinks from
                        // ~finalize-time to ~acquire-time. The old serial path (finalize THEN
                        // restart) still backs the dead/error recoveries via handleStreamSwapRecovery.
                        this.handleProactiveSwapParallel(streamId);
                        return;
                    }

                    // Enhanced video track health verification with multiple fallback methods
                    let streamHealthy = true;
                    let healthCheckDetails = '';

                    // Method 1: Check MediaRecorder stream property (can be unreliable)
                    const recordingMediaStream = currentRecording.mediaRecorder?.stream;
                    if (recordingMediaStream && recordingMediaStream.getVideoTracks) {
                        const videoTracks = recordingMediaStream.getVideoTracks();
                        const activeVideoTrack = videoTracks.find(track => track.readyState === 'live' && track.enabled);

                        if (!activeVideoTrack) {
                            healthCheckDetails += 'MediaRecorder.stream: no active tracks; ';

                            // Method 2: Check if MediaRecorder is still in recording state
                            if (mediaRecorder.state !== 'recording') {
                                healthCheckDetails += 'MediaRecorder.state: not recording; ';
                                streamHealthy = false;
                            } else {
                                // Method 3: Check original stream reference if available
                                if (currentRecording.originalMediaStream) {
                                    const originalVideoTracks = currentRecording.originalMediaStream.getVideoTracks();
                                    const originalActiveTrack = originalVideoTracks.find(track => track.readyState === 'live' && track.enabled);

                                    if (!originalActiveTrack) {
                                        healthCheckDetails += 'originalStream: no active tracks; ';
                                        streamHealthy = false;
                                    } else {
                                        healthCheckDetails += 'originalStream: healthy; ';
                                        // Stream appears healthy via original reference, continue recording
                                        streamHealthy = true;
                                    }
                                } else {
                                    // No original stream reference, be conservative and continue
                                    healthCheckDetails += 'no originalStream reference; ';
                                    streamHealthy = true; // Don't stop recording without solid evidence
                                }
                            }
                        } else {
                            healthCheckDetails += 'MediaRecorder.stream: healthy; ';
                        }
                    } else {
                        healthCheckDetails += 'MediaRecorder.stream: unavailable; ';
                        // If we can't access the stream property, don't assume it's dead
                        streamHealthy = true;
                    }

                    // The captured track snapshot looks dead - but BOTH references above point at
                    // the SAME track objects, so an OpenTok stream swap (camera change, screen-share
                    // swap, media reconnect) is indistinguishable from a truly dead stream here.
                    // Re-acquire the CURRENT stream from OpenTok before deciding anything.
                    if (!streamHealthy) {
                        const verdict = this.verifyStreamHealthAuthoritative(streamId);
                        this.debugLog(`📹 [VIDEO TRACK CHECK] Captured tracks dead (${healthCheckDetails}), authoritative verdict: ${verdict} - StreamID: ${streamId}, SessionID: ${sessionId}`);

                        if (verdict === 'healthy') {
                            // OpenTok still exposes the same live track - transient glitch, keep going
                        } else if (verdict === 'video_off') {
                            // Remote video is off / owner unsubscribed - pause and let the standard
                            // videoEnabled toggle path resume when the video comes back
                            clearInterval(chunkTimer);
                            this.pauseRecording(streamId);
                            return;
                        } else {
                            // 'swapped' (a NEW live track exists) or 'dead' - recover by finalizing
                            // this segment and restarting on the current stream instead of silently
                            // stopping for the rest of the session.
                            clearInterval(chunkTimer);
                            this.handleStreamSwapRecovery(streamId, verdict === 'swapped' ? 'health_check_swap' : 'health_check_dead');
                            return;
                        }
                    } else if (healthCheckDetails.includes('no active tracks')) {
                        // Log the potential issue but continue recording
                        this.debugLog(`📹 [VIDEO TRACK WARNING] Stream health uncertain (${healthCheckDetails}), continuing recording - StreamID: ${streamId}, SessionID: ${sessionId}`);
                    }

                    // Check if we've had too many consecutive empty chunks - might indicate real stream issues
                    // Increased threshold from 2 to 5 to be less aggressive about stopping
                    if ((currentRecording.debugInfo.consecutiveEmptyChunks || 0) >= 5) {
                        // ZOMBIE-TRACK RECOVERY: sustained empty chunks while the RECORDED track
                        // still reads readyState==='live' is the classic zombie signature -
                        // OpenTok swapped the underlying track (e.g. an early provisional
                        // video.srcObject captured before the subscriber stabilized, which
                        // OpenTok replaces once real media flows) and left our captured track
                        // live-but-dead. Raw readyState LIES here, so the old check kept
                        // resetting the counter and the recording produced empty chunks for the
                        // rest of the session. Consult the AUTHORITATIVE source (OpenTok registry)
                        // instead and recover onto the current stream - identical handling to the
                        // streamHealthy=false branch above.
                        const emptyVerdict = this.verifyStreamHealthAuthoritative(streamId);
                        this.debugLog(`🛑 [STREAM END DETECTED] ${currentRecording.debugInfo.consecutiveEmptyChunks} empty chunks, authoritative verdict: ${emptyVerdict} - StreamID: ${streamId}, SessionID: ${sessionId}`);

                        if (emptyVerdict === 'healthy') {
                            // OpenTok still exposes the SAME live track - a genuine transient
                            // encoder hiccup, not a swap. Reset the counter and keep recording.
                            this.debugLog(`📺 [STREAM ALIVE] Authoritative stream healthy, continuing despite empty chunks - StreamID: ${streamId}, SessionID: ${sessionId}`);
                            currentRecording.debugInfo.consecutiveEmptyChunks = 0;
                        } else if (emptyVerdict === 'video_off') {
                            // Remote video is off / owner unsubscribed - pause; the videoEnabled
                            // toggle path resumes when it returns.
                            clearInterval(chunkTimer);
                            this.pauseRecording(streamId);
                            return;
                        } else {
                            // 'swapped' (a NEW live track exists) or 'dead' - finalize the good
                            // segment captured so far and restart on the CURRENT stream instead
                            // of accumulating empty chunks silently for the rest of the session.
                            this.debugLog(`🔄 [ZOMBIE RECOVERY] Recording track is a zombie (${emptyVerdict}) - finalizing + restarting on current stream - StreamID: ${streamId}, SessionID: ${sessionId}`);
                            clearInterval(chunkTimer);
                            this.handleStreamSwapRecovery(streamId, emptyVerdict === 'swapped' ? 'empty_chunks_swap' : 'empty_chunks_dead');
                            return;
                        }
                    }

                    // Enhanced logging for chunk collection debugging
                    const chunkCount = currentRecording.debugInfo.totalChunks;
                    const totalSize = currentRecording.debugInfo.totalSize;
                    const queueLength = currentRecording.uploadQueue.length;

                    // Get video track status for logging (reuse existing stream reference)
                    let videoTrackStatus = 'unknown';
                    if (recordingMediaStream && recordingMediaStream.getVideoTracks) {
                        const videoTracks = recordingMediaStream.getVideoTracks();
                        const activeVideoTrack = videoTracks.find(track => track.readyState === 'live' && track.enabled);
                        videoTrackStatus = activeVideoTrack ? activeVideoTrack.readyState : 'inactive';
                    }

                    this.debugLog(`⏰ [FORCED CHUNK] #${chunkCount + 1} at ${elapsedTime.toFixed(1)}s - Total: ${totalSize}B, Queue: ${queueLength}, VideoTrack: ${videoTrackStatus} - StreamID: ${streamId}, SessionID: ${sessionId}`);

                    try {
                        // Log MediaRecorder state before requestData
                        this.debugLog(`📹 [RECORDER STATE] Before requestData: ${mediaRecorder.state} - StreamID: ${streamId}`);
                        mediaRecorder.requestData();
                        this.debugLog(`✅ [DATA REQUESTED] RequestData called successfully - StreamID: ${streamId}`);
                    } catch (error) {
                        console.error(`❌ [CHUNK ERROR] Failed to request data - StreamID: ${streamId}`, error);
                        this.diagnosticsService.logError(`Failed to request chunk data`, error, {
                            streamId: streamId,
                            elapsedTime: elapsedTime,
                            mediaRecorderState: mediaRecorder.state,
                            sessionId: sessionId
                        });
                    }
                }
            }, 1000); // Force chunks every 1 second (matching timeslice for WebM compatibility)

            // Backup chunk collection - more aggressive approach if primary timer fails
            const backupChunkTimer = setInterval(() => {
                const currentRecording = this.recordings.get(streamId);
                if (!currentRecording || !currentRecording.isRecording) {
                    clearInterval(backupChunkTimer);
                    return;
                }

                // Only fire backup if primary hasn't gotten chunks recently
                const timeSinceLastChunk = currentRecording.debugInfo.lastChunkTime
                    ? (Date.now() - currentRecording.debugInfo.lastChunkTime.getTime()) / 1000
                    : 999;

                if (mediaRecorder.state === 'recording' && timeSinceLastChunk > 3) {
                    const elapsedTime = (Date.now() - currentRecording.startTime.getTime()) / 1000;
                    this.debugLog(`🚨 [BACKUP CHUNK] Primary timer failed, forcing backup chunk at ${elapsedTime.toFixed(1)}s - StreamID: ${streamId}`);

                    try {
                        mediaRecorder.requestData();
                        this.debugLog(`✅ [BACKUP SUCCESS] Backup requestData called - StreamID: ${streamId}`);
                    } catch (error) {
                        console.error(`❌ [BACKUP ERROR] Backup chunk request failed - StreamID: ${streamId}`, error);
                    }
                }
            }, 3000); // Backup every 3 seconds

            // Store timers for cleanup
            if (recording) {
                (recording as any).chunkTimer = chunkTimer;
                (recording as any).backupChunkTimer = backupChunkTimer;
            }

            // Monitor MediaRecorder state periodically
            const stateMonitor = setInterval(() => {
                const currentRecording = this.recordings.get(streamId);
                if (!currentRecording || !currentRecording.isRecording) {
                    clearInterval(stateMonitor);
                    return;
                }

                const elapsed = (Date.now() - currentRecording.startTime.getTime()) / 1000;
                const timeSinceLastChunk = currentRecording.debugInfo.lastChunkTime
                    ? (Date.now() - currentRecording.debugInfo.lastChunkTime.getTime()) / 1000
                    : elapsed;

                // Log if there might be an issue (no chunks for more than 10 seconds with forced chunks)
                if (timeSinceLastChunk > 10) {
                    this.debugLog(`⚠️ [RECORDING STALL] No chunks for ${timeSinceLastChunk}s (should be max 2s with forced chunks) - StreamID: ${streamId}, SessionID: ${sessionId}`);
                    this.diagnosticsService.logMessage(`Recording stall detected - no chunks for ${timeSinceLastChunk}s`, 'warning', {
                        streamId: streamId,
                        timeSinceLastChunk: timeSinceLastChunk,
                        sessionId: sessionId
                    });
                }

                // Evaluate whether this segment needs rotation (token expiry / block limit / auth failure)
                this.evaluateSegmentRotation(streamId);

                // Check stream health
                const stream = this.streamReferences.get(streamId);
                if (stream) {
                    const videoTracks = currentRecording.mediaRecorder.stream?.getVideoTracks() || [];
                    const activeVideoTracks = videoTracks.filter((track: any) => track.readyState === 'live');
                    if (activeVideoTracks.length === 0) {
                        this.debugLog(`❌ [STREAM HEALTH] No active video tracks - StreamID: ${streamId}, SessionID: ${sessionId}`);
                        this.diagnosticsService.logError(`No active video tracks`, null, {
                            streamId: streamId,
                            sessionId: sessionId
                        });
                    }
                }
            }, 3000); // Check every 3 seconds

            // Store monitor for cleanup
            this.sessionListeners.set(`stateMonitor_${streamId}`, () => {
                clearInterval(stateMonitor);
            });

            this.updateRecordingStatus();
            this.debugLog(`✅ [RECORDING SUCCESS] Recording started successfully - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Recording started successfully`, 'info', {
                streamId: streamId,
                participantName: participantName,
                sessionId: sessionId
            });

            // Track successful recording start as non-fatal event. Blob details are logged by
            // adoptUploadInfo once the (still in-flight) SAS resolves.
            this.diagnosticsService.trackEvent('recording_success', `Recording started successfully`, {
                streamId: streamId,
                participantName: participantName,
                participantUserId: participantUserId,
                sessionId: sessionId,
                roomId: roomId,
                sasPending: true,
                recordingDetails: {
                    totalChunks: recording.debugInfo.totalChunks,
                    totalSize: recording.debugInfo.totalSize
                }
            });

        } catch (error) {
            console.error(`❌ [RECORDING FAILED] Failed to start recording - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`, error);
            this.diagnosticsService.logError(`Failed to start recording`, error, {
                streamId: streamId,
                participantName: participantName,
                sessionId: sessionId
            });

            // Track recording failure as critical event
            this.diagnosticsService.trackEvent('recording_failure', `Recording failed to start`, {
                streamId: streamId,
                participantName: participantName,
                participantUserId: participantUserId,
                sessionId: sessionId,
                error: error?.message || error
            });

            // Clean up a partially-created recording entry so health-check recovery can retry
            // cleanly. With instant-start the recorder may already be running and have produced
            // buffered chunks - nothing is durable until a block is uploaded, so gate on
            // blockIds (durability), not totalChunks, and stop the recorder explicitly.
            const partialRecording = this.recordings.get(streamId);
            if (partialRecording && partialRecording.blockIds.length === 0
                    && (partialRecording.lastCommittedBlockCount || 0) === 0) {
                partialRecording.isRecording = false;
                try {
                    if (partialRecording.mediaRecorder && partialRecording.mediaRecorder.state !== 'inactive') {
                        partialRecording.mediaRecorder.stop();
                    }
                } catch (stopError) {
                    // Best effort - the entry is being discarded either way
                }
                partialRecording.uploadQueue = [];
                if ((partialRecording as any).chunkTimer) {
                    clearInterval((partialRecording as any).chunkTimer);
                }
                if ((partialRecording as any).backupChunkTimer) {
                    clearInterval((partialRecording as any).backupChunkTimer);
                }
                const monitorCleanup = this.sessionListeners.get(`stateMonitor_${streamId}`);
                if (monitorCleanup) {
                    monitorCleanup();
                    this.sessionListeners.delete(`stateMonitor_${streamId}`);
                }
                this.recordings.delete(streamId);
                this.updateRecordingStatus();
            }
        } finally {
            this.startInFlight.delete(streamId);
        }
    }

    /**
     * INSTANT-START adoption: wire the prefetched SAS into a live sasPending recording and
     * drain the chunks buffered while the backend round trip was in flight.
     *
     * Adoption is the EXCLUSIVE owner of the pending->active transition: processUploadQueue
     * holds uploads while sasPending (so ensureValidSasToken can never race a second SAS
     * request against this prefetch and split the segment across two blobs).
     */
    private adoptUploadInfo(streamId: string, recording: SubscriberRecording, uploadInfo: AzureUploadInfo): void {
        // The recording may have ended (stream destroyed / stop / cleanup) while the SAS was in
        // flight. finalizeRecording awaits pendingUploadInfo itself, so nothing to do here.
        if (this.recordings.get(streamId) !== recording) {
            this.debugLog(`🔐 [SAS ADOPT SKIP] Recording gone before SAS arrived - StreamID: ${streamId}`);
            return;
        }

        // Backend (server-side OpenTok) recording took over while the SAS was in flight - it is
        // the authoritative archive now. Adopting here would commit a redundant frontend segment
        // overlapping the backend one. Drop the buffer instead; transitionToBackendRecording's
        // finalizeAndCleanupRecording sweep (which set the flag) then deletes this entry. NOTE:
        // this deliberately does NOT fire for terminal-stop / recovery teardowns (those leave
        // backendRecordingActive false), where adopting to save the buffered footage is correct.
        if (this.backendRecordingActive) {
            this.debugLog(`🔐 [SAS ADOPT SKIP] Backend recording active - dropping buffer instead of adopting - StreamID: ${streamId}, BufferedChunks: ${recording.uploadQueue.length}`);
            recording.sasPending = false;
            recording.pendingUploadInfo = undefined;
            recording.uploadQueue = [];
            return;
        }

        recording.sasUrl = uploadInfo.sasUrl;
        recording.blobPath = uploadInfo.sasUrl.split('?')[0]; // This segment is bound to exactly this blob
        // Actual expiry parsed from the SAS URL 'se' param; conservative fallback if unparsable
        recording.debugInfo.sasTokenExpiry = this.parseSasExpiry(uploadInfo.sasUrl) || new Date(Date.now() + 20 * 60 * 60 * 1000);
        recording.sasPending = false;
        recording.pendingUploadInfo = undefined;
        // The issued SAS row is now consumed by this recording - later SAS requests
        // (segment rotations) must mint FRESH blobs, never re-sign this one.
        this.pendingSasIssue.delete(streamId);

        this.debugLog(`🔐 [SAS ADOPTED] Upload info adopted, draining ${recording.uploadQueue.length} buffered chunks - StreamID: ${streamId}, Blob: ${uploadInfo.blobName}`);
        this.diagnosticsService.trackEvent('recording_sas_adopted', `SAS adopted into live recording`, {
            streamId: streamId,
            participantName: recording.participantName,
            bufferedChunks: recording.uploadQueue.length,
            sessionId: this.roomSessionService?.currentSessionId,
            uploadInfo: {
                containerName: uploadInfo.containerName,
                blobName: uploadInfo.blobName
            }
        });

        // Drain everything captured during the SAS round trip
        this.processUploadQueue(streamId);
    }

    /**
     * INSTANT-START failure path: the SAS never arrived, so nothing captured by this recording
     * can ever become durable. Tear the entry down exactly like a failed start (the backend was
     * already notified by getSasTokenWithRetry via SAS_TOKEN_ALL_RETRIES_FAILED) - the periodic
     * health check then retries with a fresh SAS attempt, and if that keeps failing it shows
     * the expert the START_FAILED notice (everRecordedStreams only tracks durable footage).
     */
    private handleSasStartFailure(streamId: string, recording: SubscriberRecording, error: any): void {
        // finalizeRecording may already have consumed the failure (short session teardown)
        if (this.recordings.get(streamId) !== recording) {
            return;
        }

        // A stop/teardown is already in progress (stop and both finalize paths set
        // isRecording=false synchronously before awaiting the pending SAS). Tearing down here
        // too would double-delete the entry and emit recording_failure alongside the stop
        // flow's own outcome. Just mark the pending state resolved-as-failed; the in-flight
        // finalize sees the empty sasUrl and reports the loss (SAVE_FAILED path) itself.
        if (!recording.isRecording) {
            recording.sasPending = false;
            recording.pendingUploadInfo = undefined;
            this.debugLog(`🔐 [SAS START FAILED] SAS rejected during an in-progress stop - deferring to the stop flow - StreamID: ${streamId}`);
            return;
        }

        const sessionId = this.roomSessionService?.currentSessionId;
        console.error(`❌ [SAS START FAILED] SAS fetch failed, discarding buffered-only recording - StreamID: ${streamId}, BufferedChunks: ${recording.uploadQueue.length}`, error);
        this.diagnosticsService.logError(`SAS fetch failed for instant-start recording`, error, {
            streamId: streamId,
            participantName: recording.participantName,
            bufferedChunks: recording.uploadQueue.length,
            bufferedBytes: recording.debugInfo.totalSize,
            sessionId: sessionId,
            component: 'RecordingService',
            method: 'handleSasStartFailure'
        });
        this.diagnosticsService.trackEvent('recording_failure', `Recording failed to start (SAS fetch failed)`, {
            streamId: streamId,
            participantName: recording.participantName,
            sessionId: sessionId,
            error: error?.message || error
        });

        recording.isRecording = false;
        try {
            if (recording.mediaRecorder && recording.mediaRecorder.state !== 'inactive') {
                recording.mediaRecorder.stop();
            }
        } catch (stopError) {
            // Best effort - the entry is being discarded either way
        }
        recording.uploadQueue = [];
        if ((recording as any).chunkTimer) {
            clearInterval((recording as any).chunkTimer);
        }
        if ((recording as any).backupChunkTimer) {
            clearInterval((recording as any).backupChunkTimer);
        }
        const monitorCleanup = this.sessionListeners.get(`stateMonitor_${streamId}`);
        if (monitorCleanup) {
            monitorCleanup();
            this.sessionListeners.delete(`stateMonitor_${streamId}`);
        }
        this.recordings.delete(streamId);
        this.updateRecordingStatus();
    }

    /**
     * Stop recording for a subscriber
     */
    private async stopRecordingForSubscriber(streamId: string): Promise<void> {
        const recording = this.recordings.get(streamId);
        if (!recording) {
            return;
        }

        // Reentrancy guard: stop is triggered from many independent sources (auto-stop,
        // queue-cap, upload-abort, stream-destroyed, session teardown). Without this, two of
        // them can run finalizeRecording concurrently on the same entry - doubling the final
        // PutBlockList and the outcome telemetry (recording_completed / SAVE_FAILED). The entry
        // stays in the map until this call's finally, so a concurrent trigger is redundant.
        if (this.stoppingStreams.has(streamId)) {
            this.debugLog(`🛑 [STOP IN FLIGHT] Stop already in progress - StreamID: ${streamId}`);
            return;
        }
        // Mutual exclusion with rotation/recovery: a rotation's finalizeAndCleanupRecording
        // owns this entry right now (its quiesce can hold the window open for minutes).
        // Running a SECOND terminal finalize here would double-commit the same entry and,
        // worse, this stop's finally could delete the REPLACEMENT entry the rotation's +2s
        // restart creates - orphaning a live MediaRecorder whose chunks silently drop. The
        // rotation finalizes the same footage; terminal intent is preserved because its
        // restart no-ops once the stream/references are gone (stream destroyed, session swept).
        if (this.rotatingStreams.has(streamId)) {
            this.debugLog(`🛑 [STOP SKIP] Rotation/recovery teardown owns this recording - StreamID: ${streamId}`);
            return;
        }
        this.stoppingStreams.add(streamId);

        const recordingDuration = (Date.now() - recording.startTime.getTime()) / 1000;
        const stopTime = new Date();
        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;

        this.debugLog(`🛑 [RECORDING STOP] Stopping recording - StreamID: ${streamId}, Duration: ${Math.round(recordingDuration)}s`);

        try {
            // Clean up chunk timers if they exist
            if ((recording as any).chunkTimer) {
                clearInterval((recording as any).chunkTimer);
                this.debugLog(`⏰ [TIMER CLEANUP] Chunk timer cleared - StreamID: ${streamId}`);
            }

            if ((recording as any).backupChunkTimer) {
                clearInterval((recording as any).backupChunkTimer);
                this.debugLog(`⏰ [BACKUP TIMER CLEANUP] Backup chunk timer cleared - StreamID: ${streamId}`);
            }

            // Stop MediaRecorder
            if (recording.mediaRecorder && recording.isRecording) {
                recording.mediaRecorder.stop();
                recording.isRecording = false;

                // Wait for final chunks to be processed
                // MediaRecorder fires a final dataavailable event after stop()
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds for final chunks
            }

            // Wait for final chunks and upload. The finalize outcome is AUTHORITATIVE:
            // a failed final commit means the tail since the last checkpoint is not durable,
            // and must never be reported as a successful recording.
            const finalizeOk = await this.finalizeRecording(streamId);

            if (finalizeOk) {
                // Log successful recording completion to local diagnostics
                this.diagnosticsService.logMessage(`Recording stopped and finalized successfully`, 'info', {
                    streamId: streamId,
                    participantName: recording.participantName,
                    duration: Math.round(recordingDuration),
                    totalChunks: recording.debugInfo.totalChunks,
                    totalSize: recording.debugInfo.totalSize,
                    uploadErrors: recording.debugInfo.uploadErrors || 0,
                    finalQueueLength: recording.uploadQueue.length,
                    sessionId: sessionId,
                    roomId: roomId,
                    component: 'RecordingService',
                    method: 'stopRecordingForSubscriber'
                });

                // Track successful recording completion
                this.diagnosticsService.trackEvent('recording_completed', `Recording completed successfully`, {
                    streamId: streamId,
                    participantName: recording.participantName,
                    duration: Math.round(recordingDuration),
                    totalChunks: recording.debugInfo.totalChunks,
                    totalSize: recording.debugInfo.totalSize,
                    uploadErrors: recording.debugInfo.uploadErrors || 0,
                    sessionId: sessionId,
                    roomId: roomId
                });
            } else {
                this.diagnosticsService.trackEvent('recording_finalize_failed', `Recording stop finished but final commit FAILED`, {
                    streamId: streamId,
                    participantName: recording.participantName,
                    duration: Math.round(recordingDuration),
                    totalChunks: recording.debugInfo.totalChunks,
                    stagedBlocks: recording.blockIds.length,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    sessionId: sessionId,
                    roomId: roomId
                });
                this.notifyRecordingFailure(sessionId || 'unknown', 'FINAL_COMMIT_FAILED', {
                    streamId: streamId,
                    stagedBlocks: recording.blockIds.length,
                    committedBlocks: recording.lastCommittedBlockCount || 0
                });
                this.notifyUserRecordingIssue('SAVE_FAILED');
            }

        } catch (error) {
            console.error(`❌ [RECORDING STOP ERROR] Error during recording stop - StreamID: ${streamId}`, error);

            // Log recording stop error to local diagnostics
            this.diagnosticsService.logError(`Error during recording stop and finalization`, error, {
                streamId: streamId,
                participantName: recording.participantName,
                duration: Math.round(recordingDuration),
                totalChunks: recording.debugInfo.totalChunks,
                totalSize: recording.debugInfo.totalSize,
                uploadErrors: recording.debugInfo.uploadErrors || 0,
                queueLength: recording.uploadQueue.length,
                errorMessage: error?.message || error,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'stopRecordingForSubscriber'
            });

            // Track recording stop failure
            this.diagnosticsService.trackEvent('recording_stop_failure', `Recording stop failed`, {
                streamId: streamId,
                participantName: recording.participantName,
                duration: Math.round(recordingDuration),
                error: error?.message || error,
                sessionId: sessionId,
                roomId: roomId
            });

        } finally {

            // Cleanup state monitor
            const stateMonitorCleanup = this.sessionListeners.get(`stateMonitor_${streamId}`);
            if (stateMonitorCleanup) {
                stateMonitorCleanup();
                this.sessionListeners.delete(`stateMonitor_${streamId}`);
            }

            // Cleanup - identity-guarded: never delete a REPLACEMENT entry that a recovery
            // restart may have created under the same streamId while this stop was draining
            // (deleting it would orphan a live MediaRecorder whose chunks silently drop).
            if (this.recordings.get(streamId) === recording) {
                this.recordings.delete(streamId);
            }
            this.stoppingStreams.delete(streamId);
            this.updateRecordingStatus();
        }
    }

    /**
     * Get MediaStream from subscriber with enhanced retry and multiple acquisition strategies
     */
    private async getSubscriberMediaStream(streamId: string): Promise<MediaStream | null> {
        const sessionId = this.roomSessionService?.currentSessionId;

        this.debugLog(`📹 [MEDIASTREAM GET] Starting enhanced MediaStream acquisition - StreamID: ${streamId}, SessionID: ${sessionId}`);

        const maxRetries = 5;
        const baseDelay = 200; // Start with 200ms
        // Strategy 1 (getStreamFromOpentokAPI) now reads the subscriber's OWN <video>.srcObject
        // via the registry - identity-safe and preferred. Strategies 2/3 (DOM query / events)
        // read the SAME srcObject via an identity-stamped selector; at start they are the same
        // provisional stream OpenTok later swaps, so there is no benefit to delaying them (the
        // earlier "registry grace" only added latency to the start the user cares about). Try
        // all strategies every attempt and start as EARLY as possible - the ~6s renegotiation
        // swap is followed at runtime by the proactive swap check in the chunk timer.
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                this.debugLog(`🔄 [MEDIASTREAM ATTEMPT] Attempt ${attempt}/${maxRetries} - StreamID: ${streamId}`);

                // Strategy 1: subscriber-element srcObject via the registry (identity-safe)
                const apiStream = this.getStreamFromOpentokAPI(streamId);
                if (apiStream) {
                    this.debugLog(`✅ [MEDIASTREAM SUCCESS] Got stream via OpenTok API - StreamID: ${streamId}, Tracks: ${apiStream.getVideoTracks().length}v/${apiStream.getAudioTracks().length}a`);
                    return this.createVideoOnlyStream(apiStream, streamId);
                }

                // Strategy 2: identity-stamped DOM query for the <video>.srcObject
                const domStream = await this.getStreamFromDOM(streamId);
                if (domStream) {
                    this.debugLog(`✅ [MEDIASTREAM SUCCESS] Got stream via DOM - StreamID: ${streamId}, Tracks: ${domStream.getVideoTracks().length}v/${domStream.getAudioTracks().length}a`);
                    return this.createVideoOnlyStream(domStream, streamId);
                }

                // Strategy 3: Event-based acquisition (wait for stream events)
                const eventStream = await this.getStreamFromEvents(streamId, 1000); // 1 second timeout
                if (eventStream) {
                    this.debugLog(`✅ [MEDIASTREAM SUCCESS] Got stream via events - StreamID: ${streamId}, Tracks: ${eventStream.getVideoTracks().length}v/${eventStream.getAudioTracks().length}a`);
                    return this.createVideoOnlyStream(eventStream, streamId);
                }

                if (attempt < maxRetries) {
                    const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
                    this.debugLog(`⏳ [MEDIASTREAM RETRY] Waiting ${delay}ms before retry ${attempt + 1} - StreamID: ${streamId}`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }

            } catch (error) {
                this.debugWarn(`⚠️ [MEDIASTREAM ERROR] Attempt ${attempt} failed - StreamID: ${streamId}:`, error);
                if (attempt === maxRetries) {
                    this.diagnosticsService.logError(`MediaStream acquisition failed after ${maxRetries} attempts`, error, {
                        streamId: streamId,
                        sessionId: sessionId,
                        lastAttempt: attempt
                    });
                }
            }
        }

        console.error(`❌ [MEDIASTREAM FAILED] All acquisition strategies failed - StreamID: ${streamId}, SessionID: ${sessionId}`);
        return null;
    }

    /**
     * Strategy 1: Get MediaStream from OpenTok API
     */
    private getStreamFromOpentokAPI(streamId: string): MediaStream | null {
        try {
            this.debugLog(`🔍 [OPENTOK API] Trying OpenTok API access - StreamID: ${streamId}`);

            // Authoritative source: the subscriber's OWN rendered <video>.srcObject, read via
            // the registry (identity-safe, never the wrong participant). This REPLACES the
            // long-dead getMediaStream() feature-guards below - the OpenTok SDK has no such
            // API, so those branches never executed and acquisition silently always fell
            // through to the DOM query. Reading srcObject live also lets a recovery restart
            // pick up the CURRENT (post-swap) stream.
            if (this.opentokService && this.opentokService.getSubscriberMediaStreamByStreamId) {
                const registryStream = this.opentokService.getSubscriberMediaStreamByStreamId(streamId);
                if (registryStream && registryStream.getVideoTracks().length > 0) {
                    this.debugLog(`✅ [SUBSCRIBER REGISTRY] Got stream via subscriber element srcObject - StreamID: ${streamId}`);
                    return registryStream;
                }
            }

            return null;
        } catch (error) {
            this.debugLog(`⚠️ [OPENTOK ERROR] OpenTok API access failed - StreamID: ${streamId}:`, error);
            return null;
        }
    }

    /**
     * Strategy 2: Get MediaStream from DOM with advanced selectors
     */
    private async getStreamFromDOM(streamId: string): Promise<MediaStream | null> {
        try {
            // Identity-bearing selectors ONLY. The old broad fallbacks ('.OT_subscriber video',
            // 'video.OT_video-element') matched ANY subscriber's video and could silently record
            // the wrong participant when several subscribers (or a screen share) were present.
            // SubscriberComponent stamps data-streamid on the subscriber element at subscribe time.
            const selectors = [
                `video[data-stream-id="${streamId}"]`,
                `video[id*="${streamId}"]`,
                `[data-streamid="${streamId}"] video`,
                `.OT_subscriber[data-stream="${streamId}"] video`,
                `.subscriber-${streamId} video`
            ];

            for (const selector of selectors) {
                const videoElements = document.querySelectorAll(selector);
                for (let i = 0; i < videoElements.length; i++) {
                    const video = videoElements[i] as HTMLVideoElement;
                    if (video && video.srcObject) {
                        const mediaStream = video.srcObject as MediaStream;
                        if (mediaStream && mediaStream.getVideoTracks().length > 0) {
                            // Validate stream is active and not ended
                            const videoTracks = mediaStream.getVideoTracks();
                            const activeTrack = videoTracks.find(track =>
                                track.readyState === 'live' && track.enabled
                            );
                            if (activeTrack) {
                                this.debugLog(`📺 [DOM STREAM] Found via selector: ${selector} - StreamID: ${streamId}`);
                                return mediaStream;
                            }
                        }
                    }
                }
            }

            // Advanced DOM traversal - look for subscriber containers
            return await this.findStreamInSubscriberContainers(streamId);

        } catch (error) {
            this.debugWarn(`⚠️ [DOM STREAM] DOM acquisition failed - StreamID: ${streamId}:`, error);
            return null;
        }
    }

    /**
     * Strategy 3: Get MediaStream by waiting for events
     */
    private async getStreamFromEvents(streamId: string, timeoutMs: number): Promise<MediaStream | null> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                cleanup();
                resolve(null);
            }, timeoutMs);

            const cleanup = () => {
                clearTimeout(timeout);
                // Remove any event listeners if added
            };

            // Check if stream reference updates
            const checkStream = () => {
                const stream = this.streamReferences.get(streamId);
                if (stream && stream.getMediaStream) {
                    const mediaStream = stream.getMediaStream();
                    if (mediaStream && mediaStream.getVideoTracks().length > 0) {
                        cleanup();
                        resolve(mediaStream);
                        return;
                    }
                }

                // Check DOM again (sometimes elements appear after delay)
                this.getStreamFromDOM(streamId).then(domStream => {
                    if (domStream) {
                        cleanup();
                        resolve(domStream);
                        return;
                    }
                });
            };

            // Check immediately and then every 100ms
            checkStream();
            const interval = setInterval(checkStream, 100);

            setTimeout(() => {
                clearInterval(interval);
            }, timeoutMs);
        });
    }

    /**
     * Advanced DOM traversal for subscriber containers
     */
    private async findStreamInSubscriberContainers(streamId: string): Promise<MediaStream | null> {
        try {
            // Find all potential subscriber containers
            const containerSelectors = [
                '.OT_subscriber',
                '.opentok-subscriber',
                '.subscriber-container',
                '[class*="subscriber"]',
                '[id*="subscriber"]'
            ];

            for (const containerSelector of containerSelectors) {
                const containers = document.querySelectorAll(containerSelector);

                for (let i = 0; i < containers.length; i++) {
                    const container = containers[i];

                    // Look for video element in this container
                    const video = container.querySelector('video') as HTMLVideoElement;
                    if (video && video.srcObject) {
                        const mediaStream = video.srcObject as MediaStream;
                        if (mediaStream && mediaStream.getVideoTracks().length > 0) {
                            // IDENTITY IS REQUIRED: the container must reference this exact
                            // streamId. Characteristic checks (live/enabled) and the old
                            // "only one active stream" fallback cannot tell WHOSE stream this
                            // is - with multiple subscribers (or a screen share) they could
                            // silently record the wrong participant, which is a privacy defect
                            // strictly worse than a delayed recording start.
                            const containerHtml = container.outerHTML;
                            if (containerHtml.includes(streamId)) {
                                this.debugLog(`📺 [CONTAINER STREAM] Found in container: ${containerSelector} - StreamID: ${streamId}`);
                                return mediaStream;
                            }
                        }
                    }
                }
            }

            return null;
        } catch (error) {
            this.debugWarn(`⚠️ [CONTAINER SEARCH] Container search failed - StreamID: ${streamId}:`, error);
            return null;
        }
    }

    /**
     * Find subscriber object by stream ID with enhanced search strategies
     */
    private findSubscriberByStreamId(streamId: string): any {
        try {
            this.debugLog(`🔍 [SUBSCRIBER SEARCH] Searching for subscriber - StreamID: ${streamId}`);

            // Strategy 1: Direct OpenTok session access
            if (this.opentokService && this.opentokService.currentSession) {
                const session = this.opentokService.currentSession;
                this.debugLog(`🔍 [SESSION ACCESS] Found session, checking subscribers`);

                // Try to access subscribers through session
                if (session.subscribers) {
                    this.debugLog(`🔍 [SUBSCRIBERS TYPE] Type:`, typeof session.subscribers, `IsArray:`, Array.isArray(session.subscribers));

                    // Try different subscriber access patterns
                    const subscriberMaps = [session.subscribers, session._subscribers, session.subscriberMap];

                    for (let i = 0; i < subscriberMaps.length; i++) {
                        const subscriberMap = subscriberMaps[i];
                        if (subscriberMap) {
                            this.debugLog(`🔍 [MAP ${i}] Checking subscriber map type:`, typeof subscriberMap);

                            // Handle Map-like structures
                            if (typeof subscriberMap.forEach === 'function') {
                                this.debugLog(`🔍 [MAP FOREACH] Using forEach method`);
                                let foundSubscriber = null;
                                try {
                                    subscriberMap.forEach((subscriber: any) => {
                                        if (subscriber && subscriber.stream && subscriber.stream.streamId === streamId) {
                                            foundSubscriber = subscriber;
                                        }
                                    });
                                    if (foundSubscriber) {
                                        this.debugLog(`✅ [SUBSCRIBER FOUND] Found via forEach - StreamID: ${streamId}`);
                                        return foundSubscriber;
                                    }
                                } catch (forEachError) {
                                    this.debugLog(`⚠️ [FOREACH ERROR] forEach failed:`, forEachError);
                                }
                            }

                            // Handle Object-like structures with safe iteration
                            if (typeof subscriberMap === 'object' && subscriberMap !== null) {
                                this.debugLog(`🔍 [OBJECT ITERATION] Using Object.keys`);
                                try {
                                    const keys = Object.keys(subscriberMap);
                                    for (let j = 0; j < keys.length; j++) {
                                        const key = keys[j];
                                        const subscriber = subscriberMap[key];
                                        if (subscriber && subscriber.stream && subscriber.stream.streamId === streamId) {
                                            this.debugLog(`✅ [SUBSCRIBER FOUND] Found via object key - StreamID: ${streamId}, Key: ${key}`);
                                            return subscriber;
                                        }
                                    }
                                } catch (keyError) {
                                    this.debugLog(`⚠️ [KEY ERROR] Object.keys failed:`, keyError);
                                }
                            }

                            // Handle Array-like structures
                            if (Array.isArray(subscriberMap)) {
                                this.debugLog(`🔍 [ARRAY ITERATION] Checking array of ${subscriberMap.length} items`);
                                for (let j = 0; j < subscriberMap.length; j++) {
                                    const subscriber = subscriberMap[j];
                                    if (subscriber && subscriber.stream && subscriber.stream.streamId === streamId) {
                                        this.debugLog(`✅ [SUBSCRIBER FOUND] Found via array - StreamID: ${streamId}, Index: ${j}`);
                                        return subscriber;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Strategy 2: Global OpenTok object access with safe iteration
            if ((window as any).OT) {
                this.debugLog(`🔍 [GLOBAL OT] Checking global OpenTok object`);
                const OT = (window as any).OT;

                try {
                    const sessions = OT.sessions || [];
                    this.debugLog(`🔍 [GLOBAL SESSIONS] Found ${Array.isArray(sessions) ? sessions.length : 'non-array'} sessions`);

                    if (Array.isArray(sessions)) {
                        for (let i = 0; i < sessions.length; i++) {
                            const session = sessions[i];
                            if (session && session.subscribers) {
                                const keys = Object.keys(session.subscribers);
                                for (let j = 0; j < keys.length; j++) {
                                    const subscriberId = keys[j];
                                    const subscriber = session.subscribers[subscriberId];
                                    if (subscriber && subscriber.stream && subscriber.stream.streamId === streamId) {
                                        this.debugLog(`✅ [SUBSCRIBER FOUND] Found via global OT - StreamID: ${streamId}`);
                                        return subscriber;
                                    }
                                }
                            }
                        }
                    }
                } catch (globalError) {
                    this.debugLog(`⚠️ [GLOBAL ERROR] Global OT access failed:`, globalError);
                }
            }

            this.debugLog(`❌ [SUBSCRIBER SEARCH] No subscriber found for StreamID: ${streamId}`);
            return null;

        } catch (error) {
            this.debugWarn(`⚠️ [SUBSCRIBER SEARCH] Critical error finding subscriber - StreamID: ${streamId}:`, error);
            return null;
        }
    }

    /**
     * Enhanced stream readiness validation with comprehensive checks
     */
    /**
     * Acquire the subscriber's MediaStream ONCE and validate its readiness in the same pass.
     * (Replaces the old validateStreamReadiness, which ran the full multi-retry acquisition
     * ladder just to return a boolean and threw the stream away - the caller then acquired
     * it a second time, doubling recording-start latency.)
     * Returns null when the stream is missing, video-less, or has no live+enabled track.
     */
    private async acquireValidatedMediaStream(streamId: string): Promise<MediaStream | null> {
        try {
            // Check 1: Stream reference exists and has video
            const stream = this.streamReferences.get(streamId);
            if (!stream || !stream.hasVideo) {
                this.debugLog(`⚠️ [STREAM READINESS] Stream reference invalid - StreamID: ${streamId}, HasVideo: ${stream?.hasVideo}`);
                return null;
            }

            // Check 2: Acquire MediaStream (single multi-strategy acquisition)
            const mediaStream = await this.getSubscriberMediaStream(streamId);
            if (!mediaStream) {
                this.debugLog(`⚠️ [STREAM READINESS] MediaStream not available - StreamID: ${streamId}`);
                return null;
            }

            // Check 3: Validate video tracks
            const videoTracks = mediaStream.getVideoTracks();
            if (videoTracks.length === 0) {
                this.debugLog(`⚠️ [STREAM READINESS] No video tracks - StreamID: ${streamId}`);
                return null;
            }

            // Check 4: Ensure tracks are live and enabled
            const liveTrack = videoTracks.find(track => track.readyState === 'live' && track.enabled);
            if (!liveTrack) {
                this.debugLog(`⚠️ [STREAM READINESS] No live video tracks - StreamID: ${streamId}, Tracks:`,
                    videoTracks.map(t => ({ readyState: t.readyState, enabled: t.enabled })));
                return null;
            }

            this.debugLog(`✅ [STREAM READINESS] Stream ready - StreamID: ${streamId}, VideoTracks: ${videoTracks.length}, Live: ${!!liveTrack}`);
            return mediaStream;

        } catch (error) {
            this.debugWarn(`⚠️ [STREAM READINESS] Acquisition/validation failed - StreamID: ${streamId}:`, error);
            return null;
        }
    }

    /**
     * Create video-only stream by removing audio tracks
     */
    private createVideoOnlyStream(originalStream: MediaStream, streamId?: string): MediaStream {
        const videoOnlyStream = new MediaStream();

        // Add only video tracks
        originalStream.getVideoTracks().forEach(track => {
            videoOnlyStream.addTrack(track);

            // React to the captured track ending: OpenTok replaces the MediaStream on camera
            // changes / screen-share swaps / reconnects, which ends this track while a NEW live
            // track exists. Recover promptly instead of waiting for the health-check tick.
            track.addEventListener('ended', () => {
                if (!streamId) {
                    return;
                }
                setTimeout(() => { // Debounce: let OpenTok settle the replacement stream
                    const rec = this.recordings.get(streamId);
                    if (!rec || !rec.isRecording || rec.isPaused) {
                        return;
                    }
                    if (this.localVideoSubscriptionState.get(streamId) === false) {
                        return; // Intentional local unsubscribe - pause path owns this
                    }
                    if (this.rotatingStreams.has(streamId)) {
                        return; // A rotation/recovery is already in flight
                    }
                    const verdict = this.verifyStreamHealthAuthoritative(streamId);
                    if (verdict === 'swapped' || verdict === 'dead') {
                        this.handleStreamSwapRecovery(streamId, 'track_ended');
                    }
                }, 1000);
            });

            track.addEventListener('mute', () => {
                // Track muted
            });

            track.addEventListener('unmute', () => {
                // Track unmuted
            });
        });

        // Ensure no audio tracks
        originalStream.getAudioTracks().forEach(track => {
            // Audio tracks are not added
        });

        return videoOnlyStream;
    }

    /**
     * Create MediaRecorder for video-only recording
     */
    private createMediaRecorder(stream: MediaStream, streamId: string): any {
        const options = this.getDefaultRecordingOptions();

        let mediaRecorder;
        try {
            mediaRecorder = new MediaRecorder(stream, {
                mimeType: options.mimeType,
                videoBitsPerSecond: options.videoBitsPerSecond,
                audioBitsPerSecond: 0 // Explicitly set to 0 for no audio
            });
        } catch (error) {
            // Fall back to the browser default; log so codec issues are visible in production
            this.diagnosticsService.logError(`MediaRecorder creation failed with preferred options, falling back to browser default`, error, {
                streamId: streamId,
                preferredMimeType: options.mimeType,
                component: 'RecordingService',
                method: 'createMediaRecorder'
            });
            mediaRecorder = new MediaRecorder(stream);
        }

        // Setup event handlers
        mediaRecorder.ondataavailable = (event: any) => {
            const recording = this.recordings.get(streamId);
            const participantName = recording ? recording.participantName : streamId;
            const elapsedTime = recording ? (Date.now() - recording.startTime.getTime()) / 1000 : 0;
            const sessionId = this.roomSessionService?.currentSessionId;

            this.debugLog(`📊 [CHUNK EVENT] DataAvailable fired - Size: ${event.data?.size || 0}B, Elapsed: ${elapsedTime.toFixed(1)}s, StreamID: ${streamId}, SessionID: ${sessionId}`);

            // Always pass the chunk to handleRecordingChunk - it will handle empty chunks properly
            if (event.data) {
                this.handleRecordingChunk(streamId, event.data);
            }
        };

        mediaRecorder.onstop = () => {
            const recording = this.recordings.get(streamId);
            const sessionId = this.roomSessionService?.currentSessionId;

            this.debugLog(`⏹️ [RECORDER STOP] MediaRecorder stopped - StreamID: ${streamId}, SessionID: ${sessionId}`);

            if (recording) {
                // Mark that MediaRecorder has finished stopping
                recording.debugInfo.lastChunkTime = new Date();

                // Log final statistics
                const duration = (Date.now() - recording.startTime.getTime()) / 1000;
                this.debugLog(`📊 [FINAL STATS] Duration: ${duration.toFixed(1)}s, Total chunks: ${recording.debugInfo.totalChunks}, Total size: ${recording.debugInfo.totalSize}B - StreamID: ${streamId}`);
                this.diagnosticsService.logMessage(`MediaRecorder stopped - Final statistics`, 'info', {
                    streamId: streamId,
                    duration: duration,
                    totalChunks: recording.debugInfo.totalChunks,
                    totalSize: recording.debugInfo.totalSize,
                    sessionId: sessionId
                });
            }
        };

        mediaRecorder.onstart = () => {
            const sessionId = this.roomSessionService?.currentSessionId;
            this.debugLog(`▶️ [RECORDER START] MediaRecorder started - StreamID: ${streamId}, SessionID: ${sessionId}`);

            // Log stream details for debugging
            const videoTracks = stream.getVideoTracks();
            this.debugLog(`📹 [STREAM INFO] Video tracks count: ${videoTracks.length} - StreamID: ${streamId}`);
            videoTracks.forEach((track, index) => {
                const settings = track.getSettings();
                this.debugLog(`📹 [TRACK ${index}] Settings:`, {
                    width: settings.width,
                    height: settings.height,
                    frameRate: settings.frameRate,
                    readyState: track.readyState,
                    enabled: track.enabled,
                    kind: track.kind
                });
            });
        };

        mediaRecorder.onpause = () => {
            // MediaRecorder paused
        };

        mediaRecorder.onresume = () => {
            // MediaRecorder resumed
        };

        mediaRecorder.onerror = (event: any) => {
            const recording = this.recordings.get(streamId);
            const sessionId = this.roomSessionService?.currentSessionId;

            console.error(`❌ [RECORDER ERROR] MediaRecorder error - StreamID: ${streamId}, SessionID: ${sessionId}`, event);

            // Enhanced error handling with recovery
            this.handleMediaRecorderError(streamId, event, recording);

            this.diagnosticsService.logError(`MediaRecorder error`, event.error || event, {
                streamId: streamId,
                sessionId: sessionId,
                participantName: recording?.participantName,
                recoveryAttempted: true
            });
        };

        return mediaRecorder;
    }

    /**
     * Get default recording options
     */
    private getDefaultRecordingOptions(): VideoRecordingOptions {
        // Prioritize WebM codecs to avoid AVC1 codec description change issues
        const supportedTypes = [
            'video/webm; codecs="vp9"',
            'video/webm; codecs="vp8"',
            'video/webm',
            'video/mp4; codecs="avc1.42E01E"'
        ];

        let mimeType = 'video/webm'; // fallback
        for (const type of supportedTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
                mimeType = type;
                this.debugLog(`✅ [CODEC SELECTED] Using codec: ${type}`);
                break;
            }
        }

        return {
            mimeType: mimeType,
            videoBitsPerSecond: 2000000, // Increased to 2 Mbps for better quality
            audioBitsPerSecond: 128000   // Add audio bitrate for complete stream
        };
    }

    /**
     * Handle recording chunk
     */
    private handleRecordingChunk(streamId: string, chunk: Blob): void {
        const recording = this.recordings.get(streamId);
        if (!recording) {
            return;
        }

        // CRITICAL: Empty chunk detection for MediaRecorder issues
        if (chunk.size === 0) {
            // Only increment empty chunks counter if recording is not paused
            // Paused recordings naturally produce empty chunks and shouldn't trigger stream end detection
            if (!recording.isPaused) {
                recording.debugInfo.consecutiveEmptyChunks = (recording.debugInfo.consecutiveEmptyChunks || 0) + 1;

                const emptyCount = recording.debugInfo.consecutiveEmptyChunks;
                console.warn(`⚠️ [EMPTY CHUNK] Received empty chunk #${emptyCount} - StreamID: ${streamId}`);

                // Check if we've exceeded the threshold
                if (emptyCount >= 10) {
                    const mediaRecorder = recording.mediaRecorder;
                    const mediaRecorderState = mediaRecorder?.state || 'unknown';
                    const mimeType = mediaRecorder?.mimeType || 'unknown';

                    // CRITICAL: Before stopping, verify if video is actually OFF or just a MediaRecorder issue
                    const stream = this.streamReferences.get(streamId);
                    const hasVideoNow = stream?.hasVideo || false;
                    const videoTracks = recording.originalMediaStream?.getVideoTracks() || [];
                    const hasLiveVideoTrack = videoTracks.some(track => track.readyState === 'live' && track.enabled);

                    console.error(`🚨 [EXCESSIVE EMPTY CHUNKS] ${emptyCount} consecutive empty chunks detected - StreamID: ${streamId}`);
                    console.error(`📹 [MEDIARECORDER STATE] State: ${mediaRecorderState}, MimeType: ${mimeType} - StreamID: ${streamId}`);
                    console.error(`📹 [VIDEO STATUS] hasVideo: ${hasVideoNow}, liveVideoTrack: ${hasLiveVideoTrack} - StreamID: ${streamId}`);

                    // If video is actually enabled but MediaRecorder is broken, try recovery
                    if (hasVideoNow && hasLiveVideoTrack) {
                        // Owner locally unsubscribed: empty chunks are expected, pause instead of recovery
                        if (this.localVideoSubscriptionState.get(streamId) === false) {
                            this.debugLog(`⏸️ [LOCAL UNSUB] Empty chunks while locally unsubscribed - pausing instead of recovery - StreamID: ${streamId}`);
                            recording.debugInfo.consecutiveEmptyChunks = 0;
                            this.pauseRecording(streamId);
                            return;
                        }

                        console.warn(`⚠️ [RECOVERY ATTEMPT] Video is ON but empty chunks - trying MediaRecorder recovery - StreamID: ${streamId}`);

                        this.diagnosticsService.logMessage(`Attempting MediaRecorder recovery - video is ON`, 'warning', {
                            streamId: streamId,
                            consecutiveEmptyChunks: emptyCount,
                            mediaRecorderState: mediaRecorderState,
                            mimeType: mimeType,
                            hasVideo: hasVideoNow,
                            hasLiveVideoTrack: hasLiveVideoTrack,
                            sessionId: this.roomSessionService?.currentSessionId,
                            participantName: recording.participantName,
                            component: 'RecordingService',
                            method: 'handleRecordingChunk'
                        });

                        // Try to recover by restarting the recording
                        recording.debugInfo.mediaRecorderRecoveryAttempts = (recording.debugInfo.mediaRecorderRecoveryAttempts || 0) + 1;

                        // Rate-limit automatic restarts: max 2 within a 5-minute sliding window per stream.
                        // Prevents infinite finalize/restart loops that fragment the recording.
                        const restartWindow = (this.restartHistory.get(streamId) || []).filter(t => Date.now() - t < 300000);
                        const restartAllowed = restartWindow.length < 2;

                        // Only try recovery once per recording instance, then give up
                        if (recording.debugInfo.mediaRecorderRecoveryAttempts <= 1 && restartAllowed) {
                            restartWindow.push(Date.now());
                            this.restartHistory.set(streamId, restartWindow);
                            console.log(`🔄 [RECOVERY] Attempting to restart recording - StreamID: ${streamId}`);

                            // Finalize current broken recording (fire and forget)
                            this.finalizeAndCleanupRecording(streamId).catch(err => {
                                console.error(`❌ [RECOVERY] Failed to finalize broken recording:`, err);
                            });

                            // Restart recording after a delay
                            setTimeout(async () => {
                                console.log(`🔄 [RECOVERY] Restarting recording after MediaRecorder failure - StreamID: ${streamId}`);
                                await this.startNewRecordingWithVideoValidation(streamId);
                            }, 2000);

                            return;
                        } else {
                            console.error(`❌ [RECOVERY FAILED] Recovery attempts exhausted or rate-limited - StreamID: ${streamId}`);
                            this.diagnosticsService.trackEvent('recording_recovery_rate_limited', `MediaRecorder recovery rate-limited - stopping recording`, {
                                streamId: streamId,
                                participantName: recording.participantName,
                                recoveryAttempts: recording.debugInfo.mediaRecorderRecoveryAttempts,
                                recentRestarts: restartWindow.length,
                                sessionId: this.roomSessionService?.currentSessionId
                            });
                        }
                    }

                    this.diagnosticsService.logError(`Excessive empty chunks detected - MediaRecorder issue`, null, {
                        streamId: streamId,
                        consecutiveEmptyChunks: emptyCount,
                        mediaRecorderState: mediaRecorderState,
                        mimeType: mimeType,
                        hasVideo: hasVideoNow,
                        hasLiveVideoTrack: hasLiveVideoTrack,
                        sessionId: this.roomSessionService?.currentSessionId,
                        participantName: recording.participantName,
                        component: 'RecordingService',
                        method: 'handleRecordingChunk'
                    });

                    // Notify backend of failure
                    this.notifyRecordingFailure(
                        this.roomSessionService?.currentSessionId || 'unknown',
                        'EXCESSIVE_EMPTY_CHUNKS',
                        {
                            streamId: streamId,
                            consecutiveEmptyChunks: emptyCount,
                            mediaRecorderState: mediaRecorderState,
                            mimeType: mimeType,
                            hasVideo: hasVideoNow,
                            hasLiveVideoTrack: hasLiveVideoTrack,
                            participantName: recording.participantName,
                            recordingDuration: (Date.now() - recording.startTime.getTime()) / 1000,
                            recoveryAttempts: recording.debugInfo.mediaRecorderRecoveryAttempts || 0
                        }
                    );

                    // Stop recording to prevent resource waste
                    console.error(`🛑 [AUTO STOP] Stopping recording due to excessive empty chunks - StreamID: ${streamId}`);
                    this.notifyUserRecordingIssue('STOPPED');
                    this.stopRecordingForSubscriber(streamId);
                    return;
                }
            } else {
                this.debugLog(`📊 [PAUSED CHUNK] Empty chunk during pause (expected) - StreamID: ${streamId}`);
            }
            return; // Don't process empty chunks further
        }

        const sessionId = this.roomSessionService?.currentSessionId;

        // Reset consecutive empty chunks counter on successful chunk
        recording.debugInfo.consecutiveEmptyChunks = 0;

        // Update debug info
        recording.debugInfo.totalChunks++;
        recording.debugInfo.totalSize += chunk.size;
        recording.debugInfo.lastChunkTime = new Date();

        // NOTE: everRecordedStreams (the START_FAILED-notice suppressor) is deliberately NOT
        // set here. With instant-start, in-memory chunks exist even when the SAS never arrives
        // and nothing durable is ever saved - the set is fed by uploadChunkToAzure on the first
        // successfully uploaded block instead (durable truth).

        // NOTE: the restart-budget reset (~60s healthy run) also moved to uploadChunkToAzure
        // (60 DURABLE blocks). Under instant-start a buffered-only recording can exceed 60
        // in-memory chunks while the SAS ladder runs (~96s worst case) - resetting on those
        // would defeat the 3-strikes START_FAILED terminal path and retry a SAS-dead stream
        // forever without ever telling the expert.

        // Memory management: limit buffer size to prevent memory leaks
        const maxBufferChunks = 10; // Keep only last 10 chunks in buffer
        recording.bufferChunks.push(chunk);
        if (recording.bufferChunks.length > maxBufferChunks) {
            recording.bufferChunks.shift(); // Remove oldest chunk
        }

        // Add to upload queue
        recording.uploadQueue.push(chunk);

        // Hard cap: if uploads cannot drain (network outage), the queued chunks are undeliverable.
        // First occurrence: drop the queue and ROTATE to a fresh segment so capture continues and
        // the committed prefix stays intact. A second undeliverable-footage drop within the
        // outage window (shared with the circuit-breaker deep-queue path) means a sustained
        // outage - stop permanently (and tell the expert) instead of rotate-thrashing.
        if (recording.uploadQueue.length >= this.maxUploadQueueLength && !recording.emergencyStopTriggered) {
            recording.emergencyStopTriggered = true;
            const capWindow = (this.queueCapHistory.get(streamId) || []).filter(t => Date.now() - t < this.outageWindowMs);
            capWindow.push(Date.now());
            this.queueCapHistory.set(streamId, capWindow);

            console.error(`🛑 [QUEUE CAP] Upload queue cap reached (${recording.uploadQueue.length}), cap #${capWindow.length} in window - StreamID: ${streamId}`);
            this.diagnosticsService.logError(`Upload queue hard cap reached`, null, {
                streamId: streamId,
                participantName: recording.participantName,
                queueLength: recording.uploadQueue.length,
                committedBlocks: recording.lastCommittedBlockCount || 0,
                stagedBlocks: recording.blockIds.length,
                capEventsInWindow: capWindow.length,
                sessionId: sessionId,
                component: 'RecordingService',
                method: 'handleRecordingChunk'
            });

            if (capWindow.length >= 2) {
                // Sustained outage - permanent stop for this stream (committed data is safe)
                this.notifyUserRecordingIssue('STOPPED');
                this.stopRecordingForSubscriber(streamId);
            } else {
                recording.uploadQueue = [];
                // The cap is one-shot per segment (emergencyStopTriggered) - bypass the rotation
                // rate limit so this recovery is never silently refused, leaving a capped segment
                // accumulating chunks with no active cap guard.
                this.lastRotationTime.delete(streamId);
                this.rotateRecordingSegment(streamId, 'upload_queue_cap');
            }
            return;
        }

        const status = recording.isRecording ? 'active' : 'final';

        // WebM format validation - check for WebM header in first chunk (simplified)
        if (recording.debugInfo.totalChunks === 1 && chunk.size > 4) {
            // Use FileReader for synchronous header check
            const reader = new FileReader();
            reader.onload = () => {
                const buffer = reader.result as ArrayBuffer;
                const chunkArray = new Uint8Array(buffer.slice(0, 4));
                const isWebM = chunkArray[0] === 0x1A && chunkArray[1] === 0x45 &&
                    chunkArray[2] === 0xDF && chunkArray[3] === 0xA3;
                this.debugLog(`🎬 [WEBM HEADER] First chunk WebM validation: ${isWebM ? 'VALID' : 'INVALID'} - StreamID: ${streamId}`);

                if (!isWebM) {
                    this.debugWarn(`⚠️ [WEBM WARNING] First chunk missing WebM header - may cause playback issues - StreamID: ${streamId}`);
                    this.diagnosticsService.logMessage(`WebM header missing in first chunk`, 'warning', {
                        streamId: streamId,
                        chunkSize: chunk.size,
                        sessionId: sessionId
                    });
                }
            };
            reader.readAsArrayBuffer(chunk.slice(0, 4));
        }

        this.debugLog(`📊 [CHUNK RECEIVED] Chunk #${recording.debugInfo.totalChunks}, Size: ${chunk.size}B, Status: ${status} - StreamID: ${streamId}, SessionID: ${sessionId}`);

        // Warn if upload queue is getting too large (potential upload issues)
        if (recording.uploadQueue.length > 10) { // Reduced threshold from 20 to 10
            this.debugWarn(`⚠️ [QUEUE WARNING] Upload queue is large: ${recording.uploadQueue.length} chunks - StreamID: ${streamId}, SessionID: ${sessionId}`);

            // Backpressure management: pause recording temporarily instead of dropping data
            if (recording.uploadQueue.length > 25) { // Increased threshold for backpressure
                this.debugWarn(`⏸️ [BACKPRESSURE] Queue too large (${recording.uploadQueue.length}), implementing backpressure - StreamID: ${streamId}`);

                // Mark backpressure with a dedicated flag. IMPORTANT: never reuse isPaused here -
                // the MediaRecorder keeps recording, and misusing isPaused corrupts the recording
                // limit accounting, the UI status and the resume() state machine.
                if (!recording.uploadBackpressure && recording.mediaRecorder && recording.mediaRecorder.state === 'recording') {
                    recording.uploadBackpressure = true;
                    this.debugLog(`⏸️ [BACKPRESSURE ON] Upload backpressure flagged - StreamID: ${streamId}`);

                    // Clear the flag after a short delay once the queue has drained
                    setTimeout(() => {
                        if (recording && recording.uploadBackpressure && recording.uploadQueue.length < 15) {
                            recording.uploadBackpressure = false;
                            this.debugLog(`▶️ [BACKPRESSURE OFF] Upload backpressure relieved - StreamID: ${streamId}`);
                        }
                    }, 2000);
                }

                // Log backpressure event to local diagnostics for monitoring
                this.diagnosticsService.logMessage(`Backpressure management activated`, 'warning', {
                    streamId: streamId,
                    queueSize: recording.uploadQueue.length,
                    totalRecordingSize: recording.debugInfo.totalSize,
                    sessionId: sessionId,
                    action: 'backpressure_activated'
                });
            }
        }

        // Process upload queue
        this.processUploadQueue(streamId);
    }

    /**
     * Process upload queue to ensure sequential uploads with circuit breaker pattern
     */
    /**
     * @param terminalDrain true ONLY for the finalize paths' own exclusive drain: it bypasses
     * the finalizeQuiesceRequested break (the flag is now HELD through the terminal commit -
     * clearing it mid-finalize let a straggler loop resurrect the concurrent-commit race).
     */
    private async processUploadQueue(streamId: string, terminalDrain: boolean = false): Promise<void> {
        const recording = this.recordings.get(streamId);
        if (!recording || recording.isUploading || recording.uploadQueue.length === 0) {
            return;
        }

        // INSTANT-START: no SAS adopted yet - uploads are impossible and MUST NOT trigger
        // ensureValidSasToken's own token fetch (it would race the in-flight prefetch and could
        // split this segment's blocks across two different blobs => InvalidBlockList).
        // adoptUploadInfo re-triggers this drain the moment the SAS arrives.
        // The !sasUrl half also guards the defer-race: if the SAS REJECTED during a teardown,
        // handleSasStartFailure clears sasPending but sasUrl stays '' - uploading then would
        // throw on every chunk and burn the circuit breaker while holding the entry.
        if (recording.sasPending || !recording.sasUrl) {
            return;
        }

        // Check circuit breaker
        if (this.isCircuitBreakerOpen(recording)) {
            console.log(`🔒 [CIRCUIT BREAKER] Upload circuit breaker is open - StreamID: ${streamId}`);
            this.debugLog(`🔒 [CIRCUIT BREAKER] Upload circuit breaker is open - StreamID: ${streamId}`);
            return;
        }

        recording.isUploading = true;
        const sessionId = this.roomSessionService?.currentSessionId;

        try {
            let consecutiveFailures = 0;
            const maxConsecutiveFailures = 5; // Increased from 3 to 5 for better resilience

            while (recording.uploadQueue.length > 0) {
                // ZOMBIE-LOOP GUARD: this recording may have been torn down and REPLACED under
                // the same streamId while an upload retry ladder held this loop for minutes
                // (refused finalize deletes the entry; the +2s recovery restart re-creates it
                // with a fresh blob). Continuing would push OLD-segment chunks through lookups
                // that now resolve to the NEW recording - splicing foreign WebM data into the
                // new blob. The loop must only ever act on ITS OWN entry.
                if (this.recordings.get(streamId) !== recording) {
                    this.debugWarn(`🧟 [STALE LOOP] Recording entry replaced/removed mid-drain, halting - StreamID: ${streamId}`);
                    break;
                }
                // A poisoned segment (terminal prefix commit / mid-segment auth failure) must
                // never receive NEW blocks: the prefix commit garbage-collected the staged tail,
                // so extending the list would only grow the guaranteed-invalid suffix.
                if (recording.forceRotation) {
                    this.debugWarn(`🔄 [ROTATION PENDING] Segment poisoned, halting upload queue - StreamID: ${streamId}`);
                    break;
                }
                // A terminal finalize is waiting to commit: stop taking new chunks and release
                // isUploading so the finalize can drain the remainder EXCLUSIVELY. Committing
                // concurrently with this loop is forbidden - the loop's next intermediate
                // commit could cover MORE blocks than the terminal list, and whichever
                // PutBlockList lands last at Azure wins (the shorter one silently drops the
                // durable tail block). The finalize's own drain passes terminalDrain=true and
                // is exempt - the flag stays SET through its commit.
                if (recording.finalizeQuiesceRequested && !terminalDrain) {
                    this.debugLog(`⏸️ [FINALIZE QUIESCE] Yielding upload loop to the terminal finalize - StreamID: ${streamId}`);
                    break;
                }
                const chunk = recording.uploadQueue.shift();
                if (chunk) {
                    try {
                        await this.uploadChunkToAzure(recording, chunk);
                        consecutiveFailures = 0; // Reset on success
                        recording.debugInfo.consecutiveFailures = 0;

                        // Crash-safety: periodically commit the staged block list while no other
                        // Put Block can be in flight (uploads are strictly sequential in this loop,
                        // guarded by isUploading). An uncommitted block staged during a Put Block List
                        // would be garbage-collected by Azure, so the commit MUST happen here.
                        await this.maybeCommitIntermediate(recording);
                    } catch (error) {
                        // Segment can no longer accept uploads (auth failure mid-segment).
                        // Stop processing immediately - rotation will start a fresh segment.
                        if (recording.forceRotation) {
                            recording.uploadQueue.unshift(chunk);
                            this.debugWarn(`🔄 [ROTATION PENDING] Halting upload queue, segment rotation required - StreamID: ${streamId}`);
                            break;
                        }

                        // Check if this is a SAS token error
                        const isSasTokenError = error.message && (
                            error.message.includes('AuthenticationFailed') ||
                            error.message.includes('Forbidden') ||
                            error.message.includes('signature') ||
                            error.message.includes('expired')
                        );

                        if (isSasTokenError) {
                            this.debugLog(`🔑 [SAS TOKEN ERROR] Attempting token refresh - StreamID: ${streamId}`);

                            // Try to refresh SAS token instead of counting as failure
                            const refreshSuccess = await this.refreshSasTokenIfNeeded(recording);
                            if (refreshSuccess) {
                                // Re-queue the chunk and continue without counting as failure
                                recording.uploadQueue.unshift(chunk);
                                continue;
                            }
                        }

                        consecutiveFailures++;
                        recording.debugInfo.consecutiveFailures = consecutiveFailures;
                        recording.debugInfo.lastErrorTime = new Date();

                        console.error(`❌ [CHUNK UPLOAD FAILED] Upload failed, consecutive failures: ${consecutiveFailures} - StreamID: ${streamId}`, error);

                        // If too many consecutive failures, trigger circuit breaker
                        if (consecutiveFailures >= maxConsecutiveFailures) {
                            this.triggerCircuitBreaker(recording);
                            console.error(`🔒 [CIRCUIT BREAKER TRIGGERED] Too many consecutive failures - StreamID: ${streamId}`);
                            break;
                        }

                        // Re-queue the failed chunk for retry
                        recording.uploadQueue.unshift(chunk);

                        // Exponential backoff before retry
                        const delay = Math.min(Math.pow(2, consecutiveFailures) * 1000, 10000);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }
            this.debugLog(`✅ [UPLOAD QUEUE SUCCESS] All chunks uploaded - StreamID: ${streamId}, SessionID: ${sessionId}`);
        } catch (error) {
            console.error(`❌ [UPLOAD QUEUE FAILED] Upload queue processing failed - StreamID: ${streamId}, SessionID: ${sessionId}`, error);
            // Mark recording as failed and attempt recovery
            if (recording) {
                recording.debugInfo.uploadErrors = (recording.debugInfo.uploadErrors || 0) + 1;
                recording.debugInfo.lastErrorTime = new Date();

                // If too many errors, stop recording to prevent resource waste
                if (recording.debugInfo.uploadErrors >= 5) {
                    console.error(`🛑 [UPLOAD ABORT] Too many upload errors, stopping recording - StreamID: ${streamId}, SessionID: ${sessionId}`);
                    this.stopRecordingForSubscriber(streamId);
                }
            }
        } finally {
            recording.isUploading = false;

            // Apply a video toggle that was deferred while this upload cycle was running.
            // NOT during a terminal finalize (finalizeQuiesceRequested held): the recording is
            // being torn down - a resume-style toggle here could spin up a concurrent
            // finalize/restart against the in-progress terminal commit.
            if (recording.pendingVideoToggle !== undefined && !recording.finalizeQuiesceRequested
                    && this.recordings.get(streamId) === recording) {
                const pendingToggle = recording.pendingVideoToggle;
                recording.pendingVideoToggle = undefined;
                this.debugLog(`🔄 [PENDING TOGGLE] Applying deferred video toggle (${pendingToggle}) - StreamID: ${streamId}`);
                this.handleVideoToggle(streamId, pendingToggle, true);
            }
        }
    }

    /**
     * Setup video toggle listener for a stream
     */
    private setupVideoToggleListener(stream: any): void {
        const streamId = stream.streamId;
        const participantName = this.getParticipantName(stream);

        // Check if listener already exists to avoid duplicates
        if (this.sessionListeners.has(`videoToggle_${streamId}`)) {
            return;
        }

        // Setup debounced video toggle handler
        const videoToggleSubject = new BehaviorSubject<boolean>(stream.hasVideo);

        const debouncedToggle = videoToggleSubject.pipe(
            debounceTime(this.videoToggleDebounceTime),
            filter((hasVideo: boolean, index: number) => index > 0) // Skip initial value
        ).subscribe((hasVideo: boolean) => {
            this.handleVideoToggle(streamId, hasVideo);
        });

        // Listen for video enabled/disabled events
        stream.on('videoEnabled', () => {
            videoToggleSubject.next(true);
        });

        stream.on('videoDisabled', () => {
            videoToggleSubject.next(false);
        });

        // Additional event listeners for better compatibility
        // Some OpenTok versions might use different event names
        stream.on('videoDimensionsChanged', (event: any) => {
            const hasVideo = event.newValue && event.newValue.width > 0 && event.newValue.height > 0;
            if (hasVideo !== stream.hasVideo) {
                videoToggleSubject.next(hasVideo);
            }
        });

        // Poll stream.hasVideo periodically as a fallback
        const pollInterval = setInterval(() => {
            const currentStream = this.streamReferences.get(streamId);
            if (!currentStream) {
                clearInterval(pollInterval);
                return;
            }

            const currentHasVideo = currentStream.hasVideo;
            const lastKnownState = videoToggleSubject.value;

            if (currentHasVideo !== lastKnownState) {
                videoToggleSubject.next(currentHasVideo);
            }
        }, 2000); // Poll every 2 seconds

        // Store subscription for cleanup
        this.sessionListeners.set(`videoToggle_${streamId}`, () => {
            debouncedToggle.unsubscribe();
            clearInterval(pollInterval);
        });
    }

    /**
     * Handle video toggle (enabled/disabled)
     */
    private async handleVideoToggle(streamId: string, hasVideo: boolean, bypassUploadGuard: boolean = false): Promise<void> {
        const recording = this.recordings.get(streamId);
        const stream = this.findStreamById(streamId);
        const participantName = stream ? this.getParticipantName(stream) : streamId;
        const sessionId = this.roomSessionService?.currentSessionId;

        this.debugLog(`🔄 [VIDEO TOGGLE] Video ${hasVideo ? 'enabled' : 'disabled'} - StreamID: ${streamId}, Participant: ${participantName}, SessionID: ${sessionId}`);

        // Defer (never drop) toggles that arrive during an upload cycle - the latest state wins
        // and is applied as soon as the upload cycle completes (see processUploadQueue finally).
        if (!bypassUploadGuard && recording && recording.isUploading) {
            recording.pendingVideoToggle = hasVideo;
            this.debugLog(`⏳ [TOGGLE DEFERRED] Recording is uploading, deferring toggle (${hasVideo}) - StreamID: ${streamId}, SessionID: ${sessionId}`);
            return;
        }


        if (hasVideo) {
            // Backend recording has taken over - never (re)start or resume frontend recording
            if (this.backendRecordingActive) {
                this.debugLog(`🚫 [TOGGLE SKIP] Backend recording active - StreamID: ${streamId}, SessionID: ${sessionId}`);
                return;
            }
            // Owner has locally unsubscribed from this video - stay paused until re-subscribed
            if (this.localVideoSubscriptionState.get(streamId) === false) {
                this.debugLog(`🚫 [TOGGLE SKIP] Owner locally unsubscribed, staying paused - StreamID: ${streamId}, SessionID: ${sessionId}`);
                return;
            }
            // Video enabled
            if (!recording) {
                // Start new recording - this handles the case where subscriber joined with video off
                if (stream) {
                    // Double-check that the stream actually has video now
                    if (stream.hasVideo) {
                        this.debugLog(`▶️ [TOGGLE START] Starting recording for newly enabled video - StreamID: ${streamId}, SessionID: ${sessionId}`);
                        this.startRecordingForSubscriber(stream);
                    } else {
                        // Wait a bit and check again
                        this.debugLog(`⏳ [TOGGLE DELAY] Waiting for video to be fully enabled - StreamID: ${streamId}, SessionID: ${sessionId}`);
                        setTimeout(() => {
                            const updatedStream = this.findStreamById(streamId);
                            if (updatedStream && updatedStream.hasVideo) {
                                this.startRecordingForSubscriber(updatedStream);
                            }
                        }, 1000);
                    }
                }
            } else if (recording.isPaused) {
                // Resume existing recording
                this.debugLog(`▶️ [TOGGLE RESUME] Resuming paused recording - StreamID: ${streamId}, SessionID: ${sessionId}`);
                await this.resumeRecording(streamId);
            }
        } else {
            // Video disabled
            if (recording && recording.isRecording) {
                this.debugLog(`⏸️ [TOGGLE PAUSE] Pausing recording for disabled video - StreamID: ${streamId}, SessionID: ${sessionId}`);
                this.pauseRecording(streamId);
            }
        }
    }

    /**
     * Pause recording (video disabled)
     */
    private pauseRecording(streamId: string): void {
        const recording = this.recordings.get(streamId);
        if (!recording || !recording.isRecording) {
            return;
        }

        recording.mediaRecorder.pause();
        recording.isPaused = true;
        recording.isVideoEnabled = false;
        recording.lastToggleTime = new Date();
    }

    /**
     * Resume recording (video enabled)
     */
    private async resumeRecording(streamId: string): Promise<void> {
        const recording = this.recordings.get(streamId);
        if (!recording || !recording.isPaused) {
            return;
        }

        // Backend recording has taken over - do not resume or restart frontend recording
        if (this.backendRecordingActive) {
            this.debugLog(`🚫 [RESUME SKIP] Backend recording active - StreamID: ${streamId}`);
            return;
        }

        // Owner has locally unsubscribed from this video - stay paused
        if (this.localVideoSubscriptionState.get(streamId) === false) {
            this.debugLog(`🚫 [RESUME SKIP] Owner locally unsubscribed - StreamID: ${streamId}`);
            return;
        }

        const pauseDuration = Date.now() - recording.lastToggleTime.getTime();
        const totalDuration = Date.now() - recording.startTime.getTime();

        // Never resume() onto a stale track snapshot: OpenTok may have replaced the MediaStream
        // during the pause (quick unsubscribe/resubscribe, camera change) - the recorder would
        // silently produce empty chunks. Restart on the current stream instead.
        const capturedTracks = recording.originalMediaStream ? recording.originalMediaStream.getVideoTracks() : [];
        const capturedTrackAlive = capturedTracks.some(track => track.readyState === 'live');

        // If paused for too long, tracks went stale, or total duration exceeds max, start new recording
        if (pauseDuration > 10000 || !capturedTrackAlive || totalDuration > this.maxRecordingDuration * 1000) {
            // Properly finalize and clean up the old recording first
            await this.finalizeAndCleanupRecording(streamId);

            // Wait a bit more and force start new recording with video state validation
            setTimeout(async () => {
                await this.startNewRecordingWithVideoValidation(streamId);
            }, 2000);
            return;
        }

        recording.mediaRecorder.resume();
        recording.isPaused = false;
        recording.isVideoEnabled = true;
        recording.lastToggleTime = new Date();

        // Reset empty chunk counter when resuming - fresh start for stream health detection
        recording.debugInfo.consecutiveEmptyChunks = 0;
    }

    /**
     * Find stream by ID
     */
    private findStreamById(streamId: string): any {
        try {
            // Use stored stream reference
            const stream = this.streamReferences.get(streamId);
            if (stream) {
                return stream;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Get Azure upload info (SAS URL)
     */
    private async getAzureUploadInfo(streamId: string): Promise<AzureUploadInfo> {
        const roomId = this.roomSessionService.currentRoomId;
        const sessionId = this.roomSessionService.currentSessionId;
        const token = this.authService.currentUser.token;

        this.debugLog(`🔐 [AZURE UPLOAD INFO] Getting upload info - StreamID: ${streamId}, RoomID: ${roomId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Getting Azure upload info`, 'info', {
            streamId: streamId,
            roomId: roomId,
            sessionId: sessionId
        });

        // Create container and blob names - backend might override the blob name
        const containerName = `vsight-recordings/${roomId}/${sessionId}`;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const requestedBlobName = `subscriber_${streamId}_${timestamp}.webm`;

        this.debugLog(`📦 [BLOB INFO] Container: ${containerName}, RequestedBlob: ${requestedBlobName} - StreamID: ${streamId}, SessionID: ${sessionId}`);
        this.diagnosticsService.logMessage(`Blob info prepared`, 'info', {
            containerName: containerName,
            requestedBlobName: requestedBlobName,
            streamId: streamId,
            sessionId: sessionId
        });

        try {
            // Get SAS token from backend
            this.debugLog(`🎫 [SAS REQUEST] Requesting SAS token from backend - StreamID: ${streamId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Requesting SAS token from backend`, 'info', {
                streamId: streamId,
                sessionId: sessionId
            });
            const sasUrl = await this.getSasTokenWithRetry(token, roomId, sessionId, requestedBlobName, streamId);

            // Extract actual blob name from SAS URL since backend might override our request
            const actualBlobName = this.extractBlobNameFromSasUrl(sasUrl);

            this.debugLog(`✅ [AZURE UPLOAD SUCCESS] Got upload info - StreamID: ${streamId}, ActualBlob: ${actualBlobName}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Got Azure upload info successfully`, 'info', {
                streamId: streamId,
                actualBlobName: actualBlobName,
                sessionId: sessionId
            });

            return {
                containerName,
                blobName: actualBlobName,
                sasUrl
            };
        } catch (error) {
            console.error(`❌ [AZURE UPLOAD FAILED] Failed to get upload info - StreamID: ${streamId}, SessionID: ${sessionId}`, error);
            this.diagnosticsService.logError(`Failed to get Azure upload info`, error, {
                streamId: streamId,
                sessionId: sessionId
            });
            throw error;
        }
    }

    /**
     * Extract blob name from SAS URL
     */
    private extractBlobNameFromSasUrl(sasUrl: string): string {
        try {
            const urlParts = sasUrl.split('?')[0]; // Remove query parameters
            const pathParts = urlParts.split('/');
            return pathParts[pathParts.length - 1]; // Last part is the blob name
        } catch (error) {
            return 'archive.webm';
        }
    }

    /**
     * Get SAS token from backend
     */
    private async getSasToken(token: string, roomId: string, sessionId: string, blobName: string, streamId: string): Promise<string> {
        // Using the dynamic endpoint from environment configuration
        const url = this.dbService.getEndPoint('getArchiveUploadLink');

        // INSTANT-START SAFETY: archiveId reuse is DISABLED. It was safe on develop only
        // because startInFlight was held across the SAS await, so a dead attempt's SAS could
        // never resolve concurrently with a live recording. Instant-start releases startInFlight
        // while the SAS is in flight, which makes reuse catastrophic: a stale response could
        // re-point the memo at a LIVE segment's blob and a later rotation's PutBlockList would
        // erase its committed footage. Minting a fresh unique blob for every SAS request is the
        // strong invariant that keeps blob identity provably safe (two recordings can never
        // share a blob). A failed start may leave a phantom pending row - the backend
        // reconciliation/TTL already handles those (pre-reuse behavior, ran in prod for years).
        const reuseArchiveId: string | undefined = undefined;

        this.debugLog(`🌐 [SAS TOKEN REQUEST] Requesting SAS token - RoomID: ${roomId}, SessionID: ${sessionId}, BlobName: ${blobName}, URL: ${url}`);
        this.diagnosticsService.logMessage(`Requesting SAS token`, 'info', {
            roomId: roomId,
            sessionId: sessionId,
            blobName: blobName,
            url: url
        });

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        });

        const requestBody: any = {
            token: token,
            roomId: roomId,
            sessionId: sessionId,
            blobName: blobName,
            recordingType: 'subscriber_video' // Distinguish from other recording types
        };
        if (reuseArchiveId) {
            requestBody.archiveId = reuseArchiveId;
        }

        try {
            this.debugLog(`📡 [SAS API] Sending request - RoomID: ${roomId}, SessionID: ${sessionId}`);
            this.diagnosticsService.logMessage(`Sending SAS API request`, 'info', {
                roomId: roomId,
                sessionId: sessionId
            });
            const response = await this.http.post<any>(url, requestBody, { headers })
                .pipe(timeout(30000)) // 30 second timeout
                .toPromise();

            if (!response || !response.data || !response.data.sasUrl) {
                console.error(`❌ [SAS INVALID RESPONSE] Invalid SAS response format - RoomID: ${roomId}, SessionID: ${sessionId}`, response);
                this.diagnosticsService.logError(`Invalid SAS response format`, null, {
                    roomId: roomId,
                    sessionId: sessionId,
                    response: JSON.stringify(response)
                });
                throw new Error('Invalid SAS token response format');
            }

            // NOTE: archiveId reuse memo intentionally NOT written - see the reuseArchiveId
            // comment above. Every SAS request mints a fresh unique blob under instant-start.

            // Log the SAS URL structure for debugging (without sensitive parts)
            const sasUrl = response.data.sasUrl;
            const urlParts = sasUrl.split('?');
            this.debugLog(`✅ [SAS TOKEN SUCCESS] Got SAS token - RoomID: ${roomId}, SessionID: ${sessionId}, BaseURL: ${urlParts[0]}`);
            this.diagnosticsService.logMessage(`Got SAS token successfully`, 'info', {
                roomId: roomId,
                sessionId: sessionId,
                baseUrl: urlParts[0]
            });

            if (urlParts.length > 1) {
                // Check if the blob name in URL matches what we requested
                if (!urlParts[0].includes(blobName)) {
                    this.debugLog(`⚠️ [SAS BLOB MISMATCH] Blob name mismatch - Requested: ${blobName}, URL: ${urlParts[0]} - SessionID: ${sessionId}`);
                    this.diagnosticsService.logMessage(`Blob name mismatch in SAS URL`, 'warning', {
                        requestedBlobName: blobName,
                        urlBlobPath: urlParts[0],
                        sessionId: sessionId
                    });
                }
            }

            return sasUrl;
        } catch (error) {
            // If the reused row was rejected (consumed/expired server-side), drop the memo
            // so the next attempt mints a fresh row instead of failing forever.
            if (reuseArchiveId) {
                this.pendingSasIssue.delete(streamId);
            }
            console.error(`❌ [SAS TOKEN FAILED] Failed to get SAS token - RoomID: ${roomId}, SessionID: ${sessionId}`, error);
            this.diagnosticsService.logError(`Failed to get SAS token`, error, {
                roomId: roomId,
                sessionId: sessionId
            });
            throw error;
        }
    }

    /**
     * Upload chunk to Azure Blob Storage with retry logic and enhanced SAS token management
     */
    private async uploadChunkToAzure(recording: SubscriberRecording, chunk: Blob): Promise<void> {
        // IDENTITY: takes the recording OBJECT, never re-resolves by streamId. Re-resolving
        // let a stale drain loop (held for minutes in a retry ladder) upload its old-segment
        // chunks into a REPLACEMENT recording's fresh blob after a teardown+restart under the
        // same streamId - foreign WebM data spliced mid-file.
        const streamId = recording.streamId;
        if (!recording.sasUrl) {
            throw new Error(`No SAS URL on recording for streamId: ${streamId}`);
        }

        const sessionId = this.roomSessionService?.currentSessionId;
        const maxRetries = 3;
        let retryCount = 0;

        while (retryCount <= maxRetries) {
            try {
                // Enhanced SAS token validation and proactive refresh
                await this.ensureValidSasToken(recording);

                // Create unique block ID with fixed length padding to ensure consistent base64 encoding
                const blockIndex = recording.blockIds.length.toString().padStart(6, '0');
                const blockIdString = `block_${Date.now()}_${blockIndex}`;
                const blockId = btoa(blockIdString);

                // Validate block ID length (Azure requires <= 64 characters when base64 decoded)
                if (blockIdString.length > 64) {
                    throw new Error(`Block ID too long: ${blockIdString.length} characters`);
                }

                // Convert blob to array buffer
                const arrayBuffer = await chunk.arrayBuffer();

                // Validate chunk size (Azure blocks can be up to 100MB)
                if (arrayBuffer.byteLength > 100 * 1024 * 1024) {
                    throw new Error(`Chunk too large: ${arrayBuffer.byteLength} bytes`);
                }

                // Upload block to Azure with timeout protection
                const blockUrl = `${recording.sasUrl}&comp=block&blockid=${blockId}`;

                console.log(`⬆️ [CHUNK UPLOAD] Uploading block - Attempt: ${retryCount + 1}, Size: ${arrayBuffer.byteLength}B, StreamID: ${streamId}`);

                // Create AbortController for 30s timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort();
                    console.error(`⏱️ [CHUNK TIMEOUT] Upload timed out after 30s - Attempt: ${retryCount + 1}, StreamID: ${streamId}`);
                }, 30000);

                try {
                    await this.http.put(blockUrl, arrayBuffer, {
                        headers: {
                            'Content-Type': 'application/octet-stream',
                            'x-ms-blob-type': 'BlockBlob'
                        }
                    }).pipe(timeout(30000)) // 30 second timeout for upload
                        .toPromise();

                    clearTimeout(timeoutId);
                } catch (uploadError) {
                    clearTimeout(timeoutId);
                    throw uploadError;
                }

                // Success - add block ID and update success tracking
                recording.blockIds.push(blockId);
                recording.debugInfo.lastSuccessfulUpload = new Date();

                // First durable footage for this stream: suppresses the START_FAILED user
                // notice (moved here from handleRecordingChunk - buffered chunks alone are
                // not durable under instant-start).
                this.everRecordedStreams.add(streamId);

                // Reset the restart budget after a sustained healthy run (~60s of DURABLE
                // blocks): legitimate swap recoveries spaced over a minute apart are never
                // budget-blocked, while rapid crash loops still hit the sliding-window caps.
                // Deliberately durable-gated (not chunk-gated) so buffered-only recordings
                // whose SAS never arrives cannot defeat the 3-strikes START_FAILED path.
                // ONLY the LIVE map entry may reset the shared streamId budget: a DETACHED
                // old segment finalizing in the background (parallel swap) must never wipe the
                // replacement segment's swap budget, or a swap storm could evade the STOPPED cap.
                if (recording.blockIds.length === 60 && this.recordings.get(streamId) === recording) {
                    this.restartHistory.delete(streamId);
                }

                // CRITICAL: Reset consecutive failures on success
                recording.debugInfo.consecutiveFailures = 0;

                console.log(`✅ [CHUNK UPLOAD SUCCESS] Block uploaded - StreamID: ${streamId}, BlockID: ${blockId}, Size: ${arrayBuffer.byteLength}, Total blocks: ${recording.blockIds.length}`);
                this.debugLog(`✅ [CHUNK UPLOAD SUCCESS] Block uploaded - StreamID: ${streamId}, BlockID: ${blockId}, Size: ${arrayBuffer.byteLength}, SessionID: ${sessionId}`);

                // Log successful upload (only log every 10th success to avoid spam)
                if (recording.blockIds.length % 10 === 0) {
                    this.diagnosticsService.logMessage(`Chunk upload milestone reached`, 'info', {
                        streamId: streamId,
                        totalBlocks: recording.blockIds.length,
                        chunkSize: arrayBuffer.byteLength,
                        totalSize: recording.debugInfo.totalSize,
                        uploadErrors: recording.debugInfo.uploadErrors || 0,
                        sessionId: sessionId,
                        component: 'RecordingService',
                        method: 'uploadChunkToAzure'
                    });
                }

                break;

            } catch (error) {
                // Segment-integrity sentinels: a fresh token would point at a DIFFERENT blob
                // (backend creates a new blob per token request) - never retry inline.
                if (error && (error.message === 'SEGMENT_BLOB_ROTATION_REQUIRED' || error.message === 'SEGMENT_SAS_EXPIRED')) {
                    throw error;
                }

                // Mid-segment auth failure: blocks are already staged on the current blob, so an
                // inline token refresh would corrupt the block list. Force a segment rotation instead.
                if (this.isSasTokenError(error) && recording.blockIds.length > 0) {
                    recording.forceRotation = true;
                    console.error(`🔄 [SEGMENT ROTATION] SAS auth failure mid-segment, rotation required - StreamID: ${streamId}`);
                    this.diagnosticsService.logError(`SAS auth failure mid-segment - forcing segment rotation`, error, {
                        streamId: streamId,
                        stagedBlocks: recording.blockIds.length,
                        committedBlocks: recording.lastCommittedBlockCount || 0,
                        errorStatus: error?.status,
                        sessionId: sessionId,
                        component: 'RecordingService',
                        method: 'uploadChunkToAzure'
                    });
                    throw error;
                }

                retryCount++;
                const errorMessage = error.message || String(error);
                const errorStatus = error?.status || 'unknown';

                console.error(`❌ [CHUNK UPLOAD FAILED] Attempt ${retryCount}/${maxRetries + 1} - Status: ${errorStatus}, Error: ${errorMessage}, StreamID: ${streamId}`);

                this.diagnosticsService.logError(`Chunk upload attempt failed`, error, {
                    streamId: streamId,
                    retryCount: retryCount,
                    maxRetries: maxRetries,
                    errorStatus: errorStatus,
                    errorMessage: errorMessage,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'uploadChunkToAzure'
                });

                // Check if this is a SAS token issue (401/403)
                // Note: only reachable when no blocks are staged yet (mid-segment auth failures are
                // rethrown above) - refreshing here is safe because the new blob becomes the segment's blob.
                if (this.isSasTokenError(error)) {
                    this.debugLog(`🔑 [SAS TOKEN ERROR] Detected SAS token issue, will refresh - StreamID: ${streamId}`);
                    recording.sasUrl = undefined; // Force token refresh on next attempt
                    recording.debugInfo.sasTokenExpiry = undefined;
                    recording.debugInfo.sasTokenRetries = (recording.debugInfo.sasTokenRetries || 0) + 1;

                    // Log SAS token error to local diagnostics
                    this.diagnosticsService.logMessage(`SAS token error during upload`, 'warning', {
                        streamId: streamId,
                        retryCount: retryCount,
                        sasTokenRetries: recording.debugInfo.sasTokenRetries,
                        errorStatus: error?.status,
                        errorMessage: error?.message || error,
                        sessionId: sessionId,
                        component: 'RecordingService',
                        method: 'uploadChunkToAzure'
                    });

                    // If too many SAS token retries, abort
                    if (recording.debugInfo.sasTokenRetries >= 3) {
                        console.error(`🛑 [SAS TOKEN ABORT] Too many SAS token retries - StreamID: ${streamId}`);

                        this.diagnosticsService.logError(`Too many SAS token retries - aborting upload`, error, {
                            streamId: streamId,
                            sasTokenRetries: recording.debugInfo.sasTokenRetries,
                            sessionId: sessionId,
                            component: 'RecordingService',
                            method: 'uploadChunkToAzure'
                        });

                        throw new Error('SAS token refresh failed multiple times');
                    }
                }

                // If this was the last retry, give up
                if (retryCount > maxRetries) {
                    console.error(`🛑 [CHUNK UPLOAD ABORT] All retry attempts failed - StreamID: ${streamId}, SessionID: ${sessionId}`);

                    // Nothing durable yet means the failing chunk is (or precedes) the WebM
                    // HEADER block (EBML + init segment). Committing any later block without it
                    // would produce an unplayable blob - poison the segment so rotation discards
                    // the headerless queue and restarts with a fresh MediaRecorder + fresh blob.
                    if (recording.blockIds.length === 0 && (recording.lastCommittedBlockCount || 0) === 0) {
                        recording.forceRotation = true;
                        this.diagnosticsService.trackEvent('recording_header_block_abort', `Header block upload failed - aborting segment to avoid headerless blob`, {
                            streamId: streamId,
                            sessionId: sessionId
                        });
                    }
                    throw error;
                }

                // Exponential backoff with jitter
                const baseDelay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
                const jitter = Math.random() * 1000; // Add up to 1s of random jitter
                const delay = baseDelay + jitter;
                console.log(`⏳ [CHUNK RETRY] Retrying in ${Math.round(delay)}ms - Attempt ${retryCount}/${maxRetries}, StreamID: ${streamId}`);
                this.debugLog(`⏳ [RETRY DELAY] Waiting ${Math.round(delay)}ms before retry - StreamID: ${streamId}, SessionID: ${sessionId}`);
                await this.delay(delay);
            }
        }
    }

    /**
     * INSTANT-START, TERMINAL STOP ONLY: a session can end while the SAS is still in flight.
     * The buffered chunks are real footage worth recovering IF the SAS is about to arrive
     * (the common cold-start case resolves in a few seconds). Give it a SHORT grace, not the
     * full ladder worst case (~96s): a long wait would hang the expert's "end session" for over
     * a minute and, worse, hold the recordings entry - blocking recovery restarts and any new
     * recording of the same stream. Beyond the grace we drop and REPORT the loss (finalize
     * returns false). Recovery/rotation teardowns do NOT call this at all - they must never
     * block, since capture continues on a replacement recording.
     *
     * This is strictly better than pre-instant-start (develop), where the recording had not
     * even started during the SAS wait, so a stop in that window captured NOTHING either way.
     */
    private readonly pendingSasFinalizeGraceMs = 4000;
    private async awaitPendingSasAdoption(recording: SubscriberRecording, caller: string): Promise<void> {
        if (!(recording.sasPending && recording.pendingUploadInfo && recording.uploadQueue.length > 0)) {
            return;
        }
        this.debugLog(`⏳ [FINALIZE SAS WAIT] Terminal stop with SAS still in flight, short grace - StreamID: ${recording.streamId}, BufferedChunks: ${recording.uploadQueue.length}, Caller: ${caller}`);
        try {
            await Promise.race([
                recording.pendingUploadInfo,
                new Promise((_, reject) => setTimeout(() => reject(new Error('FINALIZE_SAS_WAIT_TIMEOUT')), this.pendingSasFinalizeGraceMs))
            ]);
            // Yield one macrotask so the adoption continuation definitely completed
            await new Promise(resolve => setTimeout(resolve, 0));
        } catch (sasWaitError) {
            this.debugWarn(`⚠️ [FINALIZE SAS WAIT] SAS did not arrive within grace (${sasWaitError?.message}) - buffered footage cannot be saved - StreamID: ${recording.streamId}, Caller: ${caller}`);
        }
    }

    /**
     * Finalize recording and commit blocks.
     * Returns true when everything captured is durably committed (or there was nothing to
     * commit); false when the final commit failed OR captured footage had to be discarded
     * because its SAS never arrived - callers must NOT report success then.
     */
    private async finalizeRecording(streamId: string): Promise<boolean> {
        const recording = this.recordings.get(streamId);
        if (!recording) {
            this.debugWarn(`⚠️ [FINALIZE SKIP] No recording - StreamID: ${streamId}`);
            return true; // Nothing was ever staged - nothing to lose
        }

        await this.awaitPendingSasAdoption(recording, 'finalizeRecording');

        if (!recording.sasUrl) {
            // Captured-but-never-adoptable footage is a LOSS, not a success: report false so
            // stopRecordingForSubscriber emits recording_finalize_failed + SAVE_FAILED instead
            // of recording_completed (silent data loss dressed as success).
            if (recording.uploadQueue.length > 0 || recording.debugInfo.totalChunks > 0) {
                const droppedChunks = recording.uploadQueue.length;
                const droppedBytes = recording.uploadQueue.reduce((sum, c) => sum + c.size, 0);
                recording.uploadQueue = [];
                this.diagnosticsService.logError(`Dropping buffered footage - SAS never adopted`, null, {
                    streamId: streamId,
                    droppedChunks: droppedChunks,
                    droppedBytes: droppedBytes,
                    totalChunks: recording.debugInfo.totalChunks,
                    sessionId: this.roomSessionService?.currentSessionId,
                    component: 'RecordingService',
                    method: 'finalizeRecording'
                });
                return false;
            }
            this.debugWarn(`⚠️ [FINALIZE SKIP] No SAS URL and nothing captured - StreamID: ${streamId}`);
            return true; // Nothing was ever staged - nothing to lose
        }

        const sessionId = this.roomSessionService?.currentSessionId;

        try {
            this.debugLog(`🔄 [FINALIZE START] Starting finalization - StreamID: ${streamId}, SessionID: ${sessionId}`);

            // Segment can no longer accept uploads (auth failure): the queued chunks belong to the
            // old WebM stream and cannot be uploaded to any blob - drop them with full telemetry.
            if (recording.forceRotation && recording.uploadQueue.length > 0) {
                const droppedChunks = recording.uploadQueue.length;
                const droppedBytes = recording.uploadQueue.reduce((sum, c) => sum + c.size, 0);
                recording.uploadQueue = [];
                this.diagnosticsService.logError(`Dropping unuploadable chunks of rotated segment`, null, {
                    streamId: streamId,
                    droppedChunks: droppedChunks,
                    droppedBytes: droppedBytes,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'finalizeRecording'
                });
            }

            // A terminal prefix commit already garbage-collected every staged block past the
            // prefix - committing the full list would be a guaranteed InvalidBlockList. Truncate
            // to the durable prefix so the final commit is an idempotent re-commit.
            if (recording.forceRotation && recording.committedPrefixCount
                    && recording.blockIds.length > recording.committedPrefixCount) {
                recording.blockIds = recording.blockIds.slice(0, recording.committedPrefixCount);
            }

            // QUIESCE-BEFORE-COMMIT: a drain loop may already be live (isUploading). The old
            // code waited 30s, only WARNED on timeout, then fell through to commitBlocks -
            // a terminal PutBlockList racing a still-live drain loop: the loop could ACK one
            // more block and intermediate-commit a LONGER list, and this shorter terminal list
            // landing last at Azure would silently drop that durable tail block (and a commit
            // racing an in-flight PutBlock garbage-collects it). Signal the loop to stop
            // taking new chunks, and NEVER commit while it still runs.
            // The flag is HELD from here through the terminal commit (never cleared - the
            // entry is torn down after finalize anyway). Clearing it earlier let a straggler
            // drain loop wake AFTER the clear and race the commit; and with two concurrent
            // finalizers a boolean clear-by-one wiped the other's quiesce request.
            recording.finalizeQuiesceRequested = true;
            // 150s: one full uploadChunkToAzure cycle (4 attempts x 30s HTTP timeouts + 2/4/8s
            // backoffs ≈ 137s) can legitimately hold isUploading longer than the old 30s wait,
            // which then REFUSED the commit and dropped a savable tail on a single transient
            // upload timeout at stop time.
            let quiesceAttempts = 0;
            while (recording.isUploading && quiesceAttempts < 150) {
                if (quiesceAttempts % 10 === 0) {
                    this.debugLog(`⏳ [FINALIZE QUIESCE] Waiting for live upload loop to quiesce (${quiesceAttempts}/150) - StreamID: ${streamId}, SessionID: ${sessionId}`);
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
                quiesceAttempts++;
            }
            if (recording.isUploading) {
                // A stuck in-flight PutBlock / retry ladder. Committing now would race it -
                // refuse. The durable state is whatever the last checkpoint committed.
                this.diagnosticsService.logError(`Finalize aborted - upload loop did not quiesce in 150s, refusing a concurrent terminal commit`, null, {
                    streamId: streamId,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    queuedChunks: recording.uploadQueue.length,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'finalizeRecording'
                });
                return false;
            }

            // Drain any remaining chunks EXCLUSIVELY (terminalDrain bypasses the quiesce break;
            // no other loop can start - they all yield on the held flag)
            if (recording.uploadQueue.length > 0) {
                this.debugLog(`📤 [FINALIZE QUEUE] Processing ${recording.uploadQueue.length} remaining chunks - StreamID: ${streamId}, SessionID: ${sessionId}`);
                await this.processUploadQueue(streamId, true);
            }

            // If a concurrent trigger grabbed the upload lock between the quiesce and our
            // drain (late ondataavailable flush), wait for it - and refuse the commit if it
            // will not settle, exactly like the quiesce gate above.
            let attempts = 0;
            while (recording.isUploading && attempts < 30) {
                this.debugLog(`⏳ [FINALIZE WAIT] Waiting for upload completion (${attempts}/30) - StreamID: ${streamId}, SessionID: ${sessionId}`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                attempts++;
            }
            if (recording.isUploading) {
                this.diagnosticsService.logError(`Finalize aborted - drain still running after 30s, refusing a concurrent terminal commit`, null, {
                    streamId: streamId,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    queuedChunks: recording.uploadQueue.length,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'finalizeRecording'
                });
                return false;
            }

            // Commit all blocks
            if (recording.blockIds.length > 0) {
                this.debugLog(`📝 [FINALIZE COMMIT] Committing ${recording.blockIds.length} blocks - StreamID: ${streamId}, SessionID: ${sessionId}`);
                await this.commitBlocks(recording);
                this.debugLog(`✅ [FINALIZE SUCCESS] Recording finalized successfully - StreamID: ${streamId}, Blocks: ${recording.blockIds.length}, SessionID: ${sessionId}`);
            } else {
                this.debugWarn(`⚠️ [FINALIZE NO BLOCKS] No blocks to commit - StreamID: ${streamId}, SessionID: ${sessionId}`);
            }

            // If the queue could not be fully drained (circuit breaker opened mid-drain, or the
            // 30s isUploading wait timed out), the tail chunks were never uploaded - they are NOT
            // in the committed block list. Reporting success here would surface recording_completed
            // for a segment missing its tail. Instant-start makes this materially worse: the whole
            // pre-SAS backlog is drained at finalize, so a stalled drain loses seconds, not the
            // ~1 tail chunk develop left. Report failure so the SAVE_FAILED path fires.
            if (recording.uploadQueue.length > 0) {
                this.diagnosticsService.logError(`Finalize left ${recording.uploadQueue.length} chunk(s) undrained - tail not durable`, null, {
                    streamId: streamId,
                    undrainedChunks: recording.uploadQueue.length,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    circuitBreakerOpen: recording.debugInfo.circuitBreakerOpen || false,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'finalizeRecording'
                });
                return false;
            }
            return true;

        } catch (error) {
            console.error(`❌ [FINALIZE FAILED] Finalization failed - StreamID: ${streamId}, SessionID: ${sessionId}`, error);
            // Record the error but don't re-throw to prevent cascading failures
            if (recording.debugInfo) {
                recording.debugInfo.uploadErrors = (recording.debugInfo.uploadErrors || 0) + 1;
            }
            // Data staged since the last checkpoint is NOT durable - report failure to callers
            return false;
        }
    }

    /**
     * Commit blocks to finalize the blob with enhanced retry logic.
     * With intermediate=true this performs a crash-safety checkpoint commit:
     * single attempt, short timeout, never validates and callers treat failure as non-fatal.
     * Re-committing the full list later is safe (PutBlockList <Latest> resolves
     * uncommitted blocks first, then falls back to already-committed blocks).
     */
    private async commitBlocks(recording: SubscriberRecording, intermediate: boolean = false): Promise<void> {
        const sessionId = this.roomSessionService?.currentSessionId;
        const maxRetries = intermediate ? 0 : 3;
        let retryCount = 0;

        while (retryCount <= maxRetries) {
            try {
                // Ensure we have a valid SAS token before committing
                await this.ensureValidSasToken(recording);

                const commitUrl = `${recording.sasUrl}&comp=blocklist`;

                // Create block list XML with explicit ordering for WebM format
                const blockListXml = '<?xml version="1.0" encoding="utf-8"?>' +
                    '<BlockList>' +
                    recording.blockIds.map(blockId => `<Latest>${blockId}</Latest>`).join('') +
                    '</BlockList>';

                this.debugLog(`📝 [COMMIT BLOCKS] Committing ${recording.blockIds.length} blocks (${intermediate ? 'intermediate' : 'final'}, attempt ${retryCount + 1}/${maxRetries + 1}) - StreamID: ${recording.streamId}, SessionID: ${sessionId}`);

                await this.http.put(commitUrl, blockListXml, {
                    headers: {
                        'Content-Type': 'application/xml',
                        'x-ms-blob-content-type': 'video/webm' // Changed to match WebM format
                    }
                }).pipe(timeout(intermediate ? 15000 : 45000)) // Short timeout for checkpoints to avoid stalling uploads
                    .toPromise();

                this.debugLog(`✅ [COMMIT SUCCESS] Blocks committed successfully (${intermediate ? 'intermediate' : 'final'}) - StreamID: ${recording.streamId}, SessionID: ${sessionId}`);

                if (intermediate) {
                    return; // Checkpoint done - skip final-commit telemetry and validation
                }

                // Log successful commit to local diagnostics
                this.diagnosticsService.logMessage(`Recording blocks committed successfully`, 'info', {
                    streamId: recording.streamId,
                    participantName: recording.participantName,
                    totalBlocks: recording.blockIds.length,
                    totalSize: recording.debugInfo.totalSize,
                    attemptNumber: retryCount + 1,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'commitBlocks'
                });

                // Track successful recording completion
                this.diagnosticsService.trackEvent('recording_commit_success', `Recording successfully committed to Azure`, {
                    streamId: recording.streamId,
                    participantName: recording.participantName,
                    totalBlocks: recording.blockIds.length,
                    totalSize: recording.debugInfo.totalSize,
                    sessionId: sessionId
                });

                // Validate that the blob was created successfully
                await this.validateBlobExists(recording);
                return; // Success, exit retry loop

            } catch (error) {
                retryCount++;
                console.error(`❌ [COMMIT FAILED] Attempt ${retryCount}/${maxRetries + 1} (${intermediate ? 'intermediate' : 'final'}) - StreamID: ${recording.streamId}, SessionID: ${sessionId}`, error);

                // Segment-integrity sentinels cannot be retried (a fresh token targets a different blob)
                if (error && (error.message === 'SEGMENT_BLOB_ROTATION_REQUIRED' || error.message === 'SEGMENT_SAS_EXPIRED')) {
                    throw error;
                }

                // Check if this is a SAS token issue
                if (this.isSasTokenError(error)) {
                    this.debugLog(`🔑 [COMMIT SAS ERROR] SAS token issue detected during commit - StreamID: ${recording.streamId}`);
                    // Only safe to force a token refresh when no blocks are staged on the current blob
                    if (recording.blockIds.length === 0) {
                        recording.sasUrl = undefined; // Force token refresh
                        recording.debugInfo.sasTokenExpiry = undefined;
                    }

                    this.diagnosticsService.logMessage(`SAS token error during commit`, 'warning', {
                        streamId: recording.streamId,
                        participantName: recording.participantName,
                        attemptNumber: retryCount + 1,
                        errorStatus: error?.status,
                        errorMessage: error?.message || error,
                        sessionId: sessionId,
                        component: 'RecordingService',
                        method: 'commitBlocks'
                    });
                }

                if (retryCount > maxRetries) {
                    console.error(`🛑 [COMMIT ABORT] All commit attempts failed - StreamID: ${recording.streamId}, SessionID: ${sessionId}`);

                    // Log final commit failure to local diagnostics as error
                    this.diagnosticsService.logError(`Recording commit failed after all retries`, error, {
                        streamId: recording.streamId,
                        participantName: recording.participantName,
                        totalBlocks: recording.blockIds.length,
                        maxRetries: maxRetries,
                        finalError: error?.message || error,
                        sessionId: sessionId,
                        component: 'RecordingService',
                        method: 'commitBlocks'
                    });

                    // Track commit failure event
                    this.diagnosticsService.trackEvent('recording_commit_failure', `Recording commit failed after all retries`, {
                        streamId: recording.streamId,
                        participantName: recording.participantName,
                        totalBlocks: recording.blockIds.length,
                        maxRetries: maxRetries,
                        sessionId: sessionId
                    });

                    throw error; // Re-throw to let calling code handle the error
                }

                // Exponential backoff before retry
                const delay = Math.pow(2, retryCount) * 2000; // 4s, 8s, 16s
                this.debugLog(`⏳ [COMMIT RETRY] Waiting ${delay}ms before commit retry - StreamID: ${recording.streamId}`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * Finalize and completely clean up a recording (for starting fresh)
     */
    private async finalizeAndCleanupRecording(streamId: string): Promise<void> {
        const recording = this.recordings.get(streamId);
        if (!recording) {
            return;
        }

        try {
            // Stop MediaRecorder if still running
            if (recording.mediaRecorder && recording.isRecording) {
                recording.mediaRecorder.stop();
                recording.isRecording = false;
                // Wait a bit for final chunks
                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            // INSTANT-START: recovery/rotation/backend-transition teardowns can hit a sasPending
            // entry. Do NOT wait for the SAS here - this path runs while a REPLACEMENT recording
            // is about to start (swap recovery, rotation) or capture is stopping entirely
            // (backend takeover). Blocking would hold the recordings entry, dead-ending the +2s
            // recovery restart and the health-check fallback (both bail on recordings.has /
            // size>=1) - a multi-minute capture gap. The pending buffer is at most a few seconds
            // of not-yet-adoptable footage; drop it immediately with telemetry and let the
            // replacement recording take over now.
            // Gate on !sasUrl (NOT sasPending): if the SAS REJECTED during the 1s recorder-stop
            // wait above, handleSasStartFailure's defer branch clears sasPending but leaves
            // sasUrl='' - checking sasPending would then skip the drop and let the undroppable
            // queue fall through to processUploadQueue (throws per chunk, ~24s of retries).
            if (!recording.sasUrl && recording.uploadQueue.length > 0) {
                const droppedChunks = recording.uploadQueue.length;
                const droppedBytes = recording.uploadQueue.reduce((sum, c) => sum + c.size, 0);
                recording.uploadQueue = [];
                this.diagnosticsService.logError(`Dropping buffered footage - SAS never adopted`, null, {
                    streamId: streamId,
                    droppedChunks: droppedChunks,
                    droppedBytes: droppedBytes,
                    totalChunks: recording.debugInfo.totalChunks,
                    sessionId: this.roomSessionService?.currentSessionId,
                    component: 'RecordingService',
                    method: 'finalizeAndCleanupRecording'
                });
                // Monitoring parity with the stop path's failure event (no user notice here -
                // a replacement recording is taking over, so a flash message would be noise).
                this.diagnosticsService.trackEvent('recording_pending_footage_dropped', `Instant-start buffered footage dropped before adoption`, {
                    streamId: streamId,
                    droppedChunks: droppedChunks,
                    droppedBytes: droppedBytes,
                    sessionId: this.roomSessionService?.currentSessionId
                });
            }

            // Segment can no longer accept uploads (auth failure): drop the unuploadable chunks
            if (recording.forceRotation && recording.uploadQueue.length > 0) {
                const droppedChunks = recording.uploadQueue.length;
                const droppedBytes = recording.uploadQueue.reduce((sum, c) => sum + c.size, 0);
                recording.uploadQueue = [];
                this.diagnosticsService.logError(`Dropping unuploadable chunks of rotated segment`, null, {
                    streamId: streamId,
                    droppedChunks: droppedChunks,
                    droppedBytes: droppedBytes,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    component: 'RecordingService',
                    method: 'finalizeAndCleanupRecording'
                });
            }

            // A terminal prefix commit already garbage-collected staged blocks past the prefix -
            // truncate so the final commit re-commits the durable prefix instead of failing.
            if (recording.forceRotation && recording.committedPrefixCount
                    && recording.blockIds.length > recording.committedPrefixCount) {
                recording.blockIds = recording.blockIds.slice(0, recording.committedPrefixCount);
            }

            // QUIESCE-BEFORE-COMMIT (same invariant as finalizeRecording): never let the
            // terminal PutBlockList race a still-live drain loop - the loop's next
            // intermediate commit can cover MORE blocks and whichever list lands last wins.
            // The flag is HELD through the terminal commit (never cleared; the entry is
            // deleted in this method's finally) - clearing it mid-teardown let a straggler
            // loop wake after a refused finalize and, combined with the +2s restart under the
            // same streamId, splice old-segment chunks into the replacement recording's blob.
            recording.finalizeQuiesceRequested = true;
            // 150s covers one full uploadChunkToAzure retry cycle (see finalizeRecording)
            let quiesceAttempts = 0;
            while (recording.isUploading && quiesceAttempts < 150) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                quiesceAttempts++;
            }
            const quiesced = !recording.isUploading;

            // Process any remaining chunks (exclusively - terminalDrain bypasses the held flag)
            if (quiesced && recording.uploadQueue.length > 0) {
                await this.processUploadQueue(streamId, true);
            }

            // Wait for current upload to complete
            let attempts = 0;
            while (recording.isUploading && attempts < 30) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                attempts++;
            }
            if (recording.isUploading) {
                // Refuse a concurrent terminal commit - the last checkpoint stands. This
                // teardown is void (rotation/recovery path); log the loss and let cleanup run.
                this.diagnosticsService.logError(`Teardown skipped terminal commit - upload loop still running after quiesce+wait`, null, {
                    streamId: streamId,
                    committedBlocks: recording.lastCommittedBlockCount || 0,
                    stagedBlocks: recording.blockIds.length,
                    queuedChunks: recording.uploadQueue.length,
                    component: 'RecordingService',
                    method: 'finalizeAndCleanupRecording'
                });
                return;
            }

            // Commit all blocks if any exist
            if (recording.blockIds.length > 0) {
                await this.commitBlocks(recording);
            }

        } catch (error) {
            // Silent error handling
        } finally {
            // Clean up state monitor
            const stateMonitorCleanup = this.sessionListeners.get(`stateMonitor_${streamId}`);
            if (stateMonitorCleanup) {
                stateMonitorCleanup();
                this.sessionListeners.delete(`stateMonitor_${streamId}`);
            }

            // Remove from recordings map - identity-guarded (see stopRecordingForSubscriber's
            // finally: never delete a replacement entry created under the same streamId)
            if (this.recordings.get(streamId) === recording) {
                this.recordings.delete(streamId);
            }
            this.updateRecordingStatus();
        }
    }

    /**
     * Start new recording with enhanced video state validation
     */
    private async startNewRecordingWithVideoValidation(streamId: string): Promise<void> {
        // Backend recording has taken over - never start a new frontend recording
        if (this.backendRecordingActive) {
            this.debugLog(`🚫 [RESTART SKIP] Backend recording active - StreamID: ${streamId}`);
            return;
        }

        const stream = this.findStreamById(streamId);
        if (!stream) {
            return;
        }

        const participantName = this.getParticipantName(stream);

        // Multiple attempts to validate video state
        let attempts = 0;
        const maxAttempts = 5;

        while (attempts < maxAttempts) {
            const currentStream = this.findStreamById(streamId);
            if (!currentStream) {
                return;
            }

            if (currentStream.hasVideo) {
                await this.startRecordingForSubscriber(currentStream);
                return;
            }

            attempts++;
            if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        // Force start anyway - the video toggle listener will catch video when it comes back

        // Set force flag so startRecordingForSubscriber knows to continue despite no video
        this.sessionListeners.set(`forceStart_${streamId}`, () => { });

        await this.startRecordingForSubscriber(stream);
    }

    /**
     * Get participant name from stream
     */
    private getParticipantName(stream: any): string {
        try {
            const connectionData = JSON.parse(stream.connection.data);
            return connectionData.name || `User_${stream.connection.connectionId}`;
        } catch {
            return `Subscriber_${stream.streamId}`;
        }
    }

    private getParticipantUserId(stream: any): string {
        try {
            const connectionData = JSON.parse(stream.connection.data);
            return connectionData.uid || stream.connection.connectionId || stream.streamId;
        } catch {
            return stream.streamId;
        }
    }

    /**
     * Update recording status for UI
     */
    private updateRecordingStatus(): void {
        const statusMap = new Map<string, boolean>();
        this.recordings.forEach((recording, streamId) => {
            statusMap.set(streamId, recording.isRecording && !recording.isPaused);
        });
        this.recordingStatusSubject.next(statusMap);
    }

    /**
     * Check if recording is supported
     */
    isRecordingSupported(): boolean {
        return typeof MediaRecorder !== 'undefined' &&
            MediaRecorder.isTypeSupported('video/webm');
    }

    /**
     * Get active recordings count
     */
    getActiveRecordingsCount(): number {
        return Array.from(this.recordings.values()).filter(r => r.isRecording && !r.isPaused).length;
    }

    /**
     * Log current recording state for debugging
     */
    public logRecordingState(): void {
        const activeCount = this.getActiveRecordingsCount();
        const totalCount = this.recordings.size;

        if (this.recordings.size > 0) {
            this.recordings.forEach((recording, streamId) => {
                // Recording info is available for debugging purposes if needed
            });
        }
    }

    /**
     * Get recording info for debugging
     */
    getRecordingInfo(streamId: string): any {
        const recording = this.recordings.get(streamId);
        if (!recording) {
            return null;
        }

        return {
            participantName: recording.participantName,
            duration: Date.now() - recording.startTime.getTime(),
            isRecording: recording.isRecording,
            isPaused: recording.isPaused,
            totalChunks: recording.debugInfo.totalChunks,
            totalSize: recording.debugInfo.totalSize,
            blockIds: recording.blockIds.length,
            uploadErrors: recording.debugInfo.uploadErrors || 0,
            lastSuccessfulUpload: recording.debugInfo.lastSuccessfulUpload,
            sasTokenExpiry: recording.debugInfo.sasTokenExpiry,
            queueLength: recording.uploadQueue.length,
            isUploading: recording.isUploading
        };
    }

    /**
     * Stop all recordings and cleanup
     */
    private async cleanupAllRecordings(): Promise<void> {
        // SNAPSHOT-BEFORE-AWAIT: this is called UN-AWAITED from initForSession while the NEXT
        // room may already be initializing on this root-singleton service. The awaited stops
        // below can take tens of seconds (upload drains + finalize). The old order awaited
        // FIRST and cleared the live maps AFTER - by which time the incoming session's
        // listeners and stream state lived in these same maps and got wiped, leaving the new
        // room with no recording for the whole call. Snapshot the OUTGOING session's state and
        // clear the live maps SYNCHRONOUSLY (no await can interleave before this completes),
        // then do the slow stops against the snapshot.
        const activeRecordings = Array.from(this.recordings.keys());
        const outgoingListenerCleanups = Array.from(this.sessionListeners.values());
        this.sessionListeners.clear();
        this.streamReferences.clear();
        this.localVideoSubscriptionState.clear();
        this.lastStartAttempt.clear();
        this.lastRotationTime.clear();
        this.restartHistory.clear();
        this.queueCapHistory.clear();
        this.notifiedUserIssues.clear();
        this.everRecordedStreams.clear();
        this.pendingSasIssue.clear();
        // rotatingStreams / startInFlight / stoppingStreams are deliberately NOT cleared:
        // they are IN-FLIGHT mutual-exclusion guards whose owners delete their own key in a
        // finally. Wiping them here would let this sweep's stops run concurrently with a
        // rotation finalize that is still mid-quiesce (the double-finalize class the guards
        // exist to prevent). Keys are per-streamId, so old-session leftovers never collide
        // with the next session's streams and self-clean when their owner completes.

        // Detach the outgoing session's OpenTok listeners NOW - they reference the old
        // session object and must never observe the new session's events. Per-recording
        // stateMonitor cleanups live in the same snapshot; clearing them early is safe
        // (the monitors self-terminate once their recording ends).
        outgoingListenerCleanups.forEach(cleanup => {
            try {
                cleanup();
            } catch (error) {
                // Handle cleanup error silently
            }
        });

        // Stop and finalize the outgoing recordings. These only touch per-recording state
        // (recordings entries are deleted per-stream in stop's finally; guard-set deletes of
        // already-cleared keys are no-ops), so they can safely overlap the next session's
        // setup without touching its maps.
        // KNOWN LIMITATION (accepted): this.recordings entries stay under their original keys
        // until each stop's finally. If the SAME OpenTok streamId reappears in the next session
        // (owner rejoins the same session while the participant keeps publishing) its recording
        // start is skipped until the outgoing drain finishes; recoverMissedRecordings picks it
        // up afterwards (its cap counts only live recordings). Re-keying the draining entries
        // was evaluated and rejected - too many streamId-keyed lookups to re-thread safely.
        await Promise.all(activeRecordings.map(streamId => this.stopRecordingForSubscriber(streamId)));
    }

    /**
     * OnDestroy lifecycle hook
     */
    ngOnDestroy(): void {
        // Cancel a pending listener-attach timer so it can't fire into a torn-down session
        if (this.listenerSetupTimer) {
            clearTimeout(this.listenerSetupTimer);
            this.listenerSetupTimer = null;
        }
        this.cleanupAllRecordings();
        this.stopHealthMonitoring();
    }

    /**
     * Manual method to check all streams and start recording if needed
     * Can be called if automatic video toggle detection fails
     */
    public checkAllStreamsForRecording(): void {
        if (this.backendRecordingActive) {
            return;
        }
        this.streamReferences.forEach((stream, streamId) => {
            const recording = this.recordings.get(streamId);
            const participantName = this.getParticipantName(stream);
            const locallySubscribed = this.localVideoSubscriptionState.get(streamId) !== false;

            // If stream has video but no recording, start one
            if (stream.hasVideo && locallySubscribed && !recording) {
                this.startRecordingForSubscriber(stream);
            }
            // If stream has video but recording is paused, resume it
            else if (stream.hasVideo && locallySubscribed && recording && recording.isPaused) {
                this.resumeRecording(streamId);
            }
            // If stream has no video but recording is active, pause it
            else if ((!stream.hasVideo || !locallySubscribed) && recording && recording.isRecording) {
                this.pauseRecording(streamId);
            }
        });
    }

    /**
     * Get debug info for all streams and recordings
     */
    public getDebugInfo(): any {
        const streams = Array.from(this.streamReferences.entries()).map(([streamId, stream]) => ({
            streamId,
            participantName: this.getParticipantName(stream),
            hasVideo: stream.hasVideo,
            videoType: stream.videoType
        }));

        const recordings = Array.from(this.recordings.entries()).map(([streamId, recording]) => ({
            streamId,
            participantName: recording.participantName,
            isRecording: recording.isRecording,
            isPaused: recording.isPaused,
            totalChunks: recording.debugInfo.totalChunks,
            totalSize: recording.debugInfo.totalSize,
            uploadErrors: recording.debugInfo.uploadErrors || 0,
            consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
            circuitBreakerOpen: recording.debugInfo.circuitBreakerOpen || false,
            sasTokenRetries: recording.debugInfo.sasTokenRetries || 0,
            lastSuccessfulUpload: recording.debugInfo.lastSuccessfulUpload,
            lastErrorTime: recording.debugInfo.lastErrorTime,
            uploadQueueLength: recording.uploadQueue.length,
            isUploading: recording.isUploading
        }));

        return {
            streams,
            recordings,
            sessionListeners: Array.from(this.sessionListeners.keys()),
            backendRecordingActive: this.backendRecordingActive,
            maxFrontendRecordings: this.maxFrontendRecordings,
            activeRecordingsCount: this.getActiveRecordingsCount()
        };
    }

    /**
     * Get recording health metrics for monitoring
     */
    public getHealthMetrics(): any {
        const recordings = Array.from(this.recordings.values());
        const totalRecordings = recordings.length;
        const activeRecordings = recordings.filter(r => r.isRecording && !r.isPaused).length;
        const pausedRecordings = recordings.filter(r => r.isPaused).length;
        const recordingsWithErrors = recordings.filter(r => (r.debugInfo.uploadErrors || 0) > 0).length;
        const recordingsWithCircuitBreakerOpen = recordings.filter(r => r.debugInfo.circuitBreakerOpen).length;

        const totalUploadErrors = recordings.reduce((sum, r) => sum + (r.debugInfo.uploadErrors || 0), 0);
        const totalChunks = recordings.reduce((sum, r) => sum + r.debugInfo.totalChunks, 0);
        const totalSize = recordings.reduce((sum, r) => sum + r.debugInfo.totalSize, 0);
        const totalQueueLength = recordings.reduce((sum, r) => sum + r.uploadQueue.length, 0);

        const errorRate = totalChunks > 0 ? (totalUploadErrors / totalChunks) * 100 : 0;
        const avgQueueLength = totalRecordings > 0 ? totalQueueLength / totalRecordings : 0;

        // Calculate session duration metrics
        const now = new Date();
        const sessionDurations = recordings.map(r => now.getTime() - r.startTime.getTime());
        const avgSessionDuration = sessionDurations.length > 0 ?
            sessionDurations.reduce((sum, d) => sum + d, 0) / sessionDurations.length : 0;

        return {
            totalRecordings,
            activeRecordings,
            pausedRecordings,
            recordingsWithErrors,
            recordingsWithCircuitBreakerOpen,
            totalUploadErrors,
            totalChunks,
            totalSize: Math.round(totalSize / 1024 / 1024 * 100) / 100, // MB with 2 decimal places
            errorRate: Math.round(errorRate * 100) / 100, // Percentage with 2 decimal places
            avgQueueLength: Math.round(avgQueueLength * 100) / 100,
            avgSessionDuration: Math.round(avgSessionDuration / 1000), // Seconds
            backendRecordingActive: this.backendRecordingActive,
            timestamp: now.toISOString()
        };
    }

    /**
     * Transition from front-end recording to backend recording
     */
    private transitionToBackendRecording(): void {
        // Mark backend recording as active
        this.backendRecordingActive = true;

        // Gracefully finalize ALL current recordings - including paused ones, otherwise a
        // paused entry could be resumed later and record in parallel with the backend archive
        const allRecordingIds = Array.from(this.recordings.keys());
        allRecordingIds.forEach(streamId => {
            this.finalizeAndCleanupRecording(streamId);
        });

        // Optionally emit an event or call a service method to notify backend
        // this.notifyBackendToStartRecording();
    }

    /**
     * Check if backend recording is currently active
     */
    public isBackendRecordingActive(): boolean {
        return this.backendRecordingActive;
    }

    /**
     * Get maximum number of front-end recordings allowed
     */
    public getMaxFrontendRecordings(): number {
        return this.maxFrontendRecordings;
    }

    /**
     * Check if circuit breaker is open for a recording
     */
    private isCircuitBreakerOpen(recording: SubscriberRecording): boolean {
        if (!recording.debugInfo.circuitBreakerOpen) {
            return false;
        }

        // Check if circuit breaker reset time has passed
        if (recording.debugInfo.circuitBreakerResetTime &&
            new Date() > recording.debugInfo.circuitBreakerResetTime) {
            recording.debugInfo.circuitBreakerOpen = false;
            recording.debugInfo.circuitBreakerResetTime = undefined;
            recording.debugInfo.consecutiveFailures = 0;

            const sessionId = this.roomSessionService?.currentSessionId;
            const roomId = this.roomSessionService?.currentRoomId;

            this.debugLog(`🔓 [CIRCUIT BREAKER RESET] Circuit breaker reset for streamId: ${recording.streamId}`);

            // Log circuit breaker reset to local diagnostics
            this.diagnosticsService.logMessage(`Circuit breaker reset for recording`, 'info', {
                streamId: recording.streamId,
                participantName: recording.participantName,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'isCircuitBreakerOpen'
            });

            // Track recovery event
            this.diagnosticsService.trackEvent('recording_circuit_breaker_reset', `Recording circuit breaker reset - service recovered`, {
                streamId: recording.streamId,
                participantName: recording.participantName,
                sessionId: sessionId,
                roomId: roomId
            });

            return false;
        }

        return true;
    }

    /**
     * Trigger circuit breaker for a recording
     */
    private triggerCircuitBreaker(recording: SubscriberRecording): void {
        recording.debugInfo.circuitBreakerOpen = true;
        // Reset after 2 minutes for better recovery
        recording.debugInfo.circuitBreakerResetTime = new Date(Date.now() + 120000);

        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;

        console.error(`🚨 [CIRCUIT BREAKER OPEN] Circuit breaker triggered - StreamID: ${recording.streamId}, Failures: ${recording.debugInfo.consecutiveFailures}`);
        this.debugLog(`🔒 [CIRCUIT BREAKER OPEN] Circuit breaker opened for streamId: ${recording.streamId}, will reset at: ${recording.debugInfo.circuitBreakerResetTime}`);

        // Notify backend of circuit breaker trigger
        this.notifyRecordingFailure(
            sessionId || 'unknown',
            'CIRCUIT_BREAKER_OPEN',
            {
                streamId: recording.streamId,
                participantName: recording.participantName,
                consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
                uploadErrors: recording.debugInfo.uploadErrors || 0,
                totalChunks: recording.debugInfo.totalChunks,
                queueLength: recording.uploadQueue.length,
                resetTime: recording.debugInfo.circuitBreakerResetTime?.toISOString(),
                roomId: roomId
            }
        );

        // Log circuit breaker trigger to local diagnostics as critical issue
        this.diagnosticsService.logError(`Circuit breaker triggered for recording`, null, {
            streamId: recording.streamId,
            participantName: recording.participantName,
            consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
            uploadErrors: recording.debugInfo.uploadErrors || 0,
            totalChunks: recording.debugInfo.totalChunks,
            queueLength: recording.uploadQueue.length,
            resetTime: recording.debugInfo.circuitBreakerResetTime,
            sessionId: sessionId,
            roomId: roomId,
            component: 'RecordingService',
            method: 'triggerCircuitBreaker'
        });

        // Track as critical event for immediate alerting
        this.diagnosticsService.trackEvent('recording_circuit_breaker_open', `Recording circuit breaker opened due to consecutive failures`, {
            streamId: recording.streamId,
            participantName: recording.participantName,
            consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
            uploadErrors: recording.debugInfo.uploadErrors || 0,
            sessionId: sessionId,
            roomId: roomId
        });

        // Damage control while the breaker is open (uploads guaranteed idle for 120s):
        // checkpoint whatever is staged-but-uncommitted (best effort - may fail if the outage
        // is total), and if the queue is already >2 minutes deep, rotate to a fresh segment so
        // the committed prefix is sealed instead of gambling the whole tail on recovery.
        setTimeout(() => {
            const current = this.recordings.get(recording.streamId);
            if (!current || current !== recording || !current.isRecording || current.isUploading) {
                return; // Gone, replaced, or an upload cycle is still unwinding - skip safely
            }
            const uncommitted = current.blockIds.length - (current.lastCommittedBlockCount || 0);
            if (uncommitted > 0 && !current.forceRotation) {
                const blockCountAtCommit = current.blockIds.length;
                current.commitInFlightBlockCount = blockCountAtCommit;
                this.commitBlocks(current, true).then(() => {
                    current.lastCommittedBlockCount = blockCountAtCommit;
                    current.lastCommitTime = new Date();
                }).catch(() => { /* best effort - outage may still be active */ })
                .then(() => { current.commitInFlightBlockCount = undefined; });
            }
            if (current.uploadQueue.length > 120) {
                // This drop is the same "undeliverable footage" event as the queue cap - feed the
                // shared sustained-outage detector so repeated drops stop the recording with a
                // user notice instead of rotate-thrashing forever.
                const capWindow = (this.queueCapHistory.get(current.streamId) || []).filter(t => Date.now() - t < this.outageWindowMs);
                capWindow.push(Date.now());
                this.queueCapHistory.set(current.streamId, capWindow);
                if (capWindow.length >= 2) {
                    this.notifyUserRecordingIssue('STOPPED');
                    this.stopRecordingForSubscriber(current.streamId);
                    return;
                }
                current.uploadQueue = [];
                this.rotateRecordingSegment(current.streamId, 'circuit_breaker_deep_queue');
            }
        }, 1000);
    }

    /**
     * Ensure SAS token is valid for the CURRENT segment blob.
     *
     * CRITICAL INVARIANT: the backend returns a brand-new blob with every token request.
     * Once blocks are staged on a blob, fetching a new token mid-segment would silently
     * redirect uploads/commits to a different blob and corrupt the block list
     * (InvalidBlockList => total segment loss). Therefore:
     *  - near-expiry => request a graceful segment rotation, keep using the current token
     *  - token lost mid-segment => throw a sentinel and force a segment rotation
     *  - fresh segment (no blocks staged) => fetching is safe, the new blob becomes the segment's blob
     */
    private async ensureValidSasToken(recording: SubscriberRecording): Promise<void> {
        const now = new Date();
        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;

        // INSTANT-START defense-in-depth: while the prefetched SAS is still in flight, adoption
        // (adoptUploadInfo) is the ONLY allowed writer of this recording's SAS/blob identity.
        // Fetching here would race it and could split the segment across two blobs. This path
        // is unreachable while pending (processUploadQueue holds uploads), but never rely on it.
        if (recording.sasPending) {
            throw new Error('SEGMENT_SAS_PENDING');
        }

        if (recording.sasUrl && recording.debugInfo.sasTokenExpiry) {
            const timeUntilExpiry = recording.debugInfo.sasTokenExpiry.getTime() - now.getTime();

            if (timeUntilExpiry > this.sasRotationThresholdMs) {
                return; // Token is still valid with sufficient time remaining
            }

            // Near expiry: rotate the segment instead of refreshing the token in place
            if (!recording.rotationRequested) {
                recording.rotationRequested = true;
                this.debugLog(`🔄 [SAS NEAR EXPIRY] Requesting graceful segment rotation - StreamID: ${recording.streamId}, Time until expiry: ${Math.round(timeUntilExpiry / 1000)}s`);
                this.diagnosticsService.logMessage(`SAS token near expiry - segment rotation requested`, 'info', {
                    streamId: recording.streamId,
                    participantName: recording.participantName,
                    timeUntilExpiry: timeUntilExpiry,
                    stagedBlocks: recording.blockIds.length,
                    sessionId: sessionId,
                    roomId: roomId,
                    component: 'RecordingService',
                    method: 'ensureValidSasToken'
                });
            }

            if (timeUntilExpiry > 0) {
                return; // Current token is still usable until the rotation completes
            }

            // Token fully expired before the rotation completed - this segment cannot continue
            recording.forceRotation = true;
            throw new Error('SEGMENT_SAS_EXPIRED');
        }

        // No usable token. With blocks already staged, a new token would target a different
        // blob - the segment must be rotated instead.
        if (recording.blockIds.length > 0) {
            recording.forceRotation = true;
            throw new Error('SEGMENT_BLOB_ROTATION_REQUIRED');
        }

        // Fresh segment - safe to fetch a token (this defines the segment's blob)
        this.debugLog(`🔑 [SAS TOKEN NEW] Getting new SAS token - StreamID: ${recording.streamId}`);
        this.diagnosticsService.logMessage(`Getting new SAS token for recording`, 'info', {
            streamId: recording.streamId,
            participantName: recording.participantName,
            sessionId: sessionId,
            roomId: roomId,
            component: 'RecordingService',
            method: 'ensureValidSasToken'
        });

        try {
            const uploadInfo = await this.getAzureUploadInfo(recording.streamId);

            // Defense-in-depth: never adopt a different blob while blocks are staged
            const newBlobPath = uploadInfo.sasUrl.split('?')[0];
            if (recording.blockIds.length > 0 && recording.blobPath && newBlobPath !== recording.blobPath) {
                recording.forceRotation = true;
                throw new Error('SEGMENT_BLOB_ROTATION_REQUIRED');
            }

            recording.sasUrl = uploadInfo.sasUrl;
            recording.blobPath = newBlobPath;
            // Actual expiry parsed from the SAS URL 'se' param; conservative fallback if unparsable
            recording.debugInfo.sasTokenExpiry = this.parseSasExpiry(uploadInfo.sasUrl) || new Date(now.getTime() + 20 * 60 * 60 * 1000);
            recording.rotationRequested = false;

            this.debugLog(`✅ [SAS TOKEN SUCCESS] Token refreshed successfully - StreamID: ${recording.streamId}, Expires: ${recording.debugInfo.sasTokenExpiry}`);

            // Log successful token refresh to local diagnostics
            this.diagnosticsService.logMessage(`SAS token refreshed successfully`, 'info', {
                streamId: recording.streamId,
                participantName: recording.participantName,
                newExpiry: recording.debugInfo.sasTokenExpiry,
                sasTokenRetries: recording.debugInfo.sasTokenRetries || 0,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'ensureValidSasToken'
            });

            // Track successful token refresh
            this.diagnosticsService.trackEvent('sas_token_refresh_success', `SAS token refreshed successfully`, {
                streamId: recording.streamId,
                participantName: recording.participantName,
                retryCount: recording.debugInfo.sasTokenRetries || 0,
                sessionId: sessionId,
                roomId: roomId
            });

        } catch (error) {
            console.error(`❌ [SAS TOKEN FAILED] Failed to refresh SAS token - StreamID: ${recording.streamId}`, error);

            // Log token refresh failure to local diagnostics as error
            this.diagnosticsService.logError(`SAS token refresh failed`, error, {
                streamId: recording.streamId,
                participantName: recording.participantName,
                sasTokenRetries: recording.debugInfo.sasTokenRetries || 0,
                errorMessage: error?.message || error,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'ensureValidSasToken'
            });

            // Track token refresh failure
            this.diagnosticsService.trackEvent('sas_token_refresh_failure', `SAS token refresh failed`, {
                streamId: recording.streamId,
                participantName: recording.participantName,
                retryCount: recording.debugInfo.sasTokenRetries || 0,
                error: error?.message || error,
                sessionId: sessionId,
                roomId: roomId
            });

            throw error;
        }
    }

    /**
     * Refresh SAS token if needed (for error recovery)
     */
    private async refreshSasTokenIfNeeded(recording: SubscriberRecording): Promise<boolean> {
        try {
            this.debugLog(`🔄 [SAS TOKEN REFRESH] Attempting token refresh for error recovery - StreamID: ${recording.streamId}`);

            await this.ensureValidSasToken(recording);

            this.debugLog(`✅ [SAS TOKEN REFRESHED] Successfully refreshed token for error recovery - StreamID: ${recording.streamId}`);
            return true;
        } catch (error) {
            this.debugLog(`❌ [SAS TOKEN REFRESH FAILED] Failed to refresh token for error recovery - StreamID: ${recording.streamId}`, error);
            return false;
        }
    }

    /**
     * Crash-safety checkpoint: commit the staged block list periodically so the blob is
     * playable even if the browser crashes or the tab closes without a clean finalize.
     *
     * MUST only be called from within the sequential upload critical section
     * (processUploadQueue, isUploading=true): Azure garbage-collects any uncommitted block
     * that is staged while a PutBlockList commits without it.
     */
    private async maybeCommitIntermediate(recording: SubscriberRecording): Promise<void> {
        if (recording.forceRotation) {
            return; // Segment can no longer be committed with the current token
        }

        const committedCount = recording.lastCommittedBlockCount || 0;
        const blocksSinceCommit = recording.blockIds.length - committedCount;
        if (blocksSinceCommit <= 0) {
            recording.emergencyCheckpointRequested = false; // Nothing uncommitted to flush
            return;
        }

        // Backoff after a failed checkpoint: without this, a persistently failing commit
        // (each burning up to a 15s timeout) would be retried inline after EVERY chunk,
        // starving the chunk uploads themselves.
        if (recording.nextCommitAttemptTime && Date.now() < recording.nextCommitAttemptTime) {
            return;
        }

        const lastCommitTime = recording.lastCommitTime ? recording.lastCommitTime.getTime() : recording.startTime.getTime();
        const timeSinceCommit = Date.now() - lastCommitTime;

        // First-commit-ASAP: an uncommitted blob is invisible (0 bytes) to the customer, so the
        // very first staged block is committed immediately - the blob becomes playable within
        // seconds of recording start instead of after the first full checkpoint interval.
        const isFirstCommit = committedCount === 0;

        // A visibilitychange flush was requested: honor it HERE, inside the sequential upload
        // critical section, where a commit can never race an in-flight PutBlock (committing
        // around one garbage-collects the staged block => permanent InvalidBlockList).
        const flushRequested = recording.emergencyCheckpointRequested === true;

        if (!isFirstCommit && !flushRequested
                && blocksSinceCommit < this.intermediateCommitBlockInterval && timeSinceCommit < this.intermediateCommitMaxIntervalMs) {
            return;
        }

        // Drain-first: on a slow link the checkpoint would steal bandwidth from chunk uploads.
        // Defer it while the queue is deep - but never starve longer than the starvation cap.
        if (!isFirstCommit && !flushRequested
                && recording.uploadQueue.length > this.intermediateCommitDrainThreshold
                && timeSinceCommit < this.intermediateCommitMaxStarvationMs) {
            return;
        }

        const blockCountAtCommit = recording.blockIds.length;
        try {
            recording.commitInFlightBlockCount = blockCountAtCommit;
            await this.commitBlocks(recording, true);
            recording.lastCommittedBlockCount = blockCountAtCommit;
            recording.lastCommitTime = new Date();
            recording.nextCommitAttemptTime = undefined;
            recording.emergencyCheckpointRequested = false;
            recording.debugInfo.intermediateCommits = (recording.debugInfo.intermediateCommits || 0) + 1;
            this.debugLog(`💾 [CHECKPOINT] Intermediate commit #${recording.debugInfo.intermediateCommits} (${blockCountAtCommit} blocks) - StreamID: ${recording.streamId}`);
        } catch (error) {
            // Non-fatal: retried after a backoff window (or by the final commit)
            recording.nextCommitAttemptTime = Date.now() + 15000;
            this.debugWarn(`⚠️ [CHECKPOINT FAILED] Intermediate commit failed (will retry after backoff) - StreamID: ${recording.streamId}`, error);
            this.diagnosticsService.logMessage(`Intermediate commit failed - will retry after backoff`, 'warning', {
                streamId: recording.streamId,
                stagedBlocks: blockCountAtCommit,
                committedBlocks: committedCount,
                errorMessage: error?.message || error,
                sessionId: this.roomSessionService?.currentSessionId,
                component: 'RecordingService',
                method: 'maybeCommitIntermediate'
            });
        } finally {
            recording.commitInFlightBlockCount = undefined;
        }
    }

    /**
     * Authoritative stream-health verdict. The MediaRecorder is bound to a TRACK SNAPSHOT taken
     * at start; OpenTok can replace the underlying MediaStream (camera change, screen-share swap,
     * media reconnect) which ends the captured tracks while the subscriber itself is fine.
     * This re-acquires the CURRENT stream from OpenTok (never from DOM guessing) and compares
     * track identity, so a swap is distinguished from a genuinely dead stream.
     */
    private verifyStreamHealthAuthoritative(streamId: string): 'healthy' | 'swapped' | 'video_off' | 'dead' {
        try {
            const recording = this.recordings.get(streamId);
            const recordedTrackIds = new Set<string>();
            if (recording && recording.originalMediaStream) {
                recording.originalMediaStream.getVideoTracks().forEach(track => recordedTrackIds.add(track.id));
            }

            // Re-acquire the CURRENT stream from the authoritative source: the subscriber's
            // own rendered <video>.srcObject (via the registry). The previous code read the
            // non-existent getMediaStream() on the subscriber/stream/streamRef, so currentStream
            // was ALWAYS null - which made this check blind (it could never see the post-swap
            // track and returned 'dead' for every real swap). Reading srcObject live fixes that.
            let currentStream: MediaStream | null = null;
            if (this.opentokService && this.opentokService.getSubscriberMediaStreamByStreamId) {
                currentStream = this.opentokService.getSubscriberMediaStreamByStreamId(streamId) || null;
            }
            // Fallback: the identity-safe DOM query (same source, resilient to a not-yet-populated
            // registry) - reads a <video> stamped with THIS streamId only, never another
            // participant's element.
            if (!currentStream) {
                const domVideo = document.querySelector(`[data-streamid="${streamId}"] video, video[data-stream-id="${streamId}"]`) as HTMLVideoElement | null;
                const domSrc = domVideo ? domVideo.srcObject : null;
                if (domSrc instanceof MediaStream && domSrc.getVideoTracks().length > 0) {
                    currentStream = domSrc;
                }
            }

            if (currentStream) {
                const videoTracks = currentStream.getVideoTracks();
                // A track that is MUTED delivers no frames even though readyState stays 'live'
                // and enabled stays true (enabled is a local-only flag) - e.g. a remote uplink
                // freeze, which OpenTok does NOT accompany with a track swap. Such a same-id
                // frozen track must NOT read as 'healthy': that would reset the empty-chunk
                // counter forever and silently record a frozen customer stream for the whole
                // session with no recovery and no STOPPED notice. Require a track that is
                // actually delivering (live, enabled, and not muted).
                const usableTrack = videoTracks.find(track => track.readyState === 'live' && track.enabled && !track.muted);
                if (usableTrack) {
                    return recordedTrackIds.has(usableTrack.id) ? 'healthy' : 'swapped';
                }
                // No delivering track. A DIFFERENT live track (even if momentarily muted) means
                // OpenTok replaced ours - a real swap; recover onto it. If only OUR OWN recorded
                // track remains (muted/frozen), it is a dead pipeline, handled below.
                const differentLiveTrack = videoTracks.find(track => track.readyState === 'live' && !recordedTrackIds.has(track.id));
                if (differentLiveTrack) {
                    return 'swapped';
                }
            }

            // No usable / replacement track - distinguish intentional video-off from a
            // dead-or-frozen pipeline (a frozen same-id track lands here and escalates).
            const streamRef = this.streamReferences.get(streamId);
            if ((streamRef && streamRef.hasVideo === false) || this.localVideoSubscriptionState.get(streamId) === false) {
                return 'video_off';
            }
            return 'dead';
        } catch (error) {
            // Never kill a recording because the verification itself failed
            return 'healthy';
        }
    }

    /**
     * Recover from a stream swap / dead pipeline: finalize the current segment (committing
     * everything captured so far) and restart on the CURRENT OpenTok stream. This replaces the
     * old behavior of silently stopping for the rest of the session. Rate-limited by the shared
     * restart budget; the budget is cleared again after a sustained healthy run.
     */
    private async handleStreamSwapRecovery(streamId: string, trigger: string): Promise<void> {
        // stoppingStreams: a stop/teardown owns this recording - recovering concurrently would
        // double-finalize the same entry and restart a deliberately-stopped recording.
        if (this.rotatingStreams.has(streamId) || this.stoppingStreams.has(streamId) || this.backendRecordingActive) {
            return;
        }

        const sessionId = this.roomSessionService?.currentSessionId;
        const restartWindow = (this.restartHistory.get(streamId) || []).filter(t => Date.now() - t < 300000);
        if (restartWindow.length >= 3) {
            // Crash-looping: give up cleanly (committed data is safe), surface for monitoring
            this.diagnosticsService.trackEvent('recording_swap_recovery_exhausted', `Stream swap recovery budget exhausted - stopping recording`, {
                streamId: streamId,
                trigger: trigger,
                recentRestarts: restartWindow.length,
                sessionId: sessionId
            });
            this.notifyUserRecordingIssue('STOPPED');
            this.stopRecordingForSubscriber(streamId);
            return;
        }
        restartWindow.push(Date.now());
        this.restartHistory.set(streamId, restartWindow);
        this.rotatingStreams.add(streamId);

        this.debugLog(`🔄 [SWAP RECOVERY] Recovering recording after stream swap (${trigger}) - StreamID: ${streamId}`);
        this.diagnosticsService.trackEvent('recording_stream_swap_recovery', `Recording recovering after stream swap`, {
            streamId: streamId,
            trigger: trigger,
            sessionId: sessionId
        });

        // INSTANT-START: a zombie/swap can fire while this segment is still sasPending (the SAS
        // prefetch has not adopted yet). The chunks captured BEFORE the track went zombie are
        // real footage sitting in uploadQueue. finalizeAndCleanupRecording would drop them
        // (its !sasUrl branch), losing the good initial segment this recovery is meant to
        // preserve. Give the pending SAS a bounded grace so adoption commits that footage
        // first; awaitPendingSasAdoption is a no-op once already adopted (the common case,
        // where the SAS resolved seconds before the zombie). This delays recovery by at most
        // the grace window - acceptable, the track is already dead - and never blocks it
        // permanently (the restart below is scheduled after the awaits complete).
        const pendingRecording = this.recordings.get(streamId);
        if (pendingRecording) {
            await this.awaitPendingSasAdoption(pendingRecording, `swap_recovery:${trigger}`);
        }

        try {
            await this.finalizeAndCleanupRecording(streamId);
        } catch (error) {
            this.diagnosticsService.logError(`Swap recovery finalize failed`, error, {
                streamId: streamId,
                trigger: trigger,
                sessionId: sessionId,
                component: 'RecordingService',
                method: 'handleStreamSwapRecovery'
            });
        } finally {
            this.rotatingStreams.delete(streamId);
        }

        // Restart on the current stream after a settle delay. For a confirmed SWAP
        // (proactive/empty/health '..._swap'), verifyStreamHealthAuthoritative already saw a
        // NEW live+unmuted track in the subscriber's srcObject - the replacement is ready NOW,
        // so a long settle just widens the mid-session gap the user sees between the two
        // segments. Use a short settle. For dead-pipeline / recorder-error recoveries the media
        // may genuinely need time to come back, so keep the longer settle there.
        const replacementReady = trigger.endsWith('_swap');
        const settleDelayMs = replacementReady ? 300 : 2000;
        setTimeout(async () => {
            if (this.backendRecordingActive) {
                return;
            }
            const stream = this.findStreamById(streamId);
            if (stream) {
                await this.startNewRecordingWithVideoValidation(streamId);
            }
        }, settleDelayMs);
    }

    /**
     * PARALLEL swap restart (proactive swaps only): instead of the serial finalize-then-restart
     * (which leaves a ~finalize-length mid-session gap), DETACH the old segment onto its own
     * object, start the replacement segment on the current stream IMMEDIATELY, and drain +
     * commit the detached segment in the BACKGROUND. Gap shrinks from ~finalize-time to
     * ~acquire-time. Safe because the upload primitives (uploadChunkToAzure / commitBlocks) are
     * OBJECT-based, never re-resolving by streamId - so the background finalize of the old
     * segment can never touch the replacement's fresh blob.
     */
    private handleProactiveSwapParallel(streamId: string): void {
        if (this.rotatingStreams.has(streamId) || this.backendRecordingActive) {
            return;
        }
        const oldRecording = this.recordings.get(streamId);
        if (!oldRecording) {
            return;
        }

        // If the swap fires before the SAS adopted, there is no blob to commit the old segment
        // to and the split cannot be done cleanly - fall back to the serial path, which awaits
        // adoption first (rare: a swap within the first ~SAS round trip).
        if (oldRecording.sasPending || !oldRecording.sasUrl) {
            this.handleStreamSwapRecovery(streamId, 'proactive_swap');
            return;
        }

        const sessionId = this.roomSessionService?.currentSessionId;

        // Rate-limit exactly like handleStreamSwapRecovery so a swap storm still terminates
        // cleanly with the STOPPED notice rather than looping forever.
        const restartWindow = (this.restartHistory.get(streamId) || []).filter(t => Date.now() - t < 300000);
        if (restartWindow.length >= 3) {
            this.diagnosticsService.trackEvent('recording_swap_recovery_exhausted', `Stream swap recovery budget exhausted - stopping recording`, {
                streamId: streamId,
                trigger: 'proactive_swap_parallel',
                recentRestarts: restartWindow.length,
                sessionId: sessionId
            });
            this.notifyUserRecordingIssue('STOPPED');
            this.stopRecordingForSubscriber(streamId);
            return;
        }
        restartWindow.push(Date.now());
        this.restartHistory.set(streamId, restartWindow);

        this.diagnosticsService.trackEvent('recording_stream_swap_recovery', `Recording recovering after stream swap (parallel)`, {
            streamId: streamId,
            trigger: 'proactive_swap_parallel',
            sessionId: sessionId
        });

        // 1. DETACH the old segment. Stop its recorder and re-route its FINAL dataavailable
        //    chunk straight into its OWN uploadQueue - the default handler re-resolves by
        //    streamId, which after step 3 would splice the old segment's tail into the NEW
        //    segment's blob.
        oldRecording.isRecording = false;
        oldRecording.finalizeQuiesceRequested = true; // make any live drain loop yield to us
        if (oldRecording.mediaRecorder) {
            oldRecording.mediaRecorder.ondataavailable = (event: any) => {
                if (event && event.data && event.data.size > 0) {
                    oldRecording.uploadQueue.push(event.data);
                }
            };
            try {
                if (oldRecording.mediaRecorder.state !== 'inactive') {
                    oldRecording.mediaRecorder.stop();
                }
            } catch (stopError) {
                // best effort
            }
        }
        // 2. Clear the old segment's remaining stream-keyed timers/listeners BEFORE the new
        //    segment reuses the same streamId keys (chunkTimer was already cleared by the caller).
        if ((oldRecording as any).backupChunkTimer) {
            clearInterval((oldRecording as any).backupChunkTimer);
        }
        const monitorCleanup = this.sessionListeners.get(`stateMonitor_${streamId}`);
        if (monitorCleanup) {
            monitorCleanup();
            this.sessionListeners.delete(`stateMonitor_${streamId}`);
        }

        // 3. Free the map slot so the replacement can be created under this streamId, and start
        //    it NOW. startInFlight is keyed per-stream and was released when the first start
        //    returned, so a fresh start is allowed.
        this.recordings.delete(streamId);
        this.startNewRecordingWithVideoValidation(streamId).catch(err => {
            this.debugWarn(`⚠️ [PARALLEL SWAP] Replacement start failed - StreamID: ${streamId}:`, err);
        });

        // 4. Finalize the detached old segment in the BACKGROUND (drain + commit on its OWN blob).
        this.finalizeDetachedSegment(oldRecording).catch(err => {
            this.diagnosticsService.logError(`Detached segment finalize failed`, err, {
                streamId: streamId,
                sessionId: sessionId,
                component: 'RecordingService',
                method: 'handleProactiveSwapParallel'
            });
        });
    }

    /**
     * Drain + commit a DETACHED recording segment on its own blob, off the recordings map.
     * Everything here operates on the passed object (uploadChunkToAzure / commitBlocks are
     * object-based), so it never touches the live map entry that now holds the replacement.
     */
    private async finalizeDetachedSegment(recording: SubscriberRecording): Promise<void> {
        const streamId = recording.streamId;
        const sessionId = this.roomSessionService?.currentSessionId;

        // Let the final post-stop dataavailable arrive (re-routed into this segment's own queue).
        await new Promise(resolve => setTimeout(resolve, 600));

        // QUIESCE — CRITICAL: a live processUploadQueue loop may still own this object
        // (isUploading=true), stuck mid-uploadChunkToAzure on its retry ladder (up to ~2min:
        // 4 attempts x 30s HTTP timeout + backoffs). It only observes finalizeQuiesceRequested
        // and yields at the TOP of its while loop, i.e. AFTER that call returns. We must NEVER
        // drain/commit concurrently with it - two uploaders on the same object collide on
        // blockIds and race the PutBlockList, corrupting the blob. Wait for it to release
        // isUploading (bounded to cover the worst-case upload cycle), and if it still hasn't
        // quiesced, REFUSE to commit (the loop's own intermediate commits are the durable
        // state) - exactly like the serial finalizeRecording.
        let waited = 0;
        while (recording.isUploading && waited < 160) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            waited++;
        }
        if (recording.isUploading) {
            this.diagnosticsService.logError(`Detached segment finalize skipped - upload loop did not quiesce in ${waited}s`, null, {
                streamId: streamId,
                committedBlocks: recording.lastCommittedBlockCount || 0,
                stagedBlocks: recording.blockIds.length,
                queuedChunks: recording.uploadQueue.length,
                sessionId: sessionId,
                component: 'RecordingService',
                method: 'finalizeDetachedSegment'
            });
            return;
        }
        recording.finalizeQuiesceRequested = false;

        // Truncate to the durable prefix if a terminal prefix commit already GC'd the tail.
        if (recording.forceRotation && recording.committedPrefixCount
                && recording.blockIds.length > recording.committedPrefixCount) {
            recording.blockIds = recording.blockIds.slice(0, recording.committedPrefixCount);
        }

        // Drain remaining chunks EXCLUSIVELY (no other loop owns this object now).
        recording.isUploading = true;
        try {
            while (recording.uploadQueue.length > 0) {
                if (recording.forceRotation) {
                    break; // poisoned segment - the committed prefix is what survives
                }
                const chunk = recording.uploadQueue.shift();
                if (chunk) {
                    try {
                        await this.uploadChunkToAzure(recording, chunk);
                    } catch (uploadError) {
                        this.debugWarn(`⚠️ [DETACHED FINALIZE] Chunk upload failed, dropping detached tail - StreamID: ${streamId}:`, uploadError);
                        break;
                    }
                }
            }
        } finally {
            recording.isUploading = false;
        }

        // Commit the detached segment's own blob.
        if (recording.blockIds.length > 0) {
            await this.commitBlocks(recording);
            this.debugLog(`✅ [DETACHED FINALIZE] Old segment committed - StreamID: ${streamId}, Blocks: ${recording.blockIds.length}, SessionID: ${sessionId}`);
            this.diagnosticsService.trackEvent('recording_completed', `Detached swap segment completed`, {
                streamId: streamId,
                participantName: recording.participantName,
                totalBlocks: recording.blockIds.length,
                totalSize: recording.debugInfo.totalSize,
                detachedSwapSegment: true,
                sessionId: sessionId
            });
        }
    }

    /**
     * Evaluate whether the current segment must be rotated (finalized + restarted on a fresh blob).
     * Called from the per-recording state monitor every 3 seconds.
     */
    private evaluateSegmentRotation(streamId: string): void {
        const recording = this.recordings.get(streamId);
        if (!recording || !recording.isRecording || this.backendRecordingActive) {
            return;
        }

        // INSTANT-START: no SAS adopted yet - the segment has no blob identity to rotate, and
        // the placeholder sasTokenExpiry must never be mistaken for a near-expiry token.
        if (recording.sasPending) {
            return;
        }

        let reason: string | null = null;
        if (recording.forceRotation) {
            reason = 'sas_auth_failure';
        } else if (recording.rotationRequested) {
            reason = 'sas_token_near_expiry';
        } else if (recording.blockIds.length >= this.maxBlocksPerSegment) {
            reason = 'block_limit';
        } else if (recording.debugInfo.sasTokenExpiry &&
            (recording.debugInfo.sasTokenExpiry.getTime() - Date.now()) < this.sasRotationThresholdMs) {
            reason = 'sas_token_near_expiry';
        } else if (Date.now() - recording.startTime.getTime() > this.maxRecordingDuration * 1000) {
            // Bound segment length so the PutBlockList XML stays small (the frequent
            // checkpoint commits re-send the FULL list - unbounded segments would make
            // commit traffic grow quadratically) and files stay manageable.
            reason = 'segment_age';
        }

        if (reason) {
            this.rotateRecordingSegment(streamId, reason);
        }
    }

    /**
     * Rotate a recording segment: finalize the current one (committing everything uploaded
     * so far) and start a fresh recording on a new blob. Rate-limited per stream.
     */
    private async rotateRecordingSegment(streamId: string, reason: string): Promise<void> {
        // stoppingStreams: a terminal stop owns this recording - rotating concurrently would
        // double-finalize the same entry (mirror of the guard in stopRecordingForSubscriber).
        if (this.rotatingStreams.has(streamId) || this.stoppingStreams.has(streamId) || this.backendRecordingActive) {
            return;
        }

        const lastRotation = this.lastRotationTime.get(streamId) || 0;
        if (Date.now() - lastRotation < 60000) {
            return; // Rate limit: at most one rotation per stream per minute
        }
        this.lastRotationTime.set(streamId, Date.now());
        this.rotatingStreams.add(streamId);

        const sessionId = this.roomSessionService?.currentSessionId;
        const recording = this.recordings.get(streamId);

        this.debugLog(`🔄 [SEGMENT ROTATION] Rotating segment (${reason}) - StreamID: ${streamId}`);
        this.diagnosticsService.trackEvent('recording_segment_rotation', `Recording segment rotation`, {
            streamId: streamId,
            reason: reason,
            participantName: recording?.participantName,
            stagedBlocks: recording?.blockIds.length || 0,
            committedBlocks: recording?.lastCommittedBlockCount || 0,
            durationSeconds: recording ? Math.round((Date.now() - recording.startTime.getTime()) / 1000) : 0,
            sessionId: sessionId
        });

        try {
            await this.finalizeAndCleanupRecording(streamId);
        } catch (error) {
            this.diagnosticsService.logError(`Segment rotation finalize failed`, error, {
                streamId: streamId,
                reason: reason,
                sessionId: sessionId,
                component: 'RecordingService',
                method: 'rotateRecordingSegment'
            });
        } finally {
            this.rotatingStreams.delete(streamId);
        }

        // Start the replacement segment after a short settle delay
        setTimeout(async () => {
            if (this.backendRecordingActive) {
                return;
            }
            const stream = this.findStreamById(streamId);
            if (stream) {
                await this.startNewRecordingWithVideoValidation(streamId);
            }
        }, 2000);
    }

    /**
     * Keep the owner-side automatic recording in sync with the owner's LOCAL subscribeToVideo
     * state. Locally unsubscribing does not change stream.hasVideo, so without this signal the
     * recorder would capture frozen/empty frames and trigger spurious recovery restarts.
     * Called by SubscriberComponent; no-op on browsers where recording is not initialized.
     */
    public notifyLocalVideoSubscription(streamId: string, subscribed: boolean): void {
        if (!this.opentokService) {
            return; // Recording infrastructure not initialized in this browser (not the owner)
        }

        const previous = this.localVideoSubscriptionState.get(streamId);
        this.localVideoSubscriptionState.set(streamId, subscribed);

        if (previous === subscribed) {
            return;
        }

        this.debugLog(`📺 [LOCAL SUBSCRIPTION] Owner ${subscribed ? 'subscribed to' : 'unsubscribed from'} video - StreamID: ${streamId}`);
        this.diagnosticsService.logMessage(`Owner local video subscription changed`, 'info', {
            streamId: streamId,
            subscribed: subscribed,
            hasRecording: this.recordings.has(streamId),
            sessionId: this.roomSessionService?.currentSessionId,
            component: 'RecordingService',
            method: 'notifyLocalVideoSubscription'
        });

        // Route through the standard toggle path (handles pause/resume/start + upload deferral)
        this.handleVideoToggle(streamId, subscribed);
    }

    /**
     * Best-effort commit of all staged blocks during page teardown/hide, using fetch keepalive
     * so the request can outlive the page.
     *
     * @param terminal true for real teardown signals (beforeunload/pagehide). Only then may the
     * commit fall back to the LARGEST PREFIX that fits the ~64KB keepalive body limit: a prefix
     * PutBlockList garbage-collects every staged block not in the list, so the segment must never
     * be extended afterwards (forceRotation is set in case the page survives).
     *
     * RACE SAFETY: a PutBlockList that races an in-flight PutBlock garbage-collects the staged
     * block Azure processed just before it - poisoning every later full-list commit with
     * InvalidBlockList. Therefore:
     *  - NON-TERMINAL flushes (visibilitychange) never send a commit themselves. They set
     *    emergencyCheckpointRequested, which maybeCommitIntermediate honors INSIDE the sequential
     *    upload critical section (~1-2s later, gate-free) - a commit there can never race a
     *    PutBlock. The recording continues normally.
     *  - TERMINAL flushes (page dying) send the keepalive commit as the last chance, but when an
     *    upload cycle is active (a PutBlock may be in flight) the segment is poisoned
     *    (forceRotation) so a SURVIVING page (canceled unload / bfcache restore) rotates to a
     *    fresh blob instead of extending a list that may reference a garbage-collected block.
     */
    private attemptEmergencyCommits(terminal: boolean): void {
        const now = Date.now();
        this.recordings.forEach(recording => {
            try {
                if (!recording.sasUrl || recording.blockIds.length === 0) {
                    return;
                }
                if (recording.blockIds.length <= (recording.lastCommittedBlockCount || 0)) {
                    return; // Everything already committed
                }
                if (recording.forceRotation) {
                    return; // Current token cannot commit this segment
                }

                if (!terminal) {
                    // Route through the upload critical section - never commit from here
                    recording.emergencyCheckpointRequested = true;
                    return;
                }

                let idsToCommit = recording.blockIds;
                let blockListXml = '<?xml version="1.0" encoding="utf-8"?>' +
                    '<BlockList>' +
                    idsToCommit.map(blockId => `<Latest>${blockId}</Latest>`).join('') +
                    '</BlockList>';

                // Never truncate below anything that may already be durable - including a
                // checkpoint commit currently in flight (its outcome is unknowable here).
                const durableFloor = Math.max(recording.lastCommittedBlockCount || 0, recording.commitInFlightBlockCount || 0);

                if (blockListXml.length > 60000) {
                    // Terminal teardown: commit the largest prefix that fits the keepalive budget.
                    // Compute the entry budget from actual serialized sizes (block ids are uniform).
                    const perEntryBytes = Math.ceil((blockListXml.length - 62) / idsToCommit.length);
                    const maxEntries = Math.floor(59800 / perEntryBytes);
                    if (maxEntries <= durableFloor) {
                        return; // A shorter prefix would TRUNCATE already-durable data - never
                    }
                    idsToCommit = recording.blockIds.slice(0, maxEntries);
                    blockListXml = '<?xml version="1.0" encoding="utf-8"?>' +
                        '<BlockList>' +
                        idsToCommit.map(blockId => `<Latest>${blockId}</Latest>`).join('') +
                        '</BlockList>';
                    // The prefix commit garbage-collects all later staged blocks - if the page
                    // survives (unload canceled / bfcache restore) this segment must rotate, and
                    // any finalize must commit ONLY the prefix (the tail no longer exists).
                    recording.forceRotation = true;
                    recording.committedPrefixCount = idsToCommit.length;
                } else if (recording.isUploading) {
                    // Full-list terminal commit racing an in-flight PutBlock: the commit itself is
                    // still the best last act (covers everything ACKed so far), but a surviving
                    // page must not extend this segment - the racing block may have been GC'd.
                    recording.forceRotation = true;
                    recording.committedPrefixCount = idsToCommit.length;
                }

                recording.lastEmergencyCommitTime = now;

                (window as any).fetch(`${recording.sasUrl}&comp=blocklist`, {
                    method: 'PUT',
                    body: blockListXml,
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/xml',
                        'x-ms-blob-content-type': 'video/webm'
                    }
                }).catch(() => { /* best effort */ });
                // NOTE: lastCommittedBlockCount is intentionally NOT updated here - the request is
                // fire-and-forget and unverified; the awaited checkpoint path owns that accounting.
            } catch (error) {
                // Best effort - never block the unload
            }
        });
    }

    /**
     * Recover recordings that should be running but are not (e.g. the initial start failed
     * because the SAS request or MediaStream acquisition failed). Runs with the 30s health check.
     */
    private recoverMissedRecordings(): void {
        if (this.backendRecordingActive || !this.opentokService) {
            return;
        }

        this.streamReferences.forEach((stream, streamId) => {
            if (this.recordings.has(streamId) || this.startInFlight.has(streamId)) {
                return;
            }
            if (!stream || stream.videoType !== 'camera' || !stream.hasVideo) {
                return;
            }
            if (this.isOwnPublisherStream(stream)) {
                return;
            }
            if (this.localVideoSubscriptionState.get(streamId) === false) {
                return;
            }
            // Count only LIVE recordings toward the cap: entries from a previous session (or a
            // stopped stream) can linger in the map for the whole multi-second finalize drain
            // (isRecording=false from the first synchronous line of their stop) - raw map size
            // blocked recovery for the NEW session's stream exactly when it was needed.
            const liveRecordings = Array.from(this.recordings.values()).filter(r => r.isRecording).length;
            if (liveRecordings >= this.maxFrontendRecordings) {
                return;
            }

            // Throttle: leave at least ~25s between automatic attempts per stream
            const lastAttempt = this.lastStartAttempt.get(streamId) || 0;
            if (Date.now() - lastAttempt < 25000) {
                return;
            }

            // Stop retrying streams that keep failing (3+ automatic restarts in 5 minutes)
            const restartWindow = (this.restartHistory.get(streamId) || []).filter(t => Date.now() - t < 300000);
            if (restartWindow.length >= 3) {
                // Terminal for now: if this stream NEVER produced a single chunk this session,
                // the expert believes it is being recorded when nothing was ever captured - tell them.
                if (!this.everRecordedStreams.has(streamId)) {
                    this.notifyUserRecordingIssue('START_FAILED');
                }
                return;
            }
            restartWindow.push(Date.now());
            this.restartHistory.set(streamId, restartWindow);

            this.debugLog(`🚑 [RECOVERY START] Recovering missed recording - StreamID: ${streamId}`);
            this.diagnosticsService.trackEvent('recording_recovery_start', `Health check recovering missed recording`, {
                streamId: streamId,
                participantName: this.getParticipantName(stream),
                sessionId: this.roomSessionService?.currentSessionId
            });

            this.startRecordingForSubscriber(stream);
        });
    }

    /**
     * Parse the actual expiry of an Azure SAS URL from its 'se' (signed expiry) query param.
     * Returns null when the param is missing/unparsable - callers apply a conservative fallback.
     * Never assume a fixed token lifetime: the backend owns it and may change it at any time.
     */
    private parseSasExpiry(sasUrl: string): Date | null {
        try {
            const match = /[?&]se=([^&]+)/.exec(sasUrl);
            if (!match) {
                return null;
            }
            const expiry = new Date(decodeURIComponent(match[1]));
            if (isNaN(expiry.getTime())) {
                return null;
            }
            // Sanity: an expiry in the past or absurdly far out indicates a parsing/backend anomaly
            const msLeft = expiry.getTime() - Date.now();
            if (msLeft < 2 * 60 * 60 * 1000) {
                this.diagnosticsService.logMessage(`SAS token expiry unusually short`, 'warning', {
                    msLeft: msLeft,
                    component: 'RecordingService',
                    method: 'parseSasExpiry'
                });
            }
            return expiry;
        } catch (error) {
            return null;
        }
    }

    /**
     * Check if error is related to SAS token (401/403 responses)
     */
    private isSasTokenError(error: any): boolean {
        if (error && error.status) {
            return error.status === 401 || error.status === 403;
        }

        // Check error message for common SAS token issues
        const errorMessage = error?.message?.toLowerCase() || error?.toString()?.toLowerCase() || '';
        return errorMessage.includes('unauthorized') ||
            errorMessage.includes('forbidden') ||
            errorMessage.includes('authentication') ||
            errorMessage.includes('signature') ||
            errorMessage.includes('expired');
    }

    /**
     * Handle MediaRecorder errors with recovery mechanisms
     */
    private async handleMediaRecorderError(streamId: string, event: any, recording: SubscriberRecording | undefined): Promise<void> {
        if (!recording) {
            this.debugLog(`❌ [RECOVERY FAILED] No recording found for StreamID: ${streamId}`);
            return;
        }

        // STOP-IN-PROGRESS GUARD: Chrome can fire onerror during the stop() flush of a
        // recorder that a stop/teardown flow is already finalizing. Recovering here would run
        // a SECOND finalize concurrently with the stop's own (deleting the entry under it, so
        // the stop reports recording_completed on a tail it never committed) and the +2s
        // restart would revive a recording the service just decided to stop. The stop flow
        // owns the teardown - an errored recorder changes nothing for it (the recorder is
        // already stopping and its captured chunks are already flushed or lost either way).
        if (!recording.isRecording || this.stoppingStreams.has(streamId)) {
            this.debugLog(`🛑 [RECOVERY SKIP] MediaRecorder error during an in-progress stop - deferring to the stop flow - StreamID: ${streamId}`);
            return;
        }

        const sessionId = this.roomSessionService?.currentSessionId;
        const errorMessage = event.error?.message || event.message || 'Unknown MediaRecorder error';

        // BLOB-IDENTITY INVARIANT: an errored recorder must NEVER be replaced in place on the
        // same recording entry. A new MediaRecorder emits a fresh WebM EBML header, and the old
        // in-place restart appended that second WebM stream as more PutBlocks into the SAME
        // already-committed blob - players stop at the splice, so the recovery silently
        // corrupted the file while reporting success. The chunk/backup timers also hold the
        // OLD recorder in their closures, so forced requestData and the zombie health checks
        // died after the swap. Route the recovery through the swap-recovery machinery instead:
        // it finalizes + commits this segment's captured footage on its own blob (honoring a
        // still-pending SAS), then restarts on the CURRENT stream with a FRESH blob and fresh
        // timers. Rate-limited by the shared restartHistory budget (3 per 5min sliding window),
        // whose exhaustion stops cleanly with the STOPPED user notice instead of looping.
        recording.debugInfo.mediaRecorderRecoveryAttempts = (recording.debugInfo.mediaRecorderRecoveryAttempts || 0) + 1;
        this.debugLog(`🔄 [RECOVERY ATTEMPT] MediaRecorder error - recovering via segment restart on a fresh blob - StreamID: ${streamId}, Error: ${errorMessage}`);
        this.diagnosticsService.logError(`MediaRecorder error - recovering via segment restart`, event.error || event, {
            streamId: streamId,
            sessionId: sessionId,
            participantName: recording.participantName,
            recoveryAttempt: recording.debugInfo.mediaRecorderRecoveryAttempts,
            originalError: errorMessage,
            component: 'RecordingService',
            method: 'handleMediaRecorderError'
        });

        await this.handleStreamSwapRecovery(streamId, 'mediarecorder_error');
    }

    /**
     * Validate that the blob exists after commit
     */
    private async validateBlobExists(recording: SubscriberRecording): Promise<void> {
        // Skip blob validation in development environment due to CORS restrictions
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            this.debugLog(`🚧 [BLOB VALIDATION] Skipping validation in development environment - StreamID: ${recording.streamId}`);
            return;
        }

        try {
            // Use HEAD request to check if blob exists without downloading content
            const headUrl = recording.sasUrl!.split('?')[0]; // Remove query parameters for HEAD request
            const response = await this.http.head(headUrl).pipe(timeout(10000)).toPromise();
            this.debugLog(`✅ [BLOB VALIDATION] Blob exists and is accessible - StreamID: ${recording.streamId}`);
        } catch (error) {
            this.debugWarn(`⚠️ [BLOB VALIDATION] Could not validate blob existence - StreamID: ${recording.streamId}`, error);
            // Don't throw here as the commit might have succeeded even if validation fails
        }
    }

    /**
     * Start health monitoring for recording sessions
     */
    private startHealthMonitoring(): void {
        // Idempotent: this service is a root singleton that outlives PublisherComponent.
        // The component's ngOnDestroy stops the monitor at call end, so every new room's
        // init MUST be able to restart it - without this restart, recoverMissedRecordings,
        // stall detection, token-expiry checks and the START_FAILED notice were all dead
        // from the second call onward in a long-lived tab.
        if (this.healthMonitorInterval) {
            return;
        }
        // Monitor every 30 seconds
        this.healthMonitorInterval = setInterval(() => {
            this.performHealthCheck();
        }, 30000);
    }

    /**
     * Stop health monitoring
     */
    private stopHealthMonitoring(): void {
        if (this.healthMonitorInterval) {
            clearInterval(this.healthMonitorInterval);
            this.healthMonitorInterval = null;
        }
    }

    /**
     * Delay utility for retry mechanisms
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Get SAS token with retry mechanism
     * Implements 3 retry attempts with exponential backoff (2s, 4s, 6s)
     * 10s timeout per request
     */
    private async getSasTokenWithRetry(token: string, roomId: string, sessionId: string, blobName: string, streamId: string): Promise<string> {
        const maxRetries = 3;
        const timeoutMs = 10000; // 10 seconds

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`🔑 [SAS TOKEN] Attempt ${attempt}/${maxRetries} - RoomID: ${roomId}, SessionID: ${sessionId}`);

                // Create AbortController for timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

                try {
                    const sasUrl = await this.getSasToken(token, roomId, sessionId, blobName, streamId);
                    clearTimeout(timeoutId);

                    // Validate SAS URL format (must contain 'sig=')
                    if (!sasUrl || !sasUrl.includes('sig=')) {
                        throw new Error(`Invalid SAS URL format - missing signature parameter`);
                    }

                    console.log(`✅ [SAS TOKEN] Success on attempt ${attempt}/${maxRetries} - SessionID: ${sessionId}`);
                    return sasUrl;
                } catch (error) {
                    clearTimeout(timeoutId);
                    throw error;
                }
            } catch (error) {
                const errorMessage = error.message || String(error);
                const errorStatus = error?.status || 'unknown';

                console.error(`❌ [SAS TOKEN] Attempt ${attempt}/${maxRetries} failed - Status: ${errorStatus}, Error: ${errorMessage}, SessionID: ${sessionId}`);

                this.diagnosticsService.logError(`SAS token request failed`, error, {
                    attempt: attempt,
                    maxRetries: maxRetries,
                    errorStatus: errorStatus,
                    errorMessage: errorMessage,
                    roomId: roomId,
                    sessionId: sessionId,
                    component: 'RecordingService',
                    method: 'getSasTokenWithRetry'
                });

                if (attempt === maxRetries) {
                    console.error(`🚨 [SAS TOKEN] All ${maxRetries} retries failed - SessionID: ${sessionId}`);

                    // Notify backend of failure
                    await this.notifyRecordingFailure(sessionId, 'SAS_TOKEN_ALL_RETRIES_FAILED', {
                        attempts: maxRetries,
                        lastError: errorMessage,
                        lastStatus: errorStatus,
                        roomId: roomId,
                        blobName: blobName
                    });

                    throw new Error(`Failed to get SAS token after ${maxRetries} retries: ${errorMessage}`);
                }

                // Exponential backoff: 2s, 4s, 6s
                const backoffMs = attempt * 2000;
                console.log(`⏳ [SAS TOKEN] Retrying in ${backoffMs}ms - Attempt ${attempt}/${maxRetries}, SessionID: ${sessionId}`);
                await this.delay(backoffMs);
            }
        }

        throw new Error('SAS token retry logic error - should not reach here');
    }

    /**
     * Notify about recording failure via local diagnostics.
     * (The old HTTP endpoint was a dead hardcoded preview URL; a real backend endpoint
     * will be wired up in a separate backend change.)
     */
    /**
     * Surface a TERMINAL recording problem to the expert as a flash message (deduped: at most
     * once per kind per session). Transient rotations/recoveries stay silent by design - the
     * expert only needs to know when the recording will NOT recover on its own.
     */
    private notifyUserRecordingIssue(kind: 'START_FAILED' | 'STOPPED' | 'SAVE_FAILED'): void {
        try {
            if (!this.userRecordingNoticesEnabled || this.notifiedUserIssues.has(kind)) {
                return;
            }
            this.notifiedUserIssues.add(kind);
            this.flashMessageService.showTranslated(`APP.MAIN.ROOM.VIDEO_CHAT.RECORDING.${kind}`, {
                cssClass: 'alert-warning',
                timeout: 10000,
                showCloseBtn: true
            });
        } catch (error) {
            // Notices are best effort - never break recording flow over UI plumbing
        }
    }

    private async notifyRecordingFailure(sessionId: string, reason: string, details: any): Promise<void> {
        try {
            console.log(`📡 [FAILURE NOTIFICATION] Recording failure - Reason: ${reason}, SessionID: ${sessionId}`);

            console.log(`📡 [FAILURE NOTIFICATION] Recording failure - Reason: ${reason}, SessionID: ${sessionId}`);

            // Log to local diagnostics
            this.diagnosticsService.logError(`Recording failure: ${reason}`, null, {
                sessionId: sessionId,
                reason: reason,
                details: details,
                userAgent: navigator.userAgent,
                component: 'RecordingService',
                method: 'notifyRecordingFailure'
            });

            // Track as event for alerting/dashboards
            this.diagnosticsService.trackEvent('recording_failure_notification', `Recording failure: ${reason}`, {
                sessionId: sessionId,
                reason: reason,
                roomId: this.roomSessionService?.currentRoomId
            });
        } catch (error) {
            // Silent failure - notification is best effort
            console.error(`⚠️ [FAILURE NOTIFICATION] Exception during notification:`, error);
        }
    }

    /**
     * Perform health check and take corrective actions if needed
     */
    /**
     * Perform comprehensive health check on all recordings with enhanced local diagnostics logging
     */
    private performHealthCheck(): void {
        const sessionId = this.roomSessionService?.currentSessionId;
        const roomId = this.roomSessionService?.currentRoomId;
        const recordings = Array.from(this.recordings.values());
        const now = new Date();

        // Recover recordings that should be running but are not (failed initial starts etc.)
        this.recoverMissedRecordings();

        if (recordings.length === 0) {
            return; // No recordings to check
        }

        // Collect critical issues and warnings
        let criticalIssues: string[] = [];
        let warnings: string[] = [];
        let stalledRecordings = 0;
        let highErrorRateRecordings = 0;
        let expiringTokens = 0;

        recordings.forEach(recording => {
            const streamId = recording.streamId;
            const participantName = recording.participantName;

            // Check for stalled uploads (no successful upload in last 2 minutes)
            if (recording.debugInfo.lastSuccessfulUpload) {
                const timeSinceLastUpload = now.getTime() - recording.debugInfo.lastSuccessfulUpload.getTime();
                if (timeSinceLastUpload > 120000 && recording.uploadQueue.length > 0) { // 2 minutes
                    stalledRecordings++;
                    const stallTimeSeconds = Math.round(timeSinceLastUpload / 1000);
                    criticalIssues.push(`Upload stalled for ${stallTimeSeconds}s - ${participantName} (${streamId})`);

                    this.debugWarn(`⚠️ [HEALTH CHECK] Stalled upload detected - StreamID: ${streamId}, Time since last upload: ${stallTimeSeconds}s`);

                    // Log individual stall to local diagnostics with detailed context
                    this.diagnosticsService.logError(`Recording upload stalled`, null, {
                        streamId: streamId,
                        participantName: participantName,
                        timeSinceLastUpload: timeSinceLastUpload,
                        stallTimeSeconds: stallTimeSeconds,
                        queueLength: recording.uploadQueue.length,
                        totalChunks: recording.debugInfo.totalChunks,
                        totalSize: recording.debugInfo.totalSize,
                        uploadErrors: recording.debugInfo.uploadErrors || 0,
                        consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
                        circuitBreakerOpen: recording.debugInfo.circuitBreakerOpen || false,
                        sessionId: sessionId,
                        roomId: roomId,
                        component: 'RecordingService',
                        method: 'performHealthCheck'
                    });
                }
            }

            // Check for excessive queue length
            if (recording.uploadQueue.length > 15) {
                criticalIssues.push(`Large queue: ${recording.uploadQueue.length} chunks - ${participantName} (${streamId})`);

                this.debugWarn(`⚠️ [HEALTH CHECK] Large upload queue detected - StreamID: ${streamId}, Queue length: ${recording.uploadQueue.length}`);

                this.diagnosticsService.logMessage(`Large upload queue detected`, 'warning', {
                    streamId: streamId,
                    participantName: participantName,
                    queueLength: recording.uploadQueue.length,
                    isUploading: recording.isUploading,
                    uploadErrors: recording.debugInfo.uploadErrors || 0,
                    sessionId: sessionId,
                    roomId: roomId,
                    component: 'RecordingService'
                });
            } else if (recording.uploadQueue.length > 8) {
                warnings.push(`Growing queue: ${recording.uploadQueue.length} chunks - ${participantName}`);
            }

            // Check for high error rate
            if (recording.debugInfo.uploadErrors && recording.debugInfo.totalChunks > 0) {
                const errorRate = (recording.debugInfo.uploadErrors / recording.debugInfo.totalChunks) * 100;
                if (errorRate > 10) { // More than 10% error rate
                    highErrorRateRecordings++;
                    criticalIssues.push(`High error rate: ${Math.round(errorRate)}% - ${participantName} (${streamId})`);

                    this.debugWarn(`⚠️ [HEALTH CHECK] High error rate detected - StreamID: ${streamId}, Error rate: ${Math.round(errorRate)}%`);

                    this.diagnosticsService.logError(`High upload error rate detected`, null, {
                        streamId: streamId,
                        participantName: participantName,
                        errorRate: Math.round(errorRate * 100) / 100,
                        totalErrors: recording.debugInfo.uploadErrors,
                        totalChunks: recording.debugInfo.totalChunks,
                        consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
                        circuitBreakerOpen: recording.debugInfo.circuitBreakerOpen || false,
                        sessionId: sessionId,
                        roomId: roomId,
                        component: 'RecordingService'
                    });
                } else if (errorRate > 5) {
                    warnings.push(`Elevated error rate: ${Math.round(errorRate)}% - ${participantName}`);
                }
            }

            // Check for circuit breaker status
            if (recording.debugInfo.circuitBreakerOpen) {
                criticalIssues.push(`Circuit breaker open - ${participantName} (${streamId})`);

                this.diagnosticsService.logMessage(`Circuit breaker is open for recording`, 'error', {
                    streamId: streamId,
                    participantName: participantName,
                    consecutiveFailures: recording.debugInfo.consecutiveFailures || 0,
                    circuitBreakerResetTime: recording.debugInfo.circuitBreakerResetTime,
                    sessionId: sessionId,
                    roomId: roomId,
                    component: 'RecordingService'
                });
            }

            // Check for token expiry (warn 5 minutes before)
            if (recording.debugInfo.sasTokenExpiry) {
                const timeUntilExpiry = recording.debugInfo.sasTokenExpiry.getTime() - now.getTime();
                if (timeUntilExpiry < 300000 && timeUntilExpiry > 0) { // Less than 5 minutes until expiry
                    expiringTokens++;
                    const minutesUntilExpiry = Math.round(timeUntilExpiry / 60000);
                    warnings.push(`Token expires in ${minutesUntilExpiry}min - ${participantName}`);

                    this.debugLog(`🔔 [HEALTH CHECK] SAS token expiring soon - StreamID: ${streamId}, Time until expiry: ${Math.round(timeUntilExpiry / 1000)}s`);

                    this.diagnosticsService.logMessage(`SAS token expiring soon`, 'warning', {
                        streamId: streamId,
                        participantName: participantName,
                        timeUntilExpiry: timeUntilExpiry,
                        minutesUntilExpiry: minutesUntilExpiry,
                        sasTokenRetries: recording.debugInfo.sasTokenRetries || 0,
                        sessionId: sessionId,
                        roomId: roomId,
                        component: 'RecordingService'
                    });
                }
            }

            // Check for recording stalls (no chunks for 45+ seconds during active recording)
            if (recording.isRecording && !recording.isPaused && recording.debugInfo.lastChunkTime) {
                const timeSinceLastChunk = now.getTime() - recording.debugInfo.lastChunkTime.getTime();
                if (timeSinceLastChunk > 45000) { // 45 seconds
                    warnings.push(`No chunks for ${Math.round(timeSinceLastChunk / 1000)}s - ${participantName}`);
                }
            }
        });

        // Generate health summary
        const healthMetrics = this.getHealthMetrics();

        // Log critical issues to local diagnostics
        if (criticalIssues.length > 0) {
            console.error(`🚨 [HEALTH CHECK] Critical issues detected:`, criticalIssues);

            this.diagnosticsService.logError(`Recording health check - Critical issues detected`, null, {
                criticalIssuesCount: criticalIssues.length,
                criticalIssues: criticalIssues,
                stalledRecordings: stalledRecordings,
                highErrorRateRecordings: highErrorRateRecordings,
                healthMetrics: healthMetrics,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'performHealthCheck'
            });

            // Track as critical event for alerting
            this.diagnosticsService.trackEvent('recording_health_critical', `Critical recording health issues detected`, {
                issueCount: criticalIssues.length,
                issues: criticalIssues.join('; '),
                stalledRecordings: stalledRecordings,
                highErrorRateRecordings: highErrorRateRecordings,
                activeRecordings: healthMetrics.activeRecordings,
                sessionId: sessionId,
                roomId: roomId,
                errorRate: healthMetrics.errorRate,
                avgQueueLength: healthMetrics.avgQueueLength
            });
        }

        // Log warnings to local diagnostics
        if (warnings.length > 0) {
            this.debugWarn(`⚠️ [HEALTH CHECK] Warnings detected:`, warnings);

            this.diagnosticsService.logMessage(`Recording health check - Warnings detected`, 'warning', {
                warningsCount: warnings.length,
                warnings: warnings,
                expiringTokens: expiringTokens,
                healthMetrics: healthMetrics,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'performHealthCheck'
            });
        }

        // Periodically log comprehensive health metrics (every 5 minutes - 10 health checks)
        // This helps with tracking trends over time
        const oldestRecordingTime = recordings.length > 0 ?
            Math.min(...recordings.map(r => r.startTime.getTime())) : Date.now();
        const sessionDuration = (Date.now() - oldestRecordingTime) / 1000;
        const checkInterval = 30; // seconds
        const checksPerReport = 10; // 10 checks = 5 minutes
        const checkCount = Math.floor(sessionDuration / checkInterval);

        if (checkCount > 0 && checkCount % checksPerReport === 0) {
            this.diagnosticsService.logMessage(`Recording session health summary`, 'info', {
                ...healthMetrics,
                sessionDuration: Math.round(sessionDuration),
                checksPerformed: checkCount,
                sessionId: sessionId,
                roomId: roomId,
                component: 'RecordingService',
                method: 'performHealthCheck'
            });

            // Track session health as an event
            this.diagnosticsService.trackEvent('recording_session_health', `Recording session health summary`, {
                sessionDuration: Math.round(sessionDuration),
                totalRecordings: healthMetrics.totalRecordings,
                activeRecordings: healthMetrics.activeRecordings,
                errorRate: healthMetrics.errorRate,
                avgQueueLength: healthMetrics.avgQueueLength,
                totalSize: healthMetrics.totalSize,
                sessionId: sessionId,
                roomId: roomId
            });
        }
    }
}
